import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Paper, Button, TextField, IconButton, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Chip, CircularProgress,
  Alert, Tabs, Tab, LinearProgress, Tooltip, Autocomplete, Drawer, Divider,
  ToggleButton, ToggleButtonGroup, Skeleton, Menu, Collapse, Switch, FormControlLabel, Snackbar,
  Checkbox, ListItemText, OutlinedInput, InputLabel, FormControl,
} from '@mui/material';
import { useTheme, ThemeProvider, createTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SaveIcon from '@mui/icons-material/Save';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArticleIcon from '@mui/icons-material/Article';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FilterListIcon from '@mui/icons-material/FilterList';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useNavigate } from 'react-router-dom';
import LiveUniverse from '../pages/LiveUniverse';
import { ThemesDialog, ThemeBuildCard } from './ThemeLibrary';
import LeagueTable from './LeagueTable';
import RotationMap from './RotationMap';
import ThemeHoldingsTable from './ThemeHoldingsTable';
import { TickerLink } from './TickerActionMenu';
import ModelPortfolioEarnings from './ModelPortfolioEarnings';
import CorrelationHeatmap from './CorrelationHeatmap';
import EfficientFrontier, { ApplyWeightsMeta } from './EfficientFrontier';
import ModelLineage from './ModelLineage';
import MonteCarloSimulation from './MonteCarloSimulation';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine, Cell,
} from 'recharts';
import api from '../services/api';
import { bloomberg } from '../theme/bloombergTheme';
import {
  getModelPortfolios, getModelPortfolio, createModelPortfolio,
  updateModelPortfolio, deleteModelPortfolio, setModelPortfolioHoldings,
  analyzeModelPortfolio,
  downloadComparisonPdf, downloadPortfolioReportPdf,
  getAvailableBenchmarks, getPortfolioBenchmarks, setPortfolioBenchmarks, getLeagueHoldings, LeagueHolding,
  getTradeRecommendations,

  ModelPortfolioSummary, ModelPortfolioDetail, ModelPortfolioAnalysis,
  RiskMetrics as RiskMetricsType, HoldingMetric,
  AvailableBenchmark, BenchmarkComparison,
  TradeRecommendationsResponse, TradeRecommendation,
  getPortfolioSignalsReport, downloadPortfolioSignalsPdf, PortfolioSignalSymbol,
  getPortfolioAttribution, AttributionResult, HoldingAttribution, SectorAttribution, BrinsonRow, BrinsonTotals,
  detectPortfolioRegion, PortfolioRegion,
  getUniverseNews, UniverseNewsArticle, UniverseNewsResponse,
  getRebalanceHistory, RebalanceEvent, createActiveVersion, REBALANCE_SOURCE_LABELS, isActiveSection,
  API_BASE_URL,
} from '../services/api';
import { runJob, watchJob, jobKey } from '../services/backgroundJobs';

// Alert config API helpers (inline to avoid adding to api.ts for now)
const getPortfolioAlertConfig = (portfolioId: number) =>
  api.get(`/api/model-portfolios/${portfolioId}/alert-config`).then(r => r.data);
const savePortfolioAlertConfig = (portfolioId: number, data: object) =>
  api.put(`/api/model-portfolios/${portfolioId}/alert-config`, data).then(r => r.data);

const REGIONS: PortfolioRegion[] = ['US', 'UK', 'EU', 'SWISS', 'SINGAPORE', 'KOREA', 'GLOBAL', 'THEMATIC'];
// Order and labels for grouping models by kind (see model_portfolio_service.VALID_KINDS).
const KIND_ORDER = ['theme', 'active', 'curated_theme', 'regional', 'conviction', 'allocation', 'screen', 'unclassified'] as const;
const KIND_LABELS: Record<string, string> = {
  theme: 'Themes', active: 'Active', curated_theme: 'Curated themes', regional: 'Regional & sector',
  conviction: 'Convictions', allocation: 'Allocations', screen: 'Screens', unclassified: 'Unclassified',
};
const REGION_FLAGS: Record<string, string> = {
  US: '🇺🇸', UK: '🇬🇧', EU: '🇪🇺', SWISS: '🇨🇭',
  SINGAPORE: '🇸🇬', KOREA: '🇰🇷', GLOBAL: '🌐', THEMATIC: '🎯',
};
const REGION_COLORS: Record<string, string> = {
  US: '#1565c0', UK: '#c62828', EU: '#1b5e20', SWISS: '#e65100',
  SINGAPORE: '#ad1457', KOREA: '#4527a0', GLOBAL: '#00695c', THEMATIC: '#6a1b9a',
};

const PERF_LABELS: { key: string; label: string }[] = [
  { key: 'price', label: 'Price' },
  { key: 'day_1', label: '1D' },
  { key: 'week_1', label: '1W' },
  { key: 'month_1', label: '1M' },
  { key: 'month_3', label: '3M' },
  { key: 'ytd', label: 'YTD' },
  { key: 'year_1', label: '1Y' },
  { key: 'year_2', label: '2Y' },
  { key: 'year_5', label: '5Y' },
];

const TIMEFRAMES = ['5D', '10D', '20D', '30D', '1M', '3M', '6M', '1Y', '2Y'];

const SIGNAL_LABELS: Record<string, string> = {
  '52w_high': '52W High', '52w_low': '52W Low',
  crossed_above_20ma: 'Above 20MA', crossed_below_20ma: 'Below 20MA',
  crossed_above_50ma: 'Above 50MA', crossed_below_50ma: 'Below 50MA',
  crossed_above_200ma: 'Above 200MA', crossed_below_200ma: 'Below 200MA',
  golden_cross: 'Golden Cross', death_cross: 'Death Cross',
  rsi_oversold: 'RSI Oversold', rsi_overbought: 'RSI Overbought',
};

const CATEGORY_LABELS: Record<string, string> = {
  milestone: 'Milestones', ma_cross: 'MA Crosses', momentum: 'Momentum',
};

// Maps URL sub-path → tab index
// View 1 is Rankings and the default landing; view 0 (one model's page) lives
// at /portfolio/models. "league" and "performance" are old links to Rankings.
const SUB_TO_VIEW: Record<string, number> = {
  models: 0, rankings: 1, league: 1, performance: 1, signals: 2, earnings: 3, movers: 4, radar: 5, news: 6, prices: 7, rotation: 8,
};
const VIEW_TO_SUB: Record<number, string> = {
  0: 'models', 1: 'rankings', 2: 'signals', 3: 'earnings', 4: 'movers', 5: 'radar', 6: 'news', 7: 'prices', 8: 'rotation',
};

function formatTimeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (days >= 365) return `${Math.floor(days / 365)}y ago`;
  if (days >= 30) return `${Math.floor(days / 30)}mo ago`;
  if (days >= 1) return `${days}d ago`;
  if (hours >= 1) return `${hours}h ago`;
  if (mins >= 1) return `${mins}m ago`;
  return 'just now';
}

// activeOnly: the model page on its own, inside Portfolio › Active Portfolios —
// no Rankings/Rotation tabs, and the picker lists only active portfolios.
// Model-page tabs addressable from a link (&tab=…), e.g. the "ready" pop-up of a long analysis.
const TAB_INDEX: Record<string, number> = {
  holdings: 0, performance: 1, risk: 2, attribution: 3, correlation: 4, frontier: 5, recommendations: 6, 'monte-carlo': 7,
};

const ModelPortfolios: React.FC<{ initialSub?: string; initialModelId?: number; activeOnly?: boolean; initialTab?: string }> = ({ initialSub, initialModelId, activeOnly, initialTab }) => {
  const navigate = useNavigate();
  const [mainView, setMainView] = useState(
    activeOnly ? 0 : initialSub ? (SUB_TO_VIEW[initialSub] ?? 1) : 1
  );

  const handleViewChange = (_: React.SyntheticEvent, v: number) => {
    setMainView(v);
    const sub = VIEW_TO_SUB[v];
    navigate(sub ? `/portfolio/${sub}` : '/portfolio', { replace: true });
  };

  // Movers tab
  interface UniverseMover { symbol: string; company_name: string; sector: string; price: number; change_pct: number; portfolios: string[]; }
  interface MaMover extends UniverseMover { sma20_dist: number | null; sma50_dist: number | null; sma200_dist: number | null; macd_hist: number | null; }
  interface MaMomentumData { ma20: { above: MaMover[]; below: MaMover[] }; ma50: { above: MaMover[]; below: MaMover[] }; ma200: { above: MaMover[]; below: MaMover[] }; }
  const [moversGainers, setMoversGainers] = useState<UniverseMover[]>([]);
  const [moversLosers, setMoversLosers] = useState<UniverseMover[]>([]);
  const [moversLoading, setMoversLoading] = useState(false);
  const [moversPeriod, setMoversPeriod] = useState<'1D' | '7D' | '30D'>('1D');
  const [maMomentum, setMaMomentum] = useState<MaMomentumData | null>(null);
  const [maMomentumLoading, setMaMomentumLoading] = useState(false);
  const [selectedMa, setSelectedMa] = useState<'ma20' | 'ma50' | 'ma200'>('ma50');
  const [maSort, setMaSort] = useState<'ma20'|'ma50'|'ma200'|'macd'|'chg'>('ma50');
  // Radar tab
  interface RadarEntry { symbol: string; company_name: string; sector: string; country: string; market_cap: number | null; price: number; change_pct: number | null; sma20_dist: number | null; sma50_dist: number | null; sma200_dist: number | null; macd_hist: number | null; macd: number | null; macd_signal: number | null; last_date: string; }
  interface RadarData { accelerating: RadarEntry[]; decelerating: RadarEntry[]; meta: { total_accelerating: number; total_decelerating: number; available_countries: string[]; available_sectors: string[]; }; }
  const [radarData, setRadarData] = useState<RadarData | null>(null);
  const [radarLoading, setRadarLoading] = useState(false);
  const [radarError, setRadarError] = useState<string | null>(null);
  const [radarCountry, setRadarCountry] = useState('');
  const [radarSector, setRadarSector] = useState('');
  const [radarCapFilter, setRadarCapFilter] = useState('');
  const [radarSort, setRadarSort] = useState<'macd'|'ma20'|'ma50'|'ma200'|'chg'>('macd');
  const [radarIncludeSmallCap, setRadarIncludeSmallCap] = useState(false);
  // News tab
  const [newsData, setNewsData] = useState<UniverseNewsResponse | null>(null);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsPage, setNewsPage] = useState(1);
  const [newsPortfolioFilter, setNewsPortfolioFilter] = useState<number | ''>('');
  const [newsSectorFilter, setNewsSectorFilter] = useState('');
  const [newsCountryFilter, setNewsCountryFilter] = useState('');
  const [newsContentType, setNewsContentType] = useState<'all' | 'news' | 'social'>('all');
  const [newsSearch, setNewsSearch] = useState('');
  const [newsSearchInput, setNewsSearchInput] = useState('');
  const [newsBreakingOnly, setNewsBreakingOnly] = useState(false);
  const [newsSort, setNewsSort] = useState<'recent' | 'quality'>('recent');
  // Portfolio list
  const [portfolios, setPortfolios] = useState<ModelPortfolioSummary[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(initialModelId ?? null);
  // Inside Active Portfolios the open model lives in the URL, so refresh and back work.
  useEffect(() => {
    if (activeOnly && selectedId) navigate(`/portfolio/active?id=${selectedId}`, { replace: true });
  }, [activeOnly, selectedId, navigate]);

  // Opened from elsewhere with a model in the URL (e.g. Active Portfolios → /portfolio/models?id=…).
  useEffect(() => {
    if (!initialModelId) return;
    setSelectedId(initialModelId);
    setCompareId(null);
    setMainView(0);
    if (initialTab && TAB_INDEX[initialTab] !== undefined) setAnalysisTab(TAB_INDEX[initialTab]);
  }, [initialModelId, initialTab]);
  const [portfolio, setPortfolio] = useState<ModelPortfolioDetail | null>(null);
  const [loading, setLoading] = useState(false);

  // Analysis
  const [analysis, setAnalysis] = useState<ModelPortfolioAnalysis | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  // The analysis runs as a background job (services/backgroundJobs); only the
  // latest request may update the page, and the page watches it while shown.
  const [analysisJob, setAnalysisJob] = useState<{ key: string; id: number } | null>(null);
  const analysisKeyRef = useRef<string | null>(null);
  const [analysisTab, setAnalysisTab] = useState(0);
  // Replaces (not appends to) Holdings/Benchmarks/Auto-Alerts when true —
  // Analyze switches into a dedicated analysis screen with a back arrow,
  // instead of growing the same scrollable page.
  const [showAnalysis, setShowAnalysis] = useState(false);

  // The whole Analysis Dashboard should read as one flat darker-blue surface —
  // no lighter nested cards. MUI's dark-mode elevation shading is an inline
  // style the component sets itself, which beats a plain sx/styleOverrides
  // backgroundColor, so every Paper in this subtree is also pinned to
  // elevation 0 (see the JSX below) and this scoped theme only needs to
  // override the background color, not fight the overlay.
  const baseTheme = useTheme();
  const analysisTheme = useMemo(() => createTheme(baseTheme, {
    components: {
      MuiPaper: {
        styleOverrides: {
          root: { backgroundColor: 'var(--bb-bg-primary)' },
        },
      },
    },
  }), [baseTheme]);

  const [perfSortCol, setPerfSortCol] = useState<string | null>(null);
  const [perfSortDir, setPerfSortDir] = useState<'asc' | 'desc'>('desc');
  const [perfView, setPerfView] = useState<'performance' | 'technicals' | 'fundamentals'>('performance');
  const [timeframe, setTimeframe] = useState('1Y');

  // Dialog for create/edit
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [dialogName, setDialogName] = useState('');
  const [dialogDesc, setDialogDesc] = useState('');
  const [dialogTags, setDialogTags] = useState('');
  const [dialogRegion, setDialogRegion] = useState<PortfolioRegion | ''>('');
  const [detectingRegion, setDetectingRegion] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [regionMenu, setRegionMenu] = useState<{ anchor: HTMLElement | null; region: PortfolioRegion | null }>({ anchor: null, region: null });
  const [tagMenuAnchor, setTagMenuAnchor] = useState<HTMLElement | null>(null);

  // Holdings editor
  const [editHoldings, setEditHoldings] = useState<{ symbol: string; target_weight: number; name?: string }[]>([]);
  const [holdingRoles, setHoldingRoles] = useState<Record<string, 'core' | 'satellite'>>({});
  // Per-holding returns etc. from the league table's cached computation.
  const [holdingPerf, setHoldingPerf] = useState<Record<string, LeagueHolding>>({});
  const [holdingPerfLoading, setHoldingPerfLoading] = useState(false);
  const [holdingsDirty, setHoldingsDirty] = useState(false);
  const [savingHoldings, setSavingHoldings] = useState(false);

  // Rebalance history
  const [rebalanceHistory, setRebalanceHistory] = useState<RebalanceEvent[]>([]);

  // Ticker search for adding
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<{ symbol: string; name: string }[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Attribution
  const [attribution, setAttribution] = useState<AttributionResult | null>(null);
  const [attributionLoading, setAttributionLoading] = useState(false);
  const [attributionBenchmark, setAttributionBenchmark] = useState<string>('');
  const [attributionTimeframe, setAttributionTimeframe] = useState('1Y');

  // Benchmarks
  const [availableBenchmarks, setAvailableBenchmarks] = useState<AvailableBenchmark[]>([]);
  const [selectedBenchmarks, setSelectedBenchmarks] = useState<string[]>(['SPY']);
  // The model whose saved benchmarks are loaded: analysis waits for this so it
  // doesn't run once with the previous model's benchmarks.
  const [benchmarksFor, setBenchmarksFor] = useState<number | null>(null);
  const [analysisAt, setAnalysisAt] = useState<Date | null>(null);
  const [benchMenu, setBenchMenu] = useState<null | HTMLElement>(null);
  const [, setBenchmarksDirty] = useState(false);


  // Portfolio vs Portfolio comparison
  const [compareId, setCompareId] = useState<number | null>(null);
  const [compareAnalysis, setCompareAnalysis] = useState<ModelPortfolioAnalysis | null>(null);
  // Jump to the comparison tab once it has loaded ("Compare in detail").
  const [pendingCompareTab, setPendingCompareTab] = useState(false);

  // Active versions: create one, and apply Efficient Frontier weights to one
  const [creatingActive, setCreatingActive] = useState(false);
  const [applyReq, setApplyReq] = useState<{ weights: Record<string, number>; meta: ApplyWeightsMeta } | null>(null);
  const [applyTarget, setApplyTarget] = useState<number | 'new'>('new');
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // All-portfolios performance

  // Multi-portfolio Efficient Frontier
  const [frontierPortfolioIds, setFrontierPortfolioIds] = useState<number[]>([]);
  const [portfolioDetailsCache, setPortfolioDetailsCache] = useState<Record<number, ModelPortfolioDetail>>({});

  // Portfolio Signals tab
  const [pfSignals, setPfSignals] = useState<Record<string, PortfolioSignalSymbol>>({});
  const [pfCount, setPfCount] = useState(0);
  const [pfLoading, setPfLoading] = useState(false);
  const [pfPdfLoading, setPfPdfLoading] = useState(false);
  const [pfDays, setPfDays] = useState(7);
  const [pfView, setPfView] = useState<'stock' | 'event'>('stock');
  // Signals filters — empty pfSignalTypes set means "no filter, show all types"
  const [pfSignalTypes, setPfSignalTypes] = useState<Set<string>>(new Set());
  const [pfDirectionFilter, setPfDirectionFilter] = useState<'all' | 'bullish' | 'bearish'>('all');
  const [pfPortfolioFilter, setPfPortfolioFilter] = useState('');
  const [pfSearch, setPfSearch] = useState('');
  const [pfSortCol, setPfSortCol] = useState<string | null>(null);
  const [pfSortDir, setPfSortDir] = useState<'asc' | 'desc'>('desc');

  // Alert config
  const [alertConfig, setAlertConfig] = useState<{ pct_change_enabled: boolean; pct_change_threshold: number; is_active: boolean } | null>(null);
  const [alertConfigDirty, setAlertConfigDirty] = useState(false);
  const [alertConfigSaving, setAlertConfigSaving] = useState(false);
  const [alertSnackbar, setAlertSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // ── Load portfolios + available benchmarks on mount ──

  useEffect(() => {
    loadPortfolios();
    getAvailableBenchmarks().then(setAvailableBenchmarks).catch(() => {});
  }, []);

  const [themesOpen, setThemesOpen] = useState(false);
  const loadPortfolios = async () => {
    try {
      const data = await getModelPortfolios();
      setPortfolios(data);
      if (data.length > 0 && !selectedId) {
        setSelectedId(data[0].id);
      }
    } catch (err) {
      console.error('Failed to load model portfolios:', err);
    }
  };

  const fetchPortfolioSignals = useCallback(async () => {
    setPfLoading(true);
    try {
      const data = await getPortfolioSignalsReport(pfDays);
      setPfSignals(data.signals_by_symbol);
      setPfCount(data.count);
    } catch (err) {
      console.error('Error fetching portfolio signals:', err);
    } finally {
      setPfLoading(false);
    }
  }, [pfDays]);

  useEffect(() => {
    if (mainView === 2) fetchPortfolioSignals();
  }, [mainView, fetchPortfolioSignals]);

  const fetchMovers = useCallback(async (period: '1D' | '7D' | '30D') => {
    setMoversLoading(true);
    try {
      const { data } = await api.get(`/api/model-portfolios/universe/movers?period=${period}&limit=25`);
      setMoversGainers(data.gainers || []);
      setMoversLosers(data.losers || []);
    } catch { /* noop */ } finally {
      setMoversLoading(false);
    }
  }, []);

  const fetchMaMomentum = useCallback(async () => {
    if (maMomentum) return;
    setMaMomentumLoading(true);
    try {
      const { data } = await api.get('/api/model-portfolios/universe/ma-momentum?limit=20');
      setMaMomentum(data);
    } catch { /* noop */ } finally {
      setMaMomentumLoading(false);
    }
  }, [maMomentum]);

  useEffect(() => {
    if (mainView === 4) { fetchMovers(moversPeriod); fetchMaMomentum(); }
  }, [mainView, moversPeriod, fetchMovers, fetchMaMomentum]);

  const fetchRadar = useCallback(async (country = '', sector = '', cap = '', smallCap = false) => {
    setRadarLoading(true);
    setRadarError(null);
    try {
      const p = new URLSearchParams({ limit: '25' });
      if (country) p.set('country', country);
      if (sector)  p.set('sector', sector);
      // specific tier overrides toggle; otherwise apply no_small default
      const capParam = cap === 'large' ? 'large' : cap === 'mid' ? 'mid' : smallCap ? '' : 'no_small';
      if (capParam) p.set('market_cap', capParam);
      const { data } = await api.get(`/api/model-portfolios/universe/momentum-radar?${p}`);
      setRadarData(data);
    } catch (err: any) {
      console.error('Radar fetch failed:', err);
      setRadarError(err?.response?.data?.detail || err?.message || 'Failed to load radar');
    } finally {
      setRadarLoading(false);
    }
  }, []);

  useEffect(() => {
    if (mainView === 5) fetchRadar('', '', '', false);
  }, [mainView]);

  useEffect(() => {
    if (mainView === 5 && radarData) fetchRadar(radarCountry, radarSector, radarCapFilter, radarIncludeSmallCap);
  }, [radarCountry, radarSector, radarCapFilter, radarIncludeSmallCap]);

  const fetchNews = useCallback(async (page: number = 1) => {
    setNewsLoading(true);
    try {
      const params: Record<string, any> = { page, page_size: 30 };
      if (newsPortfolioFilter) params.portfolio_id = newsPortfolioFilter;
      if (newsSectorFilter) params.sector = newsSectorFilter;
      if (newsCountryFilter) params.country = newsCountryFilter;
      if (newsContentType !== 'all') params.content_type = newsContentType;
      if (newsSearch) params.search = newsSearch;
      if (newsBreakingOnly) params.breaking_only = true;
      if (newsSort !== 'recent') params.sort = newsSort;
      const data = await getUniverseNews(params);
      setNewsData(data);
      setNewsPage(page);
    } catch { /* noop */ } finally {
      setNewsLoading(false);
    }
  }, [newsPortfolioFilter, newsSectorFilter, newsCountryFilter, newsContentType, newsSearch, newsBreakingOnly, newsSort]);

  useEffect(() => {
    if (mainView === 6) fetchNews(1);
  }, [mainView, newsPortfolioFilter, newsSectorFilter, newsCountryFilter, newsContentType, newsSearch, newsBreakingOnly, newsSort]);

  // ── Load portfolio detail + benchmarks when selected ──
  useEffect(() => {
    if (selectedId) {
      loadPortfolio(selectedId);
      setHoldingPerfLoading(true);
      getLeagueHoldings(selectedId)
        .then(rows => setHoldingPerf(Object.fromEntries(rows.map(r => [r.symbol, r]))))
        .catch(() => setHoldingPerf({}))
        .finally(() => setHoldingPerfLoading(false));
      setBenchmarksFor(null);
      getPortfolioBenchmarks(selectedId).then(syms => {
        setSelectedBenchmarks(syms.length > 0 ? syms : ['SPY']);
        setBenchmarksDirty(false);
      }).catch(() => {}).finally(() => setBenchmarksFor(selectedId));
      getPortfolioAlertConfig(selectedId).then(cfg => {
        setAlertConfig(cfg);
        setAlertConfigDirty(false);
      }).catch(() => {});
      // Reset stale comparison/frontier data
      setCompareAnalysis(null);
      setFrontierPortfolioIds([selectedId]);
    }
  }, [selectedId]);

  const loadPortfolio = async (id: number) => {
    setLoading(true);
    setAnalysis(null);
    setShowAnalysis(false);
    try {
      const data = await getModelPortfolio(id);
      setPortfolio(data);
      setEditHoldings(data.holdings.map(h => ({
        symbol: h.symbol,
        target_weight: h.target_weight,
        name: h.name,
      })));
      const roles: Record<string, 'core' | 'satellite'> = {};
      data.holdings.forEach(h => { roles[h.symbol] = (h.role as 'core' | 'satellite') || 'core'; });
      setHoldingRoles(roles);
      setHoldingsDirty(false);
    } catch (err) {
      console.error('Failed to load portfolio:', err);
    } finally {
      setLoading(false);
    }
    try {
      setRebalanceHistory(await getRebalanceHistory(id));
    } catch (err) {
      console.error('Failed to load rebalance history:', err);
      setRebalanceHistory([]);
    }
  };

  // ── CRUD handlers ──
  const handleCreate = async () => {
    const name = dialogName.trim();
    if (!name) return;
    try {
      const created = await createModelPortfolio(name, dialogDesc.trim() || undefined, dialogTags.trim() || undefined, dialogRegion || null);
      setDialogOpen(false);
      await loadPortfolios();
      setSelectedId(created.id);
    } catch (err: any) {
      console.error('Failed to create portfolio:', err);
    }
  };

  const handleUpdate = async () => {
    if (!selectedId) return;
    try {
      await updateModelPortfolio(selectedId, dialogName.trim(), dialogDesc.trim(), dialogTags.trim(), dialogRegion || null);
      setDialogOpen(false);
      await loadPortfolios();
      await loadPortfolio(selectedId);
    } catch (err) {
      console.error('Failed to update portfolio:', err);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    try {
      await deleteModelPortfolio(selectedId);
      setDeleteDialogOpen(false);
      if (activeOnly) { navigate('/portfolio/active'); return; }
      setSelectedId(null);
      setPortfolio(null);
      setAnalysis(null);
      setShowAnalysis(false);
      await loadPortfolios();
    } catch (err) {
      console.error('Failed to delete portfolio:', err);
    }
  };

  // ── Holdings editor ──
  const totalWeight = editHoldings.reduce((sum, h) => sum + h.target_weight, 0);
  const weightValid = Math.abs(totalWeight - 1.0) < 0.02;

  const handleWeightChange = (index: number, value: number) => {
    const updated = [...editHoldings];
    updated[index].target_weight = value;
    setEditHoldings(updated);
    setHoldingsDirty(true);
  };

  const handleRemoveHolding = (index: number) => {
    setEditHoldings(editHoldings.filter((_, i) => i !== index));
    setHoldingsDirty(true);
  };

  const handleAddHolding = (symbol: string, name: string) => {
    if (editHoldings.some(h => h.symbol === symbol)) return;
    setEditHoldings([...editHoldings, { symbol, target_weight: 0, name }]);
    setHoldingsDirty(true);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSaveHoldings = async () => {
    if (!selectedId) return;
    setSavingHoldings(true);
    try {
      await setModelPortfolioHoldings(
        selectedId,
        editHoldings.map(h => ({ symbol: h.symbol, target_weight: h.target_weight, role: holdingRoles[h.symbol] || 'core' }))
      );
      setHoldingsDirty(false);
      await loadPortfolio(selectedId);
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to save holdings');
    } finally {
      setSavingHoldings(false);
    }
  };

  const openModel = (id: number, kindHint?: string) => {
    // Each kind opens in its own section: active portfolios under Portfolio › Active,
    // everything else (e.g. an active model's base) under Model Portfolios.
    const kind = kindHint ?? portfolios.find(p => p.id === id)?.kind;
    if (activeOnly && kind && !isActiveSection(kind)) { navigate(`/portfolio/models?id=${id}`); return; }
    if (!activeOnly && isActiveSection(kind)) { navigate(`/portfolio/active?id=${id}`); return; }
    setCompareId(null);
    setAnalysisTab(0);
    setSelectedId(id);
  };

  const handleCreateActive = async () => {
    if (!selectedId) return;
    setCreatingActive(true);
    try {
      const child = await createActiveVersion(selectedId);
      await loadPortfolios();
      openModel(child.id, 'active');
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Could not create the active version');
    } finally {
      setCreatingActive(false);
    }
  };

  const handleApplyWeights = (weights: Record<string, number>, meta: ApplyWeightsMeta) => {
    if (!portfolio) return;
    const kids = (portfolio.children || []).filter(c => c.kind === 'active');
    setApplyTarget(isActiveSection(portfolio.kind) ? portfolio.id : kids.length ? kids[0].id : 'new');
    setApplyError(null);
    setApplyReq({ weights, meta });
  };

  const confirmApplyWeights = async () => {
    if (!applyReq || !selectedId) return;
    setApplying(true);
    setApplyError(null);
    try {
      let target = applyTarget;
      if (target === 'new') target = (await createActiveVersion(selectedId)).id;
      const { meta } = applyReq;
      const note = `${meta.strategy} · ${meta.capPct >= 100 ? 'uncapped' : `${meta.capPct}% cap`} · ${meta.period}`;
      await setModelPortfolioHoldings(
        target,
        Object.entries(applyReq.weights).map(([symbol, target_weight]) => ({ symbol, target_weight, role: 'core' as const })),
        'frontier', note,
      );
      setApplyReq(null);
      await loadPortfolios();
      if (target === selectedId) { setAnalysisTab(0); await loadPortfolio(target); } else openModel(target, 'active');
    } catch (err: any) {
      setApplyError(err?.response?.data?.detail || 'Could not apply the weights.');
    } finally {
      setApplying(false);
    }
  };

  useEffect(() => {
    if (pendingCompareTab && compareAnalysis) { setAnalysisTab(8); setPendingCompareTab(false); }
  }, [pendingCompareTab, compareAnalysis]);

  // ── Ticker search (debounced) ──
  const searchSymbols = useCallback(async (q: string) => {
    if (q.length < 1) { setSearchResults([]); return; }
    setSearchLoading(true);
    try {
      const { data } = await api.get(`/api/correlation/search`, { params: { q } });
      setSearchResults(data.results || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => { if (searchQuery) searchSymbols(searchQuery); }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchSymbols]);

  // ── Analyze ──
  // Where a model's page lives, optionally on a given tab (used by the "ready" pop-ups).
  const modelRoute = (id: number, kind?: string | null, tab?: string) =>
    `${isActiveSection(kind) ? '/portfolio/active' : '/portfolio/models'}?id=${id}${tab ? `&tab=${tab}` : ''}`;

  const handleAnalyze = async () => {
    if (!selectedId || !portfolio || portfolio.holdings.length < 2) return;
    const id = selectedId;
    const benches = [...selectedBenchmarks];
    const cmp = compareId;
    const k = jobKey('analysis', { id, timeframe, b: benches, c: cmp, h: holdingsSig });
    analysisKeyRef.current = k;
    setAnalysisJob({ key: k, id });
    setAnalysisLoading(true);
    setShowAnalysis(true);
    setFrontierPortfolioIds([id]);
    try {
      const [result, cResult] = await runJob(k, { label: `Analysis · ${portfolio.name}`, route: modelRoute(id, portfolio.kind, 'performance') },
        async () => {
          const r = await analyzeModelPortfolio(id, timeframe, benches);
          const c = cmp ? await analyzeModelPortfolio(cmp, timeframe, benches) : null;
          return [r, c] as const;
        });
      if (analysisKeyRef.current !== k) return;      // a newer request, or another model, took over
      setAnalysis(result);
      setAnalysisAt(new Date());
      setCompareAnalysis(cResult);
    } catch (err: any) {
      console.error('Analysis failed:', err);
      if (analysisKeyRef.current === k) setAnalysis(null);
    } finally {
      if (analysisKeyRef.current === k) setAnalysisLoading(false);
    }
  };

  // Switching model abandons (but doesn't cancel) the previous model's analysis:
  // it finishes in the background and announces itself with a pop-up.
  useEffect(() => {
    analysisKeyRef.current = null;
    setAnalysisLoading(false);
  }, [selectedId]);
  useEffect(() => (analysisJob && analysisJob.id === selectedId && mainView === 0 ? watchJob(analysisJob.key) : undefined),
    [analysisJob, selectedId, mainView]);

  // Analysis runs by itself: when a model opens, and when its period,
  // benchmarks, comparison or saved holdings change (served from a 1h cache
  // on repeat visits).
  const holdingsSig = portfolio ? portfolio.holdings.map(h => `${h.symbol}:${h.target_weight}`).join('|') : '';
  useEffect(() => {
    if (!selectedId || !portfolio || portfolio.id !== selectedId || benchmarksFor !== selectedId) return;
    if (portfolio.holdings.length < 2 || holdingsDirty) return;
    const t = window.setTimeout(() => { handleAnalyze(); }, 300);
    return () => window.clearTimeout(t);
  }, [selectedId, portfolio?.id, holdingsSig, benchmarksFor, timeframe, selectedBenchmarks.join(','), compareId, holdingsDirty]);

  const handleToggleBenchmark = (symbol: string) => {
    const next = selectedBenchmarks.includes(symbol) ? selectedBenchmarks.filter(s => s !== symbol) : [...selectedBenchmarks, symbol];
    if (next.length === 0) return; // keep at least one
    setSelectedBenchmarks(next);
    if (selectedId) setPortfolioBenchmarks(selectedId, next).catch(() => setBenchmarksDirty(true));
  };


  const handleToggleFrontierPortfolio = async (id: number) => {
    if (id === selectedId) return; // current portfolio is always included
    if (frontierPortfolioIds.includes(id)) {
      setFrontierPortfolioIds(prev => prev.filter(i => i !== id));
    } else {
      if (!portfolioDetailsCache[id]) {
        try {
          const detail = await getModelPortfolio(id);
          setPortfolioDetailsCache(prev => ({ ...prev, [id]: detail }));
        } catch (e) {
          console.error('Failed to load portfolio for frontier:', e);
          return;
        }
      }
      setFrontierPortfolioIds(prev => [...prev, id]);
    }
  };

  // ── Attribution ──
  const handleLoadAttribution = async () => {
    if (!selectedId) return;
    const bm = attributionBenchmark || selectedBenchmarks[0] || 'SPY';
    setAttributionLoading(true);
    try {
      const result = await getPortfolioAttribution(selectedId, bm, attributionTimeframe);
      setAttribution(result);
    } catch (err: any) {
      console.error('Attribution failed:', err);
      alert(err?.response?.data?.detail || 'Attribution analysis failed');
    } finally {
      setAttributionLoading(false);
    }
  };

  // ── Render helpers ──
  const perfColor = (val: number | null | undefined) => {
    if (val == null) return 'text.secondary';
    return val > 0 ? '#4caf50' : val < 0 ? '#f44336' : 'text.primary';
  };

  const fmtPerf = (val: number | null | undefined) => {
    if (val == null) return '-';
    return `${val > 0 ? '+' : ''}${val.toFixed(1)}%`;
  };

  const scoreColor = (score: number) => {
    if (score >= 70) return '#4caf50';
    if (score >= 40) return '#ff9800';
    return '#f44336';
  };

  // Build userWeights for EfficientFrontier — memoized so the object
  // reference only changes when holdings actually change, not on every
  // unrelated re-render (an unstable reference here was silently retriggering
  // EfficientFrontier's auto-fetch effect on every ModelPortfolios re-render).
  const userWeightsMap = useMemo(() => (
    portfolio?.holdings
      ? Object.fromEntries(portfolio.holdings.map(h => [h.symbol, h.target_weight]))
      : undefined
  ), [portfolio?.holdings]);

  // Merged holdings for multi-portfolio Efficient Frontier
  const mergedFrontierData = useMemo(() => {
    const weightSums: Record<string, number> = {};
    for (const id of frontierPortfolioIds) {
      const detail = id === selectedId ? portfolio : portfolioDetailsCache[id];
      if (!detail) continue;
      for (const h of detail.holdings) {
        weightSums[h.symbol] = (weightSums[h.symbol] || 0) + h.target_weight;
      }
    }
    const symbols = Object.keys(weightSums);
    const totalWeight = Object.values(weightSums).reduce((s, v) => s + v, 0);
    const weights: Record<string, number> = {};
    for (const [sym, w] of Object.entries(weightSums)) {
      weights[sym] = totalWeight > 0 ? w / totalWeight : 1 / symbols.length;
    }
    return { symbols, weights };
  }, [frontierPortfolioIds, portfolio, portfolioDetailsCache, selectedId]);

  // Tag filtering
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    portfolios.forEach(p => {
      if (p.tags) p.tags.split(',').map(t => t.trim()).filter(Boolean).forEach(t => tagSet.add(t));
    });
    return Array.from(tagSet).sort();
  }, [portfolios]);

  const filteredPortfolios = useMemo(() => {
    if (filterTags.length === 0) return portfolios;
    return portfolios.filter(p => {
      const pTags = (p.tags || '').split(',').map(t => t.trim().toLowerCase());
      return filterTags.some(ft => pTags.includes(ft.toLowerCase()));
    });
  }, [portfolios, filterTags]);

  // A systematic theme base is equal-weight and read-only: no weights, no roles.
  const isThemeBase = portfolio?.kind === 'theme';
  // Core/Satellite only where a model actually uses it (one model, Sep 2026).
  const usesRoles = Object.values(holdingRoles).includes('satellite');
  const pctCell = (v?: number | null) => (v === null || v === undefined ? '—' : `${v >= 0 ? '+' : '−'}${Math.abs(v * 100).toFixed(1)}%`);

  return (
    <Box>
      {/* ── Top-level view tabs ── */}
      {!activeOnly && <Tabs
        value={mainView}
        onChange={handleViewChange}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value={1} label="Rankings" />
        <Tab value={8} label="Rotation" />
        <Tab value={0} label="Model" />
        <Tab icon={<WorkspacesIcon sx={{ fontSize: 15 }} />} iconPosition="start" label="Signals" value={2} />
        <Tab icon={<CalendarMonthIcon sx={{ fontSize: 15 }} />} iconPosition="start" label="Earnings" value={3} />
        <Tab icon={<TrendingUpIcon sx={{ fontSize: 15 }} />} iconPosition="start" label="Movers" value={4} />
        <Tab icon={<TravelExploreIcon sx={{ fontSize: 15 }} />} iconPosition="start" label="Radar" value={5} />
        <Tab icon={<NewspaperIcon sx={{ fontSize: 15 }} />} iconPosition="start" label="News" value={6} />
        <Tab value={7} label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{
              width: 7, height: 7, borderRadius: '50%', bgcolor: '#22c55e', flexShrink: 0,
              boxShadow: '0 0 6px 2px #22c55e',
              '@keyframes livePulse': {
                '0%, 100%': { opacity: 1, boxShadow: '0 0 6px 2px #22c55e' },
                '50%': { opacity: 0.2, boxShadow: '0 0 2px 0px #22c55e' },
              },
              animation: 'livePulse 1.8s ease-in-out infinite',
            }} />
            Latest Prices
          </Box>
        } />
      </Tabs>}

      {/* ── Performance View ── */}
      {mainView === 8 && (
        <RotationMap onOpen={(id) => {
          if (isActiveSection(portfolios.find(p => p.id === id)?.kind)) { navigate(`/portfolio/active?id=${id}`); return; }
          setSelectedId(id);
          setCompareId(null);
          setMainView(0);
          navigate('/portfolio/models', { replace: true });
        }} />
      )}

      {mainView === 1 && (
        <LeagueTable onOpen={(id, compareWith) => {
          if (isActiveSection(portfolios.find(p => p.id === id)?.kind)) { navigate(`/portfolio/active?id=${id}`); return; }
          setSelectedId(id);
          setCompareId(compareWith ?? null);
          setMainView(0);
          navigate('/portfolio/models', { replace: true });
        }} />
      )}

      {/* ── Portfolios View ── */}
      {mainView === 0 && <Box>
      {/* ── Portfolio Selector ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mr: 1 }}>
          {activeOnly ? 'Active Portfolio' : 'Model Portfolios'}
        </Typography>

        {/* Region filter pills — each opens its own dropdown */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
          {REGIONS.map(r => {
            const regionPortfolios = portfolios.filter(p => p.region === r);
            if (regionPortfolios.length === 0) return null;
            return (
              <Chip
                key={r}
                label={`${REGION_FLAGS[r]} ${r}`}
                size="small"
                onClick={(e) => setRegionMenu({ anchor: e.currentTarget as HTMLElement, region: r })}
                sx={{
                  fontSize: 11, height: 24, cursor: 'pointer',
                  bgcolor: regionMenu.region === r ? REGION_COLORS[r] : 'transparent',
                  color: regionMenu.region === r ? '#fff' : bloomberg.text.secondary,
                  border: `1px solid ${regionMenu.region === r ? REGION_COLORS[r] : bloomberg.border}`,
                  '&:hover': { bgcolor: regionMenu.region === r ? REGION_COLORS[r] : `${REGION_COLORS[r]}22` },
                }}
              />
            );
          })}
        </Box>

        {/* Per-region portfolio dropdown menu */}
        <Menu
          anchorEl={regionMenu.anchor}
          open={!!regionMenu.anchor}
          onClose={() => setRegionMenu({ anchor: null, region: null })}
          slotProps={{ paper: { sx: { bgcolor: bloomberg.bg.secondary, border: `1px solid ${bloomberg.border}`, minWidth: 220 } } }}
        >
          {regionMenu.region && portfolios.filter(p => p.region === regionMenu.region).map(p => (
            <MenuItem
              key={p.id}
              selected={p.id === selectedId}
              onClick={() => { setSelectedId(p.id); setRegionMenu({ anchor: null, region: null }); }}
              sx={{ fontSize: 13, color: bloomberg.text.primary }}
            >
              {p.name}
              <Typography component="span" sx={{ ml: 1, fontSize: 11, color: bloomberg.text.muted }}>
                ({p.holdings_count})
              </Typography>
            </MenuItem>
          ))}
        </Menu>

        {allTags.length > 0 && (
          <>
            <Tooltip title={filterTags.length > 0 ? `Tags: ${filterTags.join(', ')}` : 'Filter by tag'}>
              <IconButton
                size="small"
                onClick={(e) => setTagMenuAnchor(e.currentTarget)}
                sx={{
                  color: filterTags.length > 0 ? bloomberg.accent.orange : bloomberg.text.secondary,
                  border: `1px solid ${filterTags.length > 0 ? bloomberg.accent.orange : bloomberg.border}`,
                  borderRadius: 1, p: '3px',
                }}
              >
                <FilterListIcon sx={{ fontSize: 16 }} />
                {filterTags.length > 0 && (
                  <Typography component="span" sx={{ fontSize: 10, ml: 0.3, lineHeight: 1 }}>
                    {filterTags.length}
                  </Typography>
                )}
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={tagMenuAnchor}
              open={!!tagMenuAnchor}
              onClose={() => setTagMenuAnchor(null)}
              slotProps={{ paper: { sx: { bgcolor: bloomberg.bg.secondary, border: `1px solid ${bloomberg.border}`, minWidth: 160 } } }}
            >
              {allTags.map(tag => (
                <MenuItem
                  key={tag}
                  onClick={() => setFilterTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag])}
                  sx={{ fontSize: 13, gap: 1, color: filterTags.includes(tag) ? bloomberg.accent.orange : bloomberg.text.primary }}
                >
                  <Box sx={{ width: 14, height: 14, borderRadius: '50%', border: `2px solid ${filterTags.includes(tag) ? bloomberg.accent.orange : bloomberg.border}`, bgcolor: filterTags.includes(tag) ? bloomberg.accent.orange : 'transparent', flexShrink: 0 }} />
                  {tag}
                </MenuItem>
              ))}
              {filterTags.length > 0 && [
                <Divider key="div" sx={{ my: 0.5, borderColor: bloomberg.border }} />,
                <MenuItem key="clear" onClick={() => { setFilterTags([]); setTagMenuAnchor(null); }} sx={{ fontSize: 12, color: bloomberg.text.muted }}>
                  Clear filters
                </MenuItem>,
              ]}
            </Menu>
          </>
        )}

        {filteredPortfolios.length > 0 && (
          <Select
            value={selectedId && filteredPortfolios.some(p => p.id === selectedId) ? selectedId : ''}
            onChange={(e) => setSelectedId(Number(e.target.value))}
            size="small"
            sx={{ minWidth: 200 }}
          >
            {(() => {
              // Grouped by what the model is; within a group, by region then name.
              const grouped: Record<string, typeof filteredPortfolios> = {};
              // Judgement-based models have their own section (Portfolio › Active); only the open one is listed here.
              filteredPortfolios.filter(p => activeOnly ? isActiveSection(p.kind) : (!isActiveSection(p.kind) || p.id === selectedId)).forEach(p => {
                const key = p.kind || 'unclassified';
                if (!grouped[key]) grouped[key] = [];
                grouped[key].push(p);
              });
              const regionRank = (r: string | null) => { const i = REGIONS.indexOf(r as any); return i < 0 ? REGIONS.length : i; };
              // Theme models: each theme followed by its sub-themes.
              const themeOrder = (list: typeof filteredPortfolios) => {
                const parents = list.filter(p => !p.theme_parent).sort((a, b) => a.name.localeCompare(b.name));
                const kids = list.filter(p => p.theme_parent);
                const out: typeof filteredPortfolios = [];
                parents.forEach(par => {
                  out.push(par);
                  kids.filter(k => k.theme_parent === par.theme).sort((a, b) => a.name.localeCompare(b.name)).forEach(k => out.push(k));
                });
                kids.filter(k => !out.includes(k)).forEach(k => out.push(k));
                return out;
              };
              return KIND_ORDER.flatMap(kind => {
                const group = kind === 'theme' ? themeOrder(grouped[kind] || []) : (grouped[kind] || []).slice().sort((a, b) =>
                  regionRank(a.region) - regionRank(b.region) || a.name.localeCompare(b.name));
                if (!group.length) return [];
                return [
                  <MenuItem key={`header-${kind}`} disabled sx={{ fontSize: 11, fontWeight: 700, opacity: 1, color: bloomberg.text.muted, py: 0.3, letterSpacing: 0.4 }}>
                    {KIND_LABELS[kind].toUpperCase()}
                  </MenuItem>,
                  ...group.map(p => (
                    <MenuItem key={p.id} value={p.id} sx={{ pl: p.theme_parent ? 4.5 : 2.5 }}>
                      {p.theme_parent ? '↳ ' : ''}{p.kind !== 'theme' && p.region && REGION_FLAGS[p.region] ? `${REGION_FLAGS[p.region]} ` : ''}{p.name}
                      {p.kind === 'conviction' && p.owner ? ` · ${p.owner}` : ''} ({p.holdings_count} stocks)
                    </MenuItem>
                  )),
                ];
              });
            })()}
          </Select>
        )}

        {filteredPortfolios.length > 1 && (
          <Select
            value={compareId ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              setCompareId(!val ? null : Number(val));
              setCompareAnalysis(null);
            }}
            size="small"
            sx={{ minWidth: 180 }}
            displayEmpty
          >
            <MenuItem value=""><em style={{ color: 'var(--bb-text-secondary)', fontStyle: 'normal' }}>Compare with…</em></MenuItem>
            {filteredPortfolios.filter(p => p.id !== selectedId).map(p => (
              <MenuItem key={p.id} value={p.id}>vs. {p.name}</MenuItem>
            ))}
          </Select>
        )}

        {selectedId && analysis && (
          <Tooltip title="Download Portfolio Report (PDF)">
            <span>
              <IconButton
                size="small"
                onClick={async () => {
                  setPdfLoading(true);
                  try {
                    await downloadPortfolioReportPdf(selectedId, timeframe, selectedBenchmarks);
                  } catch (err) {
                    console.error('PDF report generation failed:', err);
                    alert('PDF report generation failed. Please try again.');
                  } finally {
                    setPdfLoading(false);
                  }
                }}
                disabled={pdfLoading}
                sx={{ color: pdfLoading ? '#64748b' : '#ff9800' }}
              >
                {pdfLoading ? <CircularProgress size={20} /> : <ArticleIcon />}
              </IconButton>
            </span>
          </Tooltip>
        )}

        {compareAnalysis && selectedId && compareId && (
          <Tooltip title="Download PDF comparison report">
            <span>
              <IconButton
                size="small"
                onClick={async () => {
                  setPdfLoading(true);
                  try {
                    await downloadComparisonPdf(selectedId, compareId, timeframe, selectedBenchmarks);
                  } catch (err) {
                    console.error('PDF generation failed:', err);
                    alert('PDF report generation failed. Please try again.');
                  } finally {
                    setPdfLoading(false);
                  }
                }}
                disabled={pdfLoading}
                sx={{ color: pdfLoading ? '#64748b' : '#ff9800' }}
              >
                {pdfLoading ? <CircularProgress size={20} /> : <CompareArrowsIcon />}
              </IconButton>
            </span>
          </Tooltip>
        )}

        {!activeOnly && <Button size="small" variant="outlined" onClick={() => setThemesOpen(true)}
          sx={{ textTransform: 'none', fontSize: 12, py: 0.2, whiteSpace: 'nowrap' }}>
          Theme models
        </Button>}
        <ThemesDialog open={themesOpen} onClose={() => setThemesOpen(false)}
          onOpenModel={(id) => { loadPortfolios(); setSelectedId(id); }}
          onChanged={() => loadPortfolios()} />
        {!activeOnly && <Tooltip title="New model portfolio">
          <IconButton size="small" color="primary" onClick={() => {
            setDialogMode('create');
            setDialogName('');
            setDialogDesc('');
            setDialogTags('');
            setDialogRegion('');
            setDialogOpen(true);
          }}>
            <AddIcon />
          </IconButton>
        </Tooltip>}

        {selectedId && (
          <>
            <Tooltip title="Edit name/description">
              <IconButton size="small" onClick={() => {
                setDialogMode('edit');
                setDialogName(portfolio?.name || '');
                setDialogDesc(portfolio?.description || '');
                setDialogTags(portfolio?.tags || '');
                setDialogRegion((portfolio?.region as PortfolioRegion) || '');
                setDialogOpen(true);
              }}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete portfolio">
              <IconButton size="small" color="error" onClick={() => setDeleteDialogOpen(true)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      {portfolio?.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {portfolio.description}
        </Typography>
      )}

      {(portfolio?.region || portfolio?.tags) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2, alignItems: 'center' }}>
          {portfolio?.region && (
            <Chip
              label={`${REGION_FLAGS[portfolio.region] || ''} ${portfolio.region}`}
              size="small"
              sx={{
                fontSize: '0.7rem', height: 22, fontWeight: 700,
                bgcolor: REGION_COLORS[portfolio.region] || bloomberg.accent.orange,
                color: '#fff',
              }}
            />
          )}
          {portfolio?.tags && portfolio.tags.split(',').map(tag => tag.trim()).filter(Boolean).map(tag => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 22, borderColor: bloomberg.accent.orange, color: bloomberg.accent.orange }}
            />
          ))}
        </Box>
      )}

      {loading && <CircularProgress size={24} sx={{ mb: 2 }} />}

      {selectedId && portfolio && !loading && (
        <ModelLineage portfolio={portfolio} onOpen={openModel} creating={creatingActive} onCreate={handleCreateActive}
          onCompare={(parentId) => { setCompareId(parentId); setPendingCompareTab(true); }} />
      )}

      {/* Apply Efficient Frontier weights to an active version */}
      <Dialog open={!!applyReq} onClose={() => !applying && setApplyReq(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Apply {applyReq?.meta.strategy} weights</DialogTitle>
        {applyReq && portfolio && (() => {
          const w = Object.entries(applyReq.weights).sort((a, b) => b[1] - a[1]);
          const dropped = portfolio.holdings.length - w.length;
          const kids = (portfolio.children || []).filter(c => c.kind === 'active');
          return (
            <DialogContent>
              <Typography sx={{ fontSize: 13, color: bloomberg.text.secondary, mb: 1.5 }}>
                {w.length} names · largest {w[0]?.[0]} at {((w[0]?.[1] || 0) * 100).toFixed(1)}%
                {' · '}{applyReq.meta.capPct >= 100 ? 'uncapped' : `${applyReq.meta.capPct}% cap`} · {applyReq.meta.period} window
                {dropped > 0 ? ` · ${dropped} name${dropped > 1 ? 's' : ''} at 0% left out` : ''}
              </Typography>
              {applyReq.meta.needsForecast && (
                <Alert severity="warning" sx={{ mb: 1.5, fontSize: 12.5 }}>
                  Max Sharpe leans on past returns and tends to pile into recent winners. Its backtest will look strong; the
                  real test is how it does from today, which the active version will track.
                </Alert>
              )}
              {applyReq.meta.fallback && (
                <Alert severity="warning" sx={{ mb: 1.5, fontSize: 12.5 }}>The optimiser did not converge; these are equal-weight fallback weights.</Alert>
              )}
              <FormControl fullWidth size="small">
                <InputLabel id="apply-target">Save to</InputLabel>
                <Select labelId="apply-target" label="Save to" value={applyTarget}
                  onChange={(e) => setApplyTarget(e.target.value === 'new' ? 'new' : Number(e.target.value))}>
                  {isActiveSection(portfolio.kind) && <MenuItem value={portfolio.id}>This portfolio ({portfolio.name})</MenuItem>}
                  {portfolio.kind !== 'active' && kids.map(k => <MenuItem key={k.id} value={k.id}>{k.name}</MenuItem>)}
                  <MenuItem value="new">New active version of {portfolio.kind === 'active' ? (portfolio.parent_name || 'the base') : portfolio.name}</MenuItem>
                </Select>
              </FormControl>
              <Typography sx={{ fontSize: 12, color: bloomberg.text.muted, mt: 1.25 }}>
                {applyTarget === portfolio.id
                  ? 'This portfolio\'s weights are replaced. The change is logged, and its real record against the yardstick restarts from today.'
                  : `${portfolio.kind === 'active' ? (portfolio.parent_name || 'The base') : portfolio.name} itself is not changed. The change is logged, and the active version starts a real track record against it from today.`}
              </Typography>
              {applyError && <Alert severity="error" sx={{ mt: 1.5 }}>{applyError}</Alert>}
            </DialogContent>
          );
        })()}
        <DialogActions>
          <Button onClick={() => setApplyReq(null)} disabled={applying}>Cancel</Button>
          <Button variant="contained" onClick={confirmApplyWeights} disabled={applying}>
            {applying ? 'Applying…' : 'Apply weights'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Holdings ── */}
      {selectedId && portfolio && !loading && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            <Tooltip title="ETFs drawn on the Performance and Risk charts for comparison. On the Active page, a portfolio is scored against its one Benchmark instead.">
              <Typography sx={{ fontSize: 12, color: bloomberg.text.muted, cursor: 'help' }}>Comparison ETFs</Typography>
            </Tooltip>
            {selectedBenchmarks.map(sym => (
              <Chip key={sym} size="small" label={sym} variant="outlined"
                onDelete={selectedBenchmarks.length > 1 ? () => handleToggleBenchmark(sym) : undefined}
                title={availableBenchmarks.find(b => b.symbol === sym)?.name || sym}
                sx={{ fontFamily: bloomberg.font.mono, fontSize: 12, borderColor: bloomberg.border.secondary }} />
            ))}
            <Button size="small" onClick={(e) => setBenchMenu(e.currentTarget)}
              sx={{ textTransform: 'none', fontSize: 12, border: `1px dashed ${bloomberg.border.secondary}`, px: 1.25, py: 0.1 }}>
              + ETF
            </Button>
            <Menu anchorEl={benchMenu} open={!!benchMenu} onClose={() => setBenchMenu(null)}
              slotProps={{ paper: { sx: { maxHeight: 420, bgcolor: bloomberg.bg.secondary, border: `1px solid ${bloomberg.border.primary}` } } }}>
              {Array.from(new Set(availableBenchmarks.map(b => b.category))).flatMap(cat => [
                <MenuItem key={`h-${cat}`} disabled sx={{ fontSize: 11, fontWeight: 700, opacity: 1, color: bloomberg.text.muted, textTransform: 'uppercase' }}>{cat}</MenuItem>,
                ...availableBenchmarks.filter(b => b.category === cat).map(b => (
                  <MenuItem key={b.symbol} onClick={() => { handleToggleBenchmark(b.symbol); setBenchMenu(null); }}
                    selected={selectedBenchmarks.includes(b.symbol)} sx={{ fontSize: 12.5, pl: 3 }}>
                    <b style={{ fontFamily: bloomberg.font.mono, width: 64, display: 'inline-block' }}>{b.symbol}</b> {b.name}
                  </MenuItem>
                )),
              ])}
            </Menu>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontSize: 12, color: bloomberg.text.muted }}>Period</Typography>
            <Select value={timeframe} onChange={(e) => setTimeframe(e.target.value as string)} size="small"
              sx={{ minWidth: 72, fontSize: 12 }} inputProps={{ 'aria-label': 'Analysis period' }}>
              {TIMEFRAMES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
            <Typography sx={{ fontSize: 12, color: bloomberg.text.muted, minWidth: 150, textAlign: 'right' }}>
              {analysisLoading ? 'Running analysis… can take a few minutes'
                : holdingsDirty ? 'Save holdings to re-run'
                : analysisAt ? `Analysis updated ${analysisAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                : portfolio.holdings.length < 2 ? 'Needs 2+ holdings to analyse' : ''}
            </Typography>
          </Box>
          <Tabs value={analysisTab} onChange={(_, v) => setAnalysisTab(v)} variant="scrollable" scrollButtons="auto"
            sx={{ borderBottom: `1px solid ${bloomberg.border.primary}` }}>
            <Tab label="Holdings" />
            <Tab label="Performance" />
            <Tab label="Risk" />
            <Tab label="Attribution" />
            <Tab label="Correlation" />
            <Tab label="Efficient Frontier" />
            <Tab label="Recommendations" />
            <Tab label="Monte Carlo" />
            {compareAnalysis && <Tab label={`vs ${portfolios.find(p => p.id === compareId)?.name || 'Portfolio'}`} />}
          </Tabs>
        </Box>
      )}

      {selectedId && portfolio && !loading && analysisTab === 0 && (
        <Box sx={{ mb: 2 }}>
        <Paper elevation={1} sx={{ p: 2 }}>
          {isThemeBase ? (
            <ThemeHoldingsTable holdings={portfolio.holdings} buildSpec={portfolio.build_spec} perf={holdingPerf}
              loading={holdingPerfLoading} onResearch={(sym) => navigate(`/research?symbol=${sym}`)} />
          ) : (<>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            Holdings & Target Weights
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Symbol</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Company</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }} align="right">3M</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }} align="right">1Y</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }} align="right">Weight %</TableCell>
                  {usesRoles && (
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }} align="center">Role</TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', width: 50 }} align="center" />
                </TableRow>
              </TableHead>
              <TableBody>
                {editHoldings.map((h, i) => (
                  <TableRow key={h.symbol}>
                    <TableCell>
                      <Chip label={h.symbol} size="small" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{h.name || h.symbol}</TableCell>
                    {(['r3m', 'r1y'] as const).map(k => {
                      const v = holdingPerf[h.symbol]?.[k];
                      return (
                        <TableCell key={k} align="right" sx={{ fontFamily: bloomberg.font.mono, fontSize: '0.78rem',
                          color: v === null || v === undefined ? bloomberg.text.muted : v >= 0 ? '#22c55e' : '#f87171' }}>
                          {holdingPerfLoading ? '…' : pctCell(v)}
                        </TableCell>
                      );
                    })}
                    <TableCell align="right" sx={{ width: 120 }}>
                      <TextField
                        type="number"
                        size="small"
                        value={Math.round(h.target_weight * 1000) / 10}
                        onChange={(e) => handleWeightChange(i, Math.max(0, Math.min(100, Number(e.target.value))) / 100)}
                        inputProps={{ min: 0, max: 100, step: 0.5, style: { textAlign: 'right', fontSize: '0.8rem' } }}
                        sx={{ width: 80 }}
                      />
                    </TableCell>
                    {usesRoles && (
                      <TableCell align="center">
                        <Chip
                          label={holdingRoles[h.symbol] === 'satellite' ? 'SATELLITE' : 'CORE'}
                          size="small"
                          onClick={() => {
                            const current = holdingRoles[h.symbol] || 'core';
                            setHoldingRoles(prev => ({ ...prev, [h.symbol]: current === 'core' ? 'satellite' : 'core' }));
                            setHoldingsDirty(true);
                          }}
                          sx={{
                            fontSize: 9, height: 18, fontWeight: 700, cursor: 'pointer',
                            bgcolor: holdingRoles[h.symbol] === 'satellite' ? 'rgba(106,27,154,0.15)' : 'rgba(0,105,92,0.15)',
                            color: holdingRoles[h.symbol] === 'satellite' ? '#6a1b9a' : '#00695c',
                            '&:hover': { opacity: 0.8 },
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0, justifyContent: 'center' }}>
                        <IconButton size="small" onClick={() => navigate(`/research?symbol=${h.symbol}`)} sx={{ color: '#a855f7' }} title="Research">
                          <ArticleIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleRemoveHolding(i)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}

                {/* Add ticker row */}
                <TableRow>
                  <TableCell colSpan={7}>
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={searchResults}
                      getOptionLabel={(opt) =>
                        typeof opt === 'string' ? opt : `${opt.symbol} — ${opt.name}`
                      }
                      inputValue={searchQuery}
                      onInputChange={(_, val) => setSearchQuery(val)}
                      onChange={(_, val) => {
                        if (val && typeof val !== 'string') {
                          handleAddHolding(val.symbol, val.name);
                        }
                      }}
                      loading={searchLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search ticker to add..."
                          variant="outlined"
                          sx={{ maxWidth: 400 }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={typeof option === 'string' ? option : option.symbol}>
                          <Typography sx={{ fontSize: '0.8rem' }}>
                            <strong>{typeof option === 'string' ? option : option.symbol}</strong>
                            {typeof option !== 'string' && ` — ${option.name}`}
                          </Typography>
                        </li>
                      )}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          </>)}

          {/* Weight sum indicator */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
            {!isThemeBase && (<>
            <Box sx={{ flex: 1, maxWidth: 300 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(totalWeight * 100, 100)}
                color={weightValid ? 'success' : totalWeight > 1.02 ? 'error' : 'warning'}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: weightValid ? '#4caf50' : '#f44336' }}
            >
              {(totalWeight * 100).toFixed(1)}%
              {weightValid ? (
                <CheckCircleIcon sx={{ fontSize: 16, ml: 0.5, verticalAlign: 'text-bottom' }} />
              ) : (
                <WarningAmberIcon sx={{ fontSize: 16, ml: 0.5, verticalAlign: 'text-bottom' }} />
              )}
            </Typography>

            <Button
              variant="contained"
              size="small"
              startIcon={<SaveIcon />}
              disabled={!holdingsDirty || savingHoldings || editHoldings.length === 0}
              onClick={handleSaveHoldings}
            >
              {savingHoldings ? 'Saving...' : 'Save Holdings'}
            </Button>
            </>)}

          </Box>

        </Paper>

        </Box>
      )}

      {/* ── Portfolio Auto-Alerts ── */}
      {selectedId && alertConfig && analysisTab === 0 && (
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <NotificationsActiveIcon sx={{ color: bloomberg.accent.orange, fontSize: 18 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: bloomberg.accent.orange }}>
              Portfolio Auto-Alerts
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={alertConfig.is_active}
                  onChange={(_, v) => { setAlertConfig(c => c ? { ...c, is_active: v } : c); setAlertConfigDirty(true); }}
                />
              }
              label={<Typography variant="body2" sx={{ fontSize: 12 }}>Active</Typography>}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={alertConfig.pct_change_enabled}
                  onChange={(_, v) => { setAlertConfig(c => c ? { ...c, pct_change_enabled: v } : c); setAlertConfigDirty(true); }}
                />
              }
              label={<Typography variant="body2" sx={{ fontSize: 12 }}>Alert on % move</Typography>}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontSize: 12, color: bloomberg.text.secondary }}>
                Alert when any holding moves ±
              </Typography>
              <TextField
                size="small"
                type="number"
                value={alertConfig.pct_change_threshold}
                onChange={(e) => { setAlertConfig(c => c ? { ...c, pct_change_threshold: parseFloat(e.target.value) || 2.0 } : c); setAlertConfigDirty(true); }}
                inputProps={{ min: 0.1, max: 20, step: 0.5 }}
                sx={{ width: 80 }}
              />
              <Typography variant="body2" sx={{ fontSize: 12, color: bloomberg.text.secondary }}>% in 1 day</Typography>
            </Box>
            <Button
              size="small"
              variant="contained"
              disabled={!alertConfigDirty || alertConfigSaving}
              onClick={async () => {
                if (!selectedId || !alertConfig) return;
                setAlertConfigSaving(true);
                try {
                  await savePortfolioAlertConfig(selectedId, alertConfig);
                  setAlertConfigDirty(false);
                  setAlertSnackbar({ open: true, message: 'Alert config saved', severity: 'success' });
                } catch {
                  setAlertSnackbar({ open: true, message: 'Failed to save alert config', severity: 'error' });
                } finally {
                  setAlertConfigSaving(false);
                }
              }}
              sx={{ fontSize: 11 }}
            >
              Save
            </Button>
          </Box>
        </Paper>
      )}

      {/* ── Analysis Dashboard ── */}
      {selectedId && !analysis && !analysisLoading && analysisTab !== 0 && (
        <Typography sx={{ py: 4, textAlign: 'center', color: bloomberg.text.muted, fontSize: 13 }}>
          {portfolio && portfolio.holdings.length < 2 ? 'This model needs at least two holdings to analyse.'
            : holdingsDirty ? 'Save the holdings to run the analysis.' : 'The analysis could not be run for this model.'}
        </Typography>
      )}

      {analysisLoading && analysisTab !== 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Running correlation & performance analysis. This can take a few minutes; you can keep using the
            app and a pop-up will tell you when it's ready.
          </Typography>
        </Box>
      )}

      {analysis && !analysisLoading && (
        <ThemeProvider theme={analysisTheme}>
        <Paper elevation={0} sx={{ p: 2 }}>

          {/* ── Tab 0: Overview ── */}
          {/* Tab 0: Overview */}
          {analysisTab === 0 && (
            <Box>
              {portfolio?.kind === 'theme' && portfolio.build_spec && <ThemeBuildCard spec={portfolio.build_spec} />}
              <RebalanceHistoryPanel history={rebalanceHistory} />
              {/* Core vs Satellite summary for THEMATIC portfolios */}
              {usesRoles && analysis?.risk_metrics?.holding_metrics && (() => {
                const hm = analysis.risk_metrics.holding_metrics;
                const coreHoldings = editHoldings.filter(h => (holdingRoles[h.symbol] || 'core') === 'core');
                const satHoldings = editHoldings.filter(h => holdingRoles[h.symbol] === 'satellite');
                const coreWeight = coreHoldings.reduce((s, h) => s + h.target_weight, 0);
                const satWeight = satHoldings.reduce((s, h) => s + h.target_weight, 0);
                const coreReturn = coreHoldings.length > 0
                  ? coreHoldings.reduce((s, h) => { const m = hm.find((x: any) => x.symbol === h.symbol); return s + (m ? m.total_return * h.target_weight : 0); }, 0) / (coreWeight || 1)
                  : null;
                const satReturn = satHoldings.length > 0
                  ? satHoldings.reduce((s, h) => { const m = hm.find((x: any) => x.symbol === h.symbol); return s + (m ? m.total_return * h.target_weight : 0); }, 0) / (satWeight || 1)
                  : null;
                return (
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                    <Paper elevation={0} sx={{ p: 2, borderLeft: `4px solid #00695c` }}>
                      <Typography variant="caption" sx={{ color: '#00695c', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>🏛 Core</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 0.5 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>{(coreWeight * 100).toFixed(0)}%</Typography>
                        {coreReturn !== null && (
                          <Typography sx={{ fontSize: 14, fontWeight: 600, color: coreReturn >= 0 ? '#4caf50' : '#ef5350' }}>
                            {coreReturn >= 0 ? '+' : ''}{coreReturn.toFixed(1)}% return
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">{coreHoldings.length} holdings</Typography>
                    </Paper>
                    <Paper elevation={0} sx={{ p: 2, borderLeft: `4px solid #6a1b9a` }}>
                      <Typography variant="caption" sx={{ color: '#6a1b9a', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>🛰 Satellite</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 0.5 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>{(satWeight * 100).toFixed(0)}%</Typography>
                        {satReturn !== null && (
                          <Typography sx={{ fontSize: 14, fontWeight: 600, color: satReturn >= 0 ? '#4caf50' : '#ef5350' }}>
                            {satReturn >= 0 ? '+' : ''}{satReturn.toFixed(1)}% return
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">{satHoldings.length} holdings</Typography>
                    </Paper>
                  </Box>
                );
              })()}
              {/* Top-level score + risk-adjusted cards */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
                <Tooltip title="Measures how uncorrelated your holdings are. Based on average pairwise correlation — lower correlation means better diversification. Score 0–100, where 100 = perfectly uncorrelated holdings." arrow placement="top">
                <Paper elevation={0} sx={{ p: 2, textAlign: 'center', cursor: 'help' }}>
                  <Typography variant="caption" color="text.secondary">Diversification</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: scoreColor(analysis.score.diversification_score) }}>
                    {analysis.score.diversification_score}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Avg corr: {analysis.score.avg_pairwise_correlation.toFixed(2)}
                  </Typography>
                </Paper>
                </Tooltip>

                <Paper elevation={0} sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Holdings</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {analysis.score.num_holdings}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Max weight: {(analysis.score.max_weight * 100).toFixed(1)}%
                  </Typography>
                </Paper>

                <Paper elevation={0} sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Weights</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: analysis.score.weight_valid ? '#4caf50' : '#f44336' }}>
                    {analysis.score.weight_valid ? 'Valid' : 'Invalid'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total: {(analysis.score.total_weight * 100).toFixed(1)}%
                  </Typography>
                </Paper>
              </Box>

              {/* Risk-adjusted ratios row (from risk_metrics) */}
              {analysis.risk_metrics && !analysis.risk_metrics.error && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
                  <MetricCard label="Sharpe Ratio" value={analysis.risk_metrics.sharpe_ratio.toFixed(2)} tooltip="Risk-adjusted return (excess return / volatility)" color={analysis.risk_metrics.sharpe_ratio >= 1 ? '#4caf50' : analysis.risk_metrics.sharpe_ratio >= 0.5 ? '#ff9800' : '#f44336'} />
                  <MetricCard label="Sortino Ratio" value={analysis.risk_metrics.sortino_ratio.toFixed(2)} tooltip="Downside risk-adjusted return" color={analysis.risk_metrics.sortino_ratio >= 1.5 ? '#4caf50' : analysis.risk_metrics.sortino_ratio >= 0.7 ? '#ff9800' : '#f44336'} />
                  <MetricCard label="Calmar Ratio" value={analysis.risk_metrics.calmar_ratio.toFixed(2)} tooltip="Annualized return / max drawdown" color={analysis.risk_metrics.calmar_ratio >= 1 ? '#4caf50' : analysis.risk_metrics.calmar_ratio >= 0.5 ? '#ff9800' : '#f44336'} />
                  <MetricCard label="Max Drawdown" value={`${analysis.risk_metrics.max_drawdown.toFixed(1)}%`} tooltip="Largest peak-to-trough decline" color={analysis.risk_metrics.max_drawdown > -15 ? '#4caf50' : analysis.risk_metrics.max_drawdown > -30 ? '#ff9800' : '#f44336'} />
                  <MetricCard label="Volatility (ann.)" value={`${analysis.risk_metrics.volatility.toFixed(1)}%`} tooltip="Annualized standard deviation of returns" />
                </Box>
              )}

              {/* Multi-benchmark comparison table */}
              {analysis.risk_metrics && analysis.risk_metrics.benchmark_comparisons?.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Benchmark Comparison
                  </Typography>
                  <BenchmarkComparisonTable
                    comparisons={analysis.risk_metrics.benchmark_comparisons}
                    portfolioReturn={analysis.risk_metrics.annualized_return}
                    portfolioVol={analysis.risk_metrics.volatility}
                    portfolioSharpe={analysis.risk_metrics.sharpe_ratio}
                    portfolioMaxDD={analysis.risk_metrics.max_drawdown}
                  />
                </Box>
              )}

              {/* Weighted portfolio returns */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Weighted Portfolio Returns
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                {PERF_LABELS.filter(({ key }) => key !== 'price').map(({ key, label }) => {
                  const val = (analysis.performance.weighted as any)[key] as number | null | undefined;
                  return (
                    <Paper key={key} elevation={0} sx={{ p: 1.5, textAlign: 'center', minWidth: 80 }}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: perfColor(val) }}>
                        {fmtPerf(val)}
                      </Typography>
                    </Paper>
                  );
                })}
              </Box>

              {/* High correlation warnings */}
              {analysis.score.high_correlation_pairs.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#ff9800' }}>
                    <WarningAmberIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />
                    High Correlation Pairs (|r| {'>'} 0.7)
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {analysis.score.high_correlation_pairs.map((pair, i) => (
                      <Chip
                        key={i}
                        label={`${pair.symbol1} / ${pair.symbol2}: ${pair.correlation.toFixed(2)}`}
                        size="small"
                        color={pair.correlation > 0.85 ? 'error' : 'warning'}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* ── Tab 2: Risk Analytics ── */}
          {analysisTab === 2 && (
            <Box>
              {analysis.risk_metrics && !analysis.risk_metrics.error ? (
                <RiskAnalyticsPanel risk={analysis.risk_metrics} />
              ) : (
                <Alert severity="warning">
                  {analysis.risk_metrics?.error || 'Risk metrics unavailable — insufficient historical data.'}
                </Alert>
              )}
            </Box>
          )}

          {/* ── Tab 3: Attribution ── */}
          {analysisTab === 3 && (
            <Box>
              {/* Controls row */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Benchmark</Typography>
                  <Select
                    size="small"
                    value={attributionBenchmark || selectedBenchmarks[0] || 'SPY'}
                    onChange={e => setAttributionBenchmark(e.target.value)}
                    sx={{ minWidth: 120, fontSize: '0.85rem' }}
                  >
                    {(availableBenchmarks.length > 0
                      ? availableBenchmarks
                      : [{ symbol: 'SPY', name: 'S&P 500', category: '' }, { symbol: 'QQQ', name: 'NASDAQ 100', category: '' }]
                    ).map(b => (
                      <MenuItem key={b.symbol} value={b.symbol} sx={{ fontSize: '0.85rem' }}>
                        {b.symbol} — {b.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Timeframe</Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={attributionTimeframe}
                    onChange={(_, v) => { if (v) setAttributionTimeframe(v); }}
                  >
                    {['1M', '3M', '6M', '1Y', '2Y'].map(tf => (
                      <ToggleButton key={tf} value={tf} sx={{ fontSize: '0.75rem', px: 1.5 }}>{tf}</ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', pb: 0.25 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleLoadAttribution}
                    disabled={attributionLoading}
                    startIcon={attributionLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
                  >
                    {attributionLoading ? 'Calculating…' : 'Calculate'}
                  </Button>
                </Box>
              </Box>

              {/* Results */}
              {attribution && (
                <>
                  {/* Summary KPIs */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    {[
                      { label: 'Portfolio Return', value: attribution.portfolio_total_return, fmt: true },
                      { label: `${attribution.benchmark} Return`, value: attribution.benchmark_total_return, fmt: true },
                      { label: 'Active Return', value: attribution.active_return, fmt: true, bold: true },
                      { label: 'Outperformers', value: attribution.n_outperformers, fmt: false },
                      { label: 'Underperformers', value: attribution.n_underperformers, fmt: false },
                    ].map(kpi => (
                      <Paper key={kpi.label} elevation={0} variant="outlined" sx={{ p: 1.5, minWidth: 140, flex: '1 1 140px' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{kpi.label}</Typography>
                        <Typography sx={{
                          fontSize: '1.1rem',
                          fontWeight: kpi.bold ? 700 : 600,
                          color: kpi.fmt
                            ? ((kpi.value as number) > 0 ? '#4caf50' : (kpi.value as number) < 0 ? '#f44336' : 'text.primary')
                            : 'text.primary'
                        }}>
                          {kpi.fmt
                            ? `${(kpi.value as number) > 0 ? '+' : ''}${(kpi.value as number).toFixed(2)}%`
                            : String(kpi.value)
                          }
                        </Typography>
                      </Paper>
                    ))}
                  </Box>

                  {/* BHB decomposition bar */}
                  {attribution.brinson_totals && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5, flexWrap: 'wrap', p: 1.5, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mr: 0.5 }}>Active Return decomposition:</Typography>
                      {[
                        { label: 'Allocation', value: attribution.brinson_totals.allocation_effect },
                        { label: 'Selection', value: attribution.brinson_totals.selection_effect },
                        { label: 'Interaction', value: attribution.brinson_totals.interaction_effect },
                      ].map((item, i) => (
                        <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {i > 0 && <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>+</Typography>}
                          <Box sx={{
                            px: 1, py: 0.25, borderRadius: 1, border: '1px solid',
                            borderColor: item.value >= 0 ? 'rgba(76,175,80,0.4)' : 'rgba(244,67,54,0.4)',
                            bgcolor: item.value >= 0 ? 'rgba(76,175,80,0.08)' : 'rgba(244,67,54,0.08)',
                          }}>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: item.value >= 0 ? '#4caf50' : '#f44336' }}>
                              {item.label}: {item.value >= 0 ? '+' : ''}{item.value.toFixed(2)}%
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mx: 0.5 }}>=</Typography>
                      <Box sx={{ px: 1, py: 0.25, borderRadius: 1, border: '1px solid', borderColor: 'rgba(255,152,0,0.5)', bgcolor: 'rgba(255,152,0,0.1)' }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#ff9800' }}>
                          Total: {attribution.brinson_totals.total_active >= 0 ? '+' : ''}{attribution.brinson_totals.total_active.toFixed(2)}%
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* BHB sector table */}
                  {attribution.brinson && attribution.brinson.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                        Brinson-Hood-Beebower Attribution by Sector
                      </Typography>
                      <TableContainer component={Paper} elevation={0} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Sector</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Port Wt</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Bench Wt</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Port Ret</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Bench Ret</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#ff9800' }}>Allocation</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#ff9800' }}>Selection</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#ff9800' }}>Interaction</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#ff9800' }}>Total Active</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {attribution.brinson.map((row: BrinsonRow) => (
                              <TableRow key={row.sector} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
                                <TableCell sx={{ fontSize: '0.78rem', fontWeight: 500 }}>{row.sector}</TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.78rem' }}>{row.portfolio_weight.toFixed(1)}%</TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{row.benchmark_weight.toFixed(1)}%</TableCell>
                                <TableCell align="right">
                                  <Typography sx={{ fontSize: '0.78rem', color: row.portfolio_sector_return >= 0 ? '#4caf50' : '#f44336' }}>
                                    {row.portfolio_sector_return >= 0 ? '+' : ''}{row.portfolio_sector_return.toFixed(1)}%
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                                  {row.benchmark_sector_return >= 0 ? '+' : ''}{row.benchmark_sector_return.toFixed(1)}%
                                </TableCell>
                                {[row.allocation_effect, row.selection_effect, row.interaction_effect, row.total_active].map((val, i) => (
                                  <TableCell key={i} align="right">
                                    <Typography sx={{ fontSize: i === 3 ? '0.82rem' : '0.78rem', fontWeight: i === 3 ? 700 : 500, color: val > 0 ? '#4caf50' : val < 0 ? '#f44336' : 'text.secondary' }}>
                                      {val > 0 ? '+' : ''}{val.toFixed(2)}%
                                    </Typography>
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                            {/* Totals row */}
                            {attribution.brinson_totals && (
                              <TableRow sx={{ borderTop: '2px solid', borderColor: 'divider', bgcolor: 'rgba(255,255,255,0.04)' }}>
                                <TableCell colSpan={5} sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'text.secondary' }}>TOTAL</TableCell>
                                {[attribution.brinson_totals.allocation_effect, attribution.brinson_totals.selection_effect, attribution.brinson_totals.interaction_effect, attribution.brinson_totals.total_active].map((val, i) => (
                                  <TableCell key={i} align="right">
                                    <Typography sx={{ fontSize: i === 3 ? '0.85rem' : '0.78rem', fontWeight: 700, color: val > 0 ? '#4caf50' : val < 0 ? '#f44336' : 'text.secondary' }}>
                                      {val > 0 ? '+' : ''}{val.toFixed(2)}%
                                    </Typography>
                                  </TableCell>
                                ))}
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  {/* Per-holding table */}
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Selection Effect by Holding</Typography>
                  <TableContainer component={Paper} elevation={0} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Symbol</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Sector</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Weight</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Holding Return</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Benchmark Return</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Active Return</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Active Contribution</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...attribution.holdings].sort((a, b) => Math.abs(b.active_contribution) - Math.abs(a.active_contribution)).map(h => (
                          <TableRow key={h.symbol}>
                            <TableCell>
                              <TickerLink symbol={h.symbol} fontSize="0.8rem" fontWeight={700} />
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{h.sector || '—'}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{(h.weight * 100).toFixed(1)}%</TableCell>
                            <TableCell align="right">
                              <Typography sx={{ fontSize: '0.8rem', color: h.holding_total_return > 0 ? '#4caf50' : h.holding_total_return < 0 ? '#f44336' : 'text.primary' }}>
                                {h.holding_total_return > 0 ? '+' : ''}{h.holding_total_return.toFixed(2)}%
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                              {h.benchmark_total_return.toFixed(2)}%
                            </TableCell>
                            <TableCell align="right">
                              <Typography sx={{ fontSize: '0.8rem', color: h.active_return > 0 ? '#4caf50' : h.active_return < 0 ? '#f44336' : 'text.primary' }}>
                                {h.active_return > 0 ? '+' : ''}{h.active_return.toFixed(2)}%
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: h.active_contribution > 0 ? '#4caf50' : h.active_contribution < 0 ? '#f44336' : 'text.primary' }}>
                                {h.active_contribution > 0 ? '+' : ''}{h.active_contribution.toFixed(2)}%
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {!attribution && !attributionLoading && (
                <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                  <Typography variant="body2">Select a benchmark and timeframe, then click Calculate to run Brinson attribution.</Typography>
                </Box>
              )}
            </Box>
          )}

          {/* ── Tab 4: Correlation ── */}
          {analysisTab === 4 && analysis.correlation && (
            <CorrelationHeatmap
              symbols={analysis.correlation.symbols}
              symbolNames={analysis.correlation.symbol_names}
              matrix={analysis.correlation.matrix}
            />
          )}

          {/* ── Tab 5: Efficient Frontier ── */}
          {analysisTab === 5 && portfolio && (
            <Box>
              {portfolios.length > 1 && (
                <Paper elevation={0} variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                    Combine portfolios for frontier analysis:
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 320 }}>
                    <InputLabel>Portfolios</InputLabel>
                    <Select
                      multiple
                      value={frontierPortfolioIds}
                      onChange={(e) => {
                        const newIds = (typeof e.target.value === 'string'
                          ? e.target.value.split(',').map(Number)
                          : e.target.value) as number[];
                        // A native multi-select changes one item per click — find it and
                        // route through the existing handler (it fetches/caches details too).
                        const added = newIds.find(id => !frontierPortfolioIds.includes(id));
                        const removed = frontierPortfolioIds.find(id => !newIds.includes(id));
                        const toggled = added ?? removed;
                        if (toggled != null) handleToggleFrontierPortfolio(toggled);
                      }}
                      input={<OutlinedInput label="Portfolios" />}
                      renderValue={(sel) => {
                        if (sel.length <= 2) {
                          return sel.map(id => portfolios.find(p => p.id === id)?.name || id).join(', ');
                        }
                        return `${sel.length} portfolios`;
                      }}
                      MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
                    >
                      {portfolios.map(p => {
                        const isCurrent = p.id === selectedId;
                        return (
                          <MenuItem key={p.id} value={p.id} disabled={isCurrent}>
                            <Checkbox checked={frontierPortfolioIds.includes(p.id)} size="small" />
                            <ListItemText
                              primary={p.name}
                              secondary={isCurrent ? `${p.holdings_count} holdings · current portfolio, always included` : `${p.holdings_count} holdings`}
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                              secondaryTypographyProps={{ variant: 'caption' }}
                            />
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  {frontierPortfolioIds.length > 1 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Combining {frontierPortfolioIds.length} portfolios · {mergedFrontierData.symbols.length} unique holdings
                    </Typography>
                  )}
                </Paper>
              )}
              <EfficientFrontier
                symbols={mergedFrontierData.symbols.length >= 2 ? mergedFrontierData.symbols : portfolio.holdings.map(h => h.symbol)}
                userWeights={frontierPortfolioIds.length > 1 ? mergedFrontierData.weights : userWeightsMap}
                onApplyWeights={frontierPortfolioIds.length > 1 ? undefined : handleApplyWeights}
                jobLabel={`Efficient Frontier · ${portfolio.name}`}
                jobRoute={modelRoute(portfolio.id, portfolio.kind, 'frontier')}
              />
            </Box>
          )}

          {/* ── Tab 7: Monte Carlo ── */}
          {analysisTab === 7 && portfolio && (
            <MonteCarloSimulation
              symbols={portfolio.holdings.map((h: any) => h.symbol)}
              userWeights={userWeightsMap}
              jobLabel={`Monte Carlo · ${portfolio.name}`}
              jobRoute={modelRoute(portfolio.id, portfolio.kind, 'monte-carlo')}
            />
          )}

          {/* ── Tab 1: Performance ── */}
          {analysisTab === 1 && (() => {
            const handlePerfSort = (col: string) => {
              if (perfSortCol === col) {
                if (perfSortDir === 'desc') setPerfSortDir('asc');
                else { setPerfSortCol(null); setPerfSortDir('desc'); }
              } else {
                setPerfSortCol(col);
                setPerfSortDir('desc');
              }
            };
            const sortedHoldings = [...(portfolio?.holdings || [])].sort((a, b) => {
              if (!perfSortCol) return 0;
              if (perfSortCol === 'weight') {
                return perfSortDir === 'desc' ? b.target_weight - a.target_weight : a.target_weight - b.target_weight;
              }
              const perfA = analysis.performance.individual[a.symbol] || {};
              const perfB = analysis.performance.individual[b.symbol] || {};
              const va = (perfA as any)[perfSortCol] ?? -Infinity;
              const vb = (perfB as any)[perfSortCol] ?? -Infinity;
              return perfSortDir === 'desc' ? vb - va : va - vb;
            });

            const fmtPct = (v: number | null | undefined, invert = false) => {
              if (v == null) return <Typography sx={{ fontSize: '0.75rem', color: '#718096' }}>-</Typography>;
              const color = invert ? (v <= -5 ? '#4caf50' : '#f44336') : (v >= 0 ? '#4caf50' : '#f44336');
              const sign = invert ? (v <= 0 ? '' : '+') : (v >= 0 ? '+' : '');
              return <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color }}>{sign}{(invert ? -v : v).toFixed(1)}%</Typography>;
            };
            const fmtRsi = (v: number | null | undefined) => {
              if (v == null) return <Typography sx={{ fontSize: '0.75rem', color: '#718096' }}>-</Typography>;
              const color = v >= 70 ? '#f44336' : v <= 30 ? '#4caf50' : 'text.primary';
              return <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color }}>{v.toFixed(1)}</Typography>;
            };
            const fmtNum = (v: number | null | undefined, decimals = 1, suffix = '') => {
              if (v == null) return <Typography sx={{ fontSize: '0.75rem', color: '#718096' }}>-</Typography>;
              return <Typography sx={{ fontSize: '0.75rem' }}>{v.toFixed(decimals)}{suffix}</Typography>;
            };
            const fmtMktCap = (v: number | null | undefined) => {
              if (v == null) return '-';
              if (v >= 1e12) return `$${(v / 1e12).toFixed(1)}T`;
              if (v >= 1e9)  return `$${(v / 1e9).toFixed(1)}B`;
              if (v >= 1e6)  return `$${(v / 1e6).toFixed(0)}M`;
              return `$${v.toFixed(0)}`;
            };

            const GROUP_HEADER_SX = {
              fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: bloomberg.text.muted,
              borderBottom: `1px solid ${bloomberg.border.primary}`,
              bgcolor: bloomberg.bg.tertiary, py: 0.4,
            };

            const renderHeaderCell = (col: string, label: string) => (
              <TableCell key={col} align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', whiteSpace: 'nowrap', bgcolor: bloomberg.bg.tertiary, color: bloomberg.accent.orange }}>
                <TableSortLabel
                  active={perfSortCol === col}
                  direction={perfSortCol === col ? perfSortDir : 'desc'}
                  onClick={() => handlePerfSort(col)}
                >
                  {label}
                </TableSortLabel>
              </TableCell>
            );

            return (
            <Box>
              {/* View switcher */}
              <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
                {(['performance', 'technicals', 'fundamentals'] as ('performance' | 'technicals' | 'fundamentals')[]).map(v => (
                  <Chip
                    key={v}
                    label={v.charAt(0).toUpperCase() + v.slice(1)}
                    size="small"
                    clickable
                    onClick={() => setPerfView(v)}
                    sx={{
                      fontSize: 11, height: 24,
                      bgcolor: perfView === v ? bloomberg.accent.orange : 'transparent',
                      color: perfView === v ? '#000' : bloomberg.text.muted,
                      border: `1px solid ${perfView === v ? bloomberg.accent.orange : bloomberg.border.primary}`,
                      fontWeight: perfView === v ? 700 : 400,
                      '&:hover': { bgcolor: perfView === v ? bloomberg.accent.orange : bloomberg.bg.hover },
                    }}
                  />
                ))}
              </Box>

              <TableContainer>
                <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { px: 1, py: 0.5 } }}>

                  {/* ── Performance view ── */}
                  {perfView === 'performance' && (<>
                  <TableHead>
                    <TableRow>
                      <TableCell rowSpan={2} sx={{ fontWeight: 700, fontSize: '0.7rem', bgcolor: bloomberg.bg.tertiary, color: bloomberg.accent.orange, verticalAlign: 'bottom' }}>Symbol</TableCell>
                      <TableCell rowSpan={2} sx={{ fontWeight: 700, fontSize: '0.7rem', bgcolor: bloomberg.bg.tertiary, color: bloomberg.accent.orange, verticalAlign: 'bottom' }}>Company</TableCell>
                      <TableCell rowSpan={2} align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', bgcolor: bloomberg.bg.tertiary, color: bloomberg.accent.orange, verticalAlign: 'bottom' }}>
                        <TableSortLabel active={perfSortCol === 'weight'} direction={perfSortCol === 'weight' ? perfSortDir : 'desc'} onClick={() => handlePerfSort('weight')}>Wt%</TableSortLabel>
                      </TableCell>
                      <TableCell align="center" colSpan={2} sx={{ ...GROUP_HEADER_SX }}>Price</TableCell>
                      <TableCell align="center" colSpan={7} sx={{ ...GROUP_HEADER_SX }}>Returns</TableCell>
                    </TableRow>
                    <TableRow>
                      {renderHeaderCell('price', 'Last')}
                      {renderHeaderCell('day_1', '1D')}
                      {renderHeaderCell('week_1', '1W')}
                      {renderHeaderCell('month_1', '1M')}
                      {renderHeaderCell('month_3', '3M')}
                      {renderHeaderCell('ytd', 'YTD')}
                      {renderHeaderCell('year_1', '1Y')}
                      {renderHeaderCell('year_2', '2Y')}
                      {renderHeaderCell('year_5', '5Y')}
                    </TableRow>
                  </TableHead>
                  <TableBody key={`perf-sort-${perfSortCol}-${perfSortDir}-perf`}>
                    {sortedHoldings.map(h => {
                      const perf = analysis.performance.individual[h.symbol] || {};
                      return (
                        <TableRow key={h.symbol} hover>
                          <TableCell><TickerLink symbol={h.symbol} fontSize="0.78rem" fontWeight={700} /></TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>{h.name}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{(h.target_weight * 100).toFixed(1)}%</TableCell>
                          <TableCell align="right"><Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{(perf as any).price != null ? (perf as any).price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</Typography></TableCell>
                          {['day_1','week_1','month_1','month_3','ytd','year_1','year_2','year_5'].map(k =>
                            <TableCell key={k} align="right">{fmtPct((perf as any)[k])}</TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                    <TableRow sx={{ '& td': { borderTop: '2px solid', borderColor: 'divider', fontWeight: 700, bgcolor: `${bloomberg.bg.tertiary}88` } }}>
                      <TableCell colSpan={2}><Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>Weighted Portfolio</Typography></TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem' }}>100%</TableCell>
                      <TableCell />
                      {['day_1','week_1','month_1','month_3','ytd','year_1','year_2','year_5'].map(k => {
                        const val = (analysis.performance.weighted as any)[k] as number | null | undefined;
                        return <TableCell key={k} align="right"><Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: perfColor(val) }}>{fmtPerf(val)}</Typography></TableCell>;
                      })}
                    </TableRow>
                  </TableBody>
                  </>)}

                  {/* ── Technicals view ── */}
                  {perfView === 'technicals' && (<>
                  <TableHead>
                    <TableRow>
                      <TableCell rowSpan={2} sx={{ fontWeight: 700, fontSize: '0.7rem', bgcolor: bloomberg.bg.tertiary, color: bloomberg.accent.orange, verticalAlign: 'bottom' }}>Symbol</TableCell>
                      <TableCell rowSpan={2} sx={{ fontWeight: 700, fontSize: '0.7rem', bgcolor: bloomberg.bg.tertiary, color: bloomberg.accent.orange, verticalAlign: 'bottom' }}>Company</TableCell>
                      <TableCell rowSpan={2} align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', bgcolor: bloomberg.bg.tertiary, color: bloomberg.accent.orange, verticalAlign: 'bottom' }}>
                        <TableSortLabel active={perfSortCol === 'weight'} direction={perfSortCol === 'weight' ? perfSortDir : 'desc'} onClick={() => handlePerfSort('weight')}>Wt%</TableSortLabel>
                      </TableCell>
                      <TableCell align="center" colSpan={2} sx={{ ...GROUP_HEADER_SX }}>Momentum</TableCell>
                      <TableCell align="center" colSpan={3} sx={{ ...GROUP_HEADER_SX }}>vs. Moving Avg</TableCell>
                      <TableCell align="center" colSpan={2} sx={{ ...GROUP_HEADER_SX }}>52-Week Range</TableCell>
                    </TableRow>
                    <TableRow>
                      {renderHeaderCell('day_1', '1D')}
                      {renderHeaderCell('rsi', 'RSI')}
                      {renderHeaderCell('pct_from_sma20', '% 20MA')}
                      {renderHeaderCell('pct_from_sma50', '% 50MA')}
                      {renderHeaderCell('pct_from_sma200', '% 200MA')}
                      {renderHeaderCell('pct_from_52w_high', '52W Hi')}
                      {renderHeaderCell('pct_from_52w_low', '52W Lo')}
                    </TableRow>
                  </TableHead>
                  <TableBody key={`perf-sort-${perfSortCol}-${perfSortDir}-tech`}>
                    {sortedHoldings.map(h => {
                      const perf = analysis.performance.individual[h.symbol] || {};
                      return (
                        <TableRow key={h.symbol} hover>
                          <TableCell><TickerLink symbol={h.symbol} fontSize="0.78rem" fontWeight={700} /></TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>{h.name}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{(h.target_weight * 100).toFixed(1)}%</TableCell>
                          <TableCell align="right">{fmtPct((perf as any).day_1)}</TableCell>
                          <TableCell align="right">{fmtRsi((perf as any).rsi)}</TableCell>
                          <TableCell align="right">{fmtPct((perf as any).pct_from_sma20)}</TableCell>
                          <TableCell align="right">{fmtPct((perf as any).pct_from_sma50)}</TableCell>
                          <TableCell align="right">{fmtPct((perf as any).pct_from_sma200)}</TableCell>
                          <TableCell align="right">
                            {(perf as any).pct_from_52w_high != null
                              ? <Typography sx={{ fontSize: '0.75rem', color: Math.abs((perf as any).pct_from_52w_high) <= 5 ? '#4caf50' : 'text.primary' }}>-{Math.abs((perf as any).pct_from_52w_high).toFixed(1)}%</Typography>
                              : <Typography sx={{ fontSize: '0.75rem', color: '#718096' }}>-</Typography>}
                          </TableCell>
                          <TableCell align="right">
                            {(perf as any).pct_from_52w_low != null
                              ? <Typography sx={{ fontSize: '0.75rem', color: '#ff9800' }}>+{(perf as any).pct_from_52w_low.toFixed(1)}%</Typography>
                              : <Typography sx={{ fontSize: '0.75rem', color: '#718096' }}>-</Typography>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                  </>)}

                  {/* ── Fundamentals view ── */}
                  {perfView === 'fundamentals' && (<>
                  <TableHead>
                    <TableRow>
                      <TableCell rowSpan={2} sx={{ fontWeight: 700, fontSize: '0.7rem', bgcolor: bloomberg.bg.tertiary, color: bloomberg.accent.orange, verticalAlign: 'bottom' }}>Symbol</TableCell>
                      <TableCell rowSpan={2} sx={{ fontWeight: 700, fontSize: '0.7rem', bgcolor: bloomberg.bg.tertiary, color: bloomberg.accent.orange, verticalAlign: 'bottom' }}>Company</TableCell>
                      <TableCell rowSpan={2} align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', bgcolor: bloomberg.bg.tertiary, color: bloomberg.accent.orange, verticalAlign: 'bottom' }}>
                        <TableSortLabel active={perfSortCol === 'weight'} direction={perfSortCol === 'weight' ? perfSortDir : 'desc'} onClick={() => handlePerfSort('weight')}>Wt%</TableSortLabel>
                      </TableCell>
                      <TableCell align="center" colSpan={2} sx={{ ...GROUP_HEADER_SX }}>Size</TableCell>
                      <TableCell align="center" colSpan={6} sx={{ ...GROUP_HEADER_SX }}>Valuation</TableCell>
                      <TableCell align="center" colSpan={2} sx={{ ...GROUP_HEADER_SX }}>Quality</TableCell>
                      <TableCell align="center" colSpan={2} sx={{ ...GROUP_HEADER_SX }}>Leverage</TableCell>
                      <TableCell align="center" colSpan={2} sx={{ ...GROUP_HEADER_SX }}>Analyst</TableCell>
                    </TableRow>
                    <TableRow>
                      {renderHeaderCell('market_cap', 'Mkt Cap')}
                      {renderHeaderCell('beta', 'Beta')}
                      {renderHeaderCell('pe_ratio', 'P/E')}
                      {renderHeaderCell('ev_ebitda', 'EV/EBITDA')}
                      {renderHeaderCell('price_to_book', 'P/B')}
                      {renderHeaderCell('price_to_sales', 'P/S')}
                      {renderHeaderCell('dividend_yield', 'Yield')}
                      {renderHeaderCell('year_1', '1Y Ret')}
                      {renderHeaderCell('roe', 'ROE')}
                      {renderHeaderCell('fcf_yield', 'FCF Y')}
                      {renderHeaderCell('debt_to_equity', 'D/E')}
                      {renderHeaderCell('net_debt_to_ebitda', 'NetD/EBITDA')}
                      {renderHeaderCell('analyst_rating', 'Rating')}
                      {renderHeaderCell('day_1', '1D')}
                    </TableRow>
                  </TableHead>
                  <TableBody key={`perf-sort-${perfSortCol}-${perfSortDir}-fund`}>
                    {sortedHoldings.map(h => {
                      const perf = analysis.performance.individual[h.symbol] || {};
                      const rating = (perf as any).analyst_rating as number | null | undefined;
                      const ratingColor = rating == null ? '#718096' : rating >= 4.5 ? '#4caf50' : rating >= 3.5 ? '#8bc34a' : rating >= 2.5 ? '#ff9800' : '#f44336';
                      const beta = (perf as any).beta as number | null | undefined;
                      return (
                        <TableRow key={h.symbol} hover>
                          <TableCell><TickerLink symbol={h.symbol} fontSize="0.78rem" fontWeight={700} /></TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>{h.name}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{(h.target_weight * 100).toFixed(1)}%</TableCell>
                          <TableCell align="right"><Typography sx={{ fontSize: '0.75rem' }}>{fmtMktCap((perf as any).market_cap)}</Typography></TableCell>
                          <TableCell align="right"><Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: beta != null ? (beta > 1.5 ? '#f44336' : beta < 0.8 ? '#60a5fa' : 'text.primary') : '#718096' }}>{beta != null ? beta.toFixed(2) : '-'}</Typography></TableCell>
                          <TableCell align="right">{fmtNum((perf as any).pe_ratio, 1)}</TableCell>
                          <TableCell align="right">{fmtNum((perf as any).ev_ebitda, 1)}</TableCell>
                          <TableCell align="right">{fmtNum((perf as any).price_to_book, 2)}</TableCell>
                          <TableCell align="right">{fmtNum((perf as any).price_to_sales, 2)}</TableCell>
                          <TableCell align="right">{(perf as any).dividend_yield != null ? <Typography sx={{ fontSize: '0.75rem' }}>{((perf as any).dividend_yield as number).toFixed(2)}%</Typography> : <Typography sx={{ fontSize: '0.75rem', color: '#718096' }}>-</Typography>}</TableCell>
                          <TableCell align="right">{fmtPct((perf as any).year_1)}</TableCell>
                          <TableCell align="right">{(() => {
                            const v = (perf as any).roe as number | null;
                            if (v == null) return <Typography sx={{ fontSize: '0.75rem', color: '#718096' }}>-</Typography>;
                            const color = v >= 20 ? '#22c55e' : v < 0 ? '#ef4444' : 'text.primary';
                            return <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color }}>{v.toFixed(1)}%</Typography>;
                          })()}</TableCell>
                          <TableCell align="right">{(() => {
                            const v = (perf as any).fcf_yield as number | null;
                            if (v == null) return <Typography sx={{ fontSize: '0.75rem', color: '#718096' }}>-</Typography>;
                            const color = v >= 5 ? '#22c55e' : v < 0 ? '#ef4444' : 'text.primary';
                            return <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color }}>{v.toFixed(1)}%</Typography>;
                          })()}</TableCell>
                          <TableCell align="right">{(() => {
                            const v = (perf as any).debt_to_equity as number | null;
                            if (v == null) return <Typography sx={{ fontSize: '0.75rem', color: '#718096' }}>-</Typography>;
                            const color = v > 1.5 ? '#ef4444' : v < 0.5 ? '#22c55e' : 'text.primary';
                            return <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color }}>{v.toFixed(2)}</Typography>;
                          })()}</TableCell>
                          <TableCell align="right">{(() => {
                            const v = (perf as any).net_debt_to_ebitda as number | null;
                            if (v == null) return <Typography sx={{ fontSize: '0.75rem', color: '#718096' }}>-</Typography>;
                            const color = v > 4 ? '#ef4444' : v < 0 ? '#60a5fa' : 'text.primary';
                            return <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color }}>{v.toFixed(2)}</Typography>;
                          })()}</TableCell>
                          <TableCell align="right"><Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: ratingColor }}>{rating != null ? rating.toFixed(1) : '-'}</Typography></TableCell>
                          <TableCell align="right">{fmtPct((perf as any).day_1)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                  </>)}

                </Table>
              </TableContainer>

              {/* Weight in Positive / Negative Territory */}
              {perfView === 'performance' && (() => {
                const indiv = analysis.performance.individual as Record<string, Record<string, number | null>>;
                const holdings = portfolio?.holdings ?? [];
                const periods = [
                  { key: 'day_1',   label: '1D' },
                  { key: 'week_1',  label: '1W' },
                  { key: 'month_1', label: '1M' },
                  { key: 'month_3', label: '3M' },
                  { key: 'ytd',     label: 'YTD' },
                  { key: 'year_1',  label: '1Y' },
                  { key: 'year_2',  label: '2Y' },
                  { key: 'year_5',  label: '5Y' },
                ];
                return (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                      Portfolio Weight in Territory (by Period)
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {periods.map(({ key, label }) => {
                        let posW = 0, negW = 0, totalW = 0;
                        holdings.forEach(h => {
                          const val = indiv[h.symbol]?.[key];
                          if (val == null) return;
                          totalW += h.target_weight;
                          if (val > 0) posW += h.target_weight;
                          else if (val < 0) negW += h.target_weight;
                        });
                        if (totalW === 0) return null;
                        const posPct = Math.round((posW / totalW) * 100);
                        const negPct = Math.round((negW / totalW) * 100);
                        const neutralPct = 100 - posPct - negPct;
                        return (
                          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontSize: '0.72rem', color: bloomberg.text.muted, width: 28, textAlign: 'right', flexShrink: 0 }}>
                              {label}
                            </Typography>
                            <Box sx={{ flex: 1, display: 'flex', height: 18, borderRadius: 0.5, overflow: 'hidden' }}>
                              <Box sx={{
                                width: `${negPct}%`, height: '100%',
                                bgcolor: '#ef4444',
                                transition: 'width 0.3s ease',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                {negPct >= 12 && (
                                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                                    {negPct}%
                                  </Typography>
                                )}
                              </Box>
                              {neutralPct > 0 && (
                                <Box sx={{ width: `${neutralPct}%`, height: '100%', bgcolor: bloomberg.bg.tertiary }} />
                              )}
                              <Box sx={{
                                width: `${posPct}%`, height: '100%',
                                bgcolor: '#22c55e',
                                transition: 'width 0.3s ease',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                {posPct >= 12 && (
                                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                                    {posPct}%
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <Typography sx={{ fontSize: '0.72rem', color: bloomberg.text.muted, width: 80, flexShrink: 0, whiteSpace: 'nowrap' }}>
                              <span style={{ color: '#22c55e', fontWeight: 700 }}>{posPct}%</span>
                              {' / '}
                              <span style={{ color: '#ef4444', fontWeight: 700 }}>{negPct}%</span>
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                      {[{ color: '#22c55e', label: 'Positive' }, { color: bloomberg.bg.tertiary, label: 'Flat/no data', border: bloomberg.border.primary }, { color: '#ef4444', label: 'Negative' }].map(({ color, label, border }) => (
                        <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: color, border: border ? `1px solid ${border}` : undefined }} />
                          <Typography sx={{ fontSize: '0.68rem', color: bloomberg.text.muted }}>{label}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })()}

              {/* Technicals: weighted breakdown + KPIs */}
              {perfView === 'technicals' && (() => {
                const indiv = analysis.performance.individual as Record<string, Record<string, number | null>>;
                const holdings = portfolio?.holdings ?? [];

                const maBuckets = [
                  { key: 'pct_from_sma20',  label: '20 MA' },
                  { key: 'pct_from_sma50',  label: '50 MA' },
                  { key: 'pct_from_sma200', label: '200 MA' },
                ];

                const weightedAvg = (key: string) => {
                  let num = 0, den = 0;
                  holdings.forEach(h => {
                    const v = indiv[h.symbol]?.[key];
                    if (v == null) return;
                    num += v * h.target_weight;
                    den += h.target_weight;
                  });
                  return den > 0 ? num / den : null;
                };

                const rsiAvg     = weightedAvg('rsi');
                const ma20Avg    = weightedAvg('pct_from_sma20');
                const ma50Avg    = weightedAvg('pct_from_sma50');
                const ma200Avg   = weightedAvg('pct_from_sma200');
                const hiAvg      = weightedAvg('pct_from_52w_high');
                const loAvg      = weightedAvg('pct_from_52w_low');

                const rsiColor = (v: number | null) =>
                  v == null ? bloomberg.text.muted :
                  v >= 70 ? '#ef4444' :
                  v <= 30 ? '#22c55e' :
                  bloomberg.text.primary;
                const pctColor = (v: number | null) =>
                  v == null ? bloomberg.text.muted :
                  v >= 0 ? '#22c55e' : '#ef4444';
                const fmtPctVal = (v: number | null) =>
                  v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
                const fmtNumVal = (v: number | null) =>
                  v == null ? '—' : v.toFixed(1);

                return (
                  <Box sx={{ mt: 3 }}>
                    {/* Above / Below MA stacked bars */}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                      Portfolio Weight vs Moving Averages
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {maBuckets.map(({ key, label }) => {
                        let aboveW = 0, belowW = 0, totalW = 0;
                        holdings.forEach(h => {
                          const val = indiv[h.symbol]?.[key];
                          if (val == null) return;
                          totalW += h.target_weight;
                          if (val > 0) aboveW += h.target_weight;
                          else if (val < 0) belowW += h.target_weight;
                        });
                        if (totalW === 0) return null;
                        const abovePct = Math.round((aboveW / totalW) * 100);
                        const belowPct = Math.round((belowW / totalW) * 100);
                        const neutralPct = 100 - abovePct - belowPct;
                        return (
                          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontSize: '0.72rem', color: bloomberg.text.muted, width: 48, textAlign: 'right', flexShrink: 0 }}>
                              {label}
                            </Typography>
                            <Box sx={{ flex: 1, display: 'flex', height: 18, borderRadius: 0.5, overflow: 'hidden' }}>
                              <Box sx={{
                                width: `${belowPct}%`, height: '100%', bgcolor: '#ef4444',
                                transition: 'width 0.3s ease',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                {belowPct >= 12 && (
                                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                                    {belowPct}%
                                  </Typography>
                                )}
                              </Box>
                              {neutralPct > 0 && (
                                <Box sx={{ width: `${neutralPct}%`, height: '100%', bgcolor: bloomberg.bg.tertiary }} />
                              )}
                              <Box sx={{
                                width: `${abovePct}%`, height: '100%', bgcolor: '#22c55e',
                                transition: 'width 0.3s ease',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                {abovePct >= 12 && (
                                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                                    {abovePct}%
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <Typography sx={{ fontSize: '0.72rem', color: bloomberg.text.muted, width: 100, flexShrink: 0, whiteSpace: 'nowrap' }}>
                              <span style={{ color: '#22c55e', fontWeight: 700 }}>{abovePct}% above</span>
                              {' / '}
                              <span style={{ color: '#ef4444', fontWeight: 700 }}>{belowPct}% below</span>
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 3 }}>
                      {[{ color: '#22c55e', label: 'Above MA' }, { color: bloomberg.bg.tertiary, label: 'At MA / no data', border: bloomberg.border.primary }, { color: '#ef4444', label: 'Below MA' }].map(({ color, label, border }) => (
                        <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: color, border: border ? `1px solid ${border}` : undefined }} />
                          <Typography sx={{ fontSize: '0.68rem', color: bloomberg.text.muted }}>{label}</Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* Weighted technical KPIs */}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Weighted Technical Averages
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                      {[
                        { label: 'RSI',          value: fmtNumVal(rsiAvg),     color: rsiColor(rsiAvg),
                          sub: rsiAvg == null ? '' : rsiAvg >= 70 ? 'Overbought' : rsiAvg <= 30 ? 'Oversold' : 'Neutral' },
                        { label: 'vs 20 MA',     value: fmtPctVal(ma20Avg),    color: pctColor(ma20Avg),  sub: '' },
                        { label: 'vs 50 MA',     value: fmtPctVal(ma50Avg),    color: pctColor(ma50Avg),  sub: '' },
                        { label: 'vs 200 MA',    value: fmtPctVal(ma200Avg),   color: pctColor(ma200Avg), sub: '' },
                        { label: 'vs 52W High',  value: hiAvg == null ? '—' : `${hiAvg.toFixed(1)}%`,
                          color: bloomberg.text.primary, sub: '' },
                        { label: 'vs 52W Low',   value: loAvg == null ? '—' : `+${loAvg.toFixed(1)}%`,
                          color: '#ff9800', sub: '' },
                      ].map(({ label, value, color, sub }) => (
                        <Paper key={label} elevation={0} sx={{ p: 1.25, textAlign: 'center', minWidth: 92 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem' }}>
                            {label}
                          </Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color, lineHeight: 1.2 }}>
                            {value}
                          </Typography>
                          {sub && (
                            <Typography sx={{ fontSize: '0.6rem', color: bloomberg.text.muted, mt: 0.25 }}>
                              {sub}
                            </Typography>
                          )}
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                );
              })()}

              {/* Fundamentals: weighted breakdown + KPIs */}
              {perfView === 'fundamentals' && (() => {
                const indiv = analysis.performance.individual as Record<string, Record<string, number | null>>;
                const holdings = portfolio?.holdings ?? [];

                const weightedAvg = (key: string) => {
                  let num = 0, den = 0;
                  holdings.forEach(h => {
                    const v = indiv[h.symbol]?.[key];
                    if (v == null) return;
                    num += v * h.target_weight;
                    den += h.target_weight;
                  });
                  return den > 0 ? num / den : null;
                };

                // Market Cap buckets
                const mcapBuckets = [
                  { label: '< $10B',     color: '#60a5fa', test: (v: number) => v < 10e9 },
                  { label: '$10–50B',    color: '#3b82f6', test: (v: number) => v >= 10e9 && v < 50e9 },
                  { label: '$50–100B',   color: '#1d4ed8', test: (v: number) => v >= 50e9 && v < 100e9 },
                  { label: '> $100B',    color: '#7c3aed', test: (v: number) => v >= 100e9 },
                ];
                const mcapWeights = mcapBuckets.map(b => 0);
                let mcapTotalW = 0;
                holdings.forEach(h => {
                  const v = indiv[h.symbol]?.market_cap;
                  if (v == null) return;
                  mcapTotalW += h.target_weight;
                  mcapBuckets.forEach((b, i) => {
                    if (b.test(v)) mcapWeights[i] += h.target_weight;
                  });
                });
                const mcapPcts = mcapWeights.map(w => mcapTotalW > 0 ? (w / mcapTotalW) * 100 : 0);

                // Rating buckets
                const ratingBuckets = [
                  { label: '< 3.5',     color: '#ef4444', test: (v: number) => v < 3.5 },
                  { label: '3.5 – 4',   color: '#f59e0b', test: (v: number) => v >= 3.5 && v < 4 },
                  { label: '> 4',       color: '#22c55e', test: (v: number) => v >= 4 },
                ];
                const ratingWeights = ratingBuckets.map(b => 0);
                let ratingTotalW = 0;
                holdings.forEach(h => {
                  const v = indiv[h.symbol]?.analyst_rating;
                  if (v == null) return;
                  ratingTotalW += h.target_weight;
                  ratingBuckets.forEach((b, i) => {
                    if (b.test(v)) ratingWeights[i] += h.target_weight;
                  });
                });
                const ratingPcts = ratingWeights.map(w => ratingTotalW > 0 ? (w / ratingTotalW) * 100 : 0);

                const betaAvg     = weightedAvg('beta');
                const ratingAvg   = weightedAvg('analyst_rating');
                const peAvg       = weightedAvg('pe_ratio');
                const yieldAvg    = weightedAvg('dividend_yield');
                const evEbitdaAvg = weightedAvg('ev_ebitda');
                const pbAvg       = weightedAvg('price_to_book');
                const psAvg       = weightedAvg('price_to_sales');
                const roeAvg      = weightedAvg('roe');
                const fcfYieldAvg = weightedAvg('fcf_yield');
                const deAvg       = weightedAvg('debt_to_equity');
                const ndeAvg      = weightedAvg('net_debt_to_ebitda');

                const betaColor =
                  betaAvg == null ? bloomberg.text.muted :
                  betaAvg > 1.5 ? '#ef4444' :
                  betaAvg < 0.8 ? '#60a5fa' :
                  bloomberg.text.primary;
                const ratingColor =
                  ratingAvg == null ? bloomberg.text.muted :
                  ratingAvg >= 4.5 ? '#4caf50' :
                  ratingAvg >= 3.5 ? '#8bc34a' :
                  ratingAvg >= 2.5 ? '#ff9800' :
                  '#f44336';
                const contrarianLabel =
                  ratingAvg == null ? '' :
                  ratingAvg >= 4 ? 'Consensus Buy' :
                  ratingAvg < 3.5 ? 'Contrarian' :
                  'Mixed view';

                const renderStackedBar = (
                  buckets: { label: string; color: string }[],
                  pcts: number[],
                ) => (
                  <Box sx={{ display: 'flex', height: 22, borderRadius: 0.5, overflow: 'hidden', mb: 1 }}>
                    {buckets.map((b, i) => pcts[i] > 0 && (
                      <Box key={b.label} sx={{
                        width: `${pcts[i]}%`, height: '100%', bgcolor: b.color,
                        transition: 'width 0.3s ease',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {pcts[i] >= 10 && (
                          <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                            {Math.round(pcts[i])}%
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                );

                const renderLegend = (buckets: { label: string; color: string }[], pcts: number[]) => (
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {buckets.map((b, i) => (
                      <Box key={b.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: b.color }} />
                        <Typography sx={{ fontSize: '0.68rem', color: bloomberg.text.muted }}>
                          {b.label} <span style={{ color: bloomberg.text.primary, fontWeight: 700 }}>{Math.round(pcts[i])}%</span>
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                );

                return (
                  <Box sx={{ mt: 3 }}>
                    {/* Market Cap distribution */}
                    {mcapTotalW > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          Market Cap Distribution (weighted)
                        </Typography>
                        {renderStackedBar(mcapBuckets, mcapPcts)}
                        {renderLegend(mcapBuckets, mcapPcts)}
                      </Box>
                    )}

                    {/* Analyst Rating distribution */}
                    {ratingTotalW > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          Analyst Rating Distribution (weighted)
                        </Typography>
                        {renderStackedBar(ratingBuckets, ratingPcts)}
                        {renderLegend(ratingBuckets, ratingPcts)}
                      </Box>
                    )}

                    {/* Weighted fundamental KPIs */}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Weighted Fundamental Averages
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                      {[
                        { label: 'Beta',         value: betaAvg   == null ? '—' : betaAvg.toFixed(2),
                          color: betaColor,
                          sub: betaAvg == null ? '' : betaAvg > 1.5 ? 'High-beta' : betaAvg < 0.8 ? 'Defensive' : 'Market-like' },
                        { label: 'Analyst Rating', value: ratingAvg == null ? '—' : ratingAvg.toFixed(2),
                          color: ratingColor, sub: contrarianLabel },
                        { label: 'P/E',          value: peAvg     == null ? '—' : peAvg.toFixed(1),
                          color: bloomberg.text.primary, sub: '' },
                        { label: 'EV/EBITDA',    value: evEbitdaAvg == null ? '—' : evEbitdaAvg.toFixed(1),
                          color: evEbitdaAvg == null ? bloomberg.text.muted : evEbitdaAvg > 20 ? '#ef4444' : evEbitdaAvg < 10 ? '#22c55e' : bloomberg.text.primary,
                          sub: evEbitdaAvg == null ? '' : evEbitdaAvg > 20 ? 'Premium' : evEbitdaAvg < 10 ? 'Cheap' : 'Fair' },
                        { label: 'P/B',          value: pbAvg     == null ? '—' : pbAvg.toFixed(2),
                          color: pbAvg == null ? bloomberg.text.muted : pbAvg > 5 ? '#ef4444' : pbAvg < 1.5 ? '#22c55e' : bloomberg.text.primary,
                          sub: '' },
                        { label: 'P/S',          value: psAvg     == null ? '—' : psAvg.toFixed(2),
                          color: psAvg == null ? bloomberg.text.muted : psAvg > 8 ? '#ef4444' : psAvg < 2 ? '#22c55e' : bloomberg.text.primary,
                          sub: '' },
                        { label: 'ROE',          value: roeAvg    == null ? '—' : `${roeAvg.toFixed(1)}%`,
                          color: roeAvg == null ? bloomberg.text.muted : roeAvg >= 20 ? '#22c55e' : roeAvg < 0 ? '#ef4444' : bloomberg.text.primary,
                          sub: roeAvg == null ? '' : roeAvg >= 20 ? 'High quality' : roeAvg < 0 ? 'Losing money' : 'Mid quality' },
                        { label: 'FCF Yield',    value: fcfYieldAvg == null ? '—' : `${fcfYieldAvg.toFixed(1)}%`,
                          color: fcfYieldAvg == null ? bloomberg.text.muted : fcfYieldAvg >= 5 ? '#22c55e' : fcfYieldAvg < 0 ? '#ef4444' : bloomberg.text.primary,
                          sub: fcfYieldAvg == null ? '' : fcfYieldAvg >= 5 ? 'Cash cow' : fcfYieldAvg < 0 ? 'Cash-burning' : '' },
                        { label: 'Debt / Equity', value: deAvg    == null ? '—' : deAvg.toFixed(2),
                          color: deAvg == null ? bloomberg.text.muted : deAvg > 1.5 ? '#ef4444' : deAvg < 0.5 ? '#22c55e' : bloomberg.text.primary,
                          sub: deAvg == null ? '' : deAvg > 1.5 ? 'High leverage' : deAvg < 0.5 ? 'Low leverage' : '' },
                        { label: 'Net Debt / EBITDA', value: ndeAvg == null ? '—' : ndeAvg.toFixed(2),
                          color: ndeAvg == null ? bloomberg.text.muted : ndeAvg > 4 ? '#ef4444' : ndeAvg < 0 ? '#60a5fa' : bloomberg.text.primary,
                          sub: ndeAvg == null ? '' : ndeAvg > 4 ? 'Stressed' : ndeAvg < 0 ? 'Net cash' : '' },
                        { label: 'Div Yield',    value: yieldAvg  == null ? '—' : `${yieldAvg.toFixed(2)}%`,
                          color: '#22c55e', sub: '' },
                      ].map(({ label, value, color, sub }) => (
                        <Paper key={label} elevation={0} sx={{ p: 1.25, textAlign: 'center', minWidth: 100 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem' }}>
                            {label}
                          </Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color, lineHeight: 1.2 }}>
                            {value}
                          </Typography>
                          {sub && (
                            <Typography sx={{ fontSize: '0.6rem', color: bloomberg.text.muted, mt: 0.25 }}>
                              {sub}
                            </Typography>
                          )}
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                );
              })()}
            </Box>
            );
          })()}

          {/* ── Tab 6: Recommendations ── */}
          {analysisTab === 6 && portfolio && (
            <TradeRecommendationsPanel
              portfolioId={selectedId!}
              timeframe={timeframe}
              benchmarks={selectedBenchmarks}
              roles={holdingRoles}
              onApplied={() => selectedId && loadPortfolio(selectedId)}
            />
          )}

          {/* ── Tab 8: Portfolio Comparison ── */}
          {analysisTab === 8 && compareAnalysis && (
            <PortfolioComparePanel
              nameA={portfolio?.name || 'Portfolio A'}
              nameB={portfolios.find(p => p.id === compareId)?.name || 'Portfolio B'}
              analysisA={analysis}
              analysisB={compareAnalysis}
              perfColor={perfColor}
              fmtPerf={fmtPerf}
            />
          )}
        </Paper>
        </ThemeProvider>
      )}

      {/* ── Create / Edit Dialog ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{dialogMode === 'create' ? 'New Model Portfolio' : 'Edit Portfolio'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Portfolio name"
            value={dialogName}
            onChange={(e) => setDialogName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (dialogMode === 'create' ? handleCreate() : handleUpdate())}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description (optional)"
            value={dialogDesc}
            onChange={(e) => setDialogDesc(e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
            <Select
              value={dialogRegion}
              onChange={(e) => setDialogRegion(e.target.value as PortfolioRegion | '')}
              size="small"
              displayEmpty
              fullWidth
            >
              <MenuItem value=""><em style={{ color: bloomberg.text.muted, fontStyle: 'normal' }}>Region (optional)</em></MenuItem>
              {REGIONS.map(r => (
                <MenuItem key={r} value={r}>
                  {REGION_FLAGS[r]} {r}
                </MenuItem>
              ))}
            </Select>
            {dialogMode === 'edit' && selectedId && (
              <Button
                size="small"
                variant="outlined"
                disabled={detectingRegion}
                onClick={async () => {
                  setDetectingRegion(true);
                  try {
                    const detected = await detectPortfolioRegion(selectedId);
                    setDialogRegion(detected);
                  } catch { }
                  finally { setDetectingRegion(false); }
                }}
                sx={{ whiteSpace: 'nowrap', fontSize: 11, minWidth: 90 }}
              >
                {detectingRegion ? '...' : 'Auto-detect'}
              </Button>
            )}
          </Box>
          <TextField
            fullWidth
            label="Tags (comma-separated)"
            value={dialogTags}
            onChange={(e) => setDialogTags(e.target.value)}
            placeholder="e.g. damien, growth, 2026"
            helperText="Add tags for traceability and filtering"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={dialogMode === 'create' ? handleCreate : handleUpdate}
            disabled={!dialogName.trim()}
          >
            {dialogMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation ── */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs">
        <DialogTitle>Delete Model Portfolio</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{portfolio?.name}"? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={alertSnackbar.open}
        autoHideDuration={3000}
        onClose={() => setAlertSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={alertSnackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {alertSnackbar.message}
        </Alert>
      </Snackbar>

      {/* Empty state */}
      {portfolios.length === 0 && !loading && (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            No model portfolios yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create a model portfolio to start building and analyzing investment strategies.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setDialogMode('create');
              setDialogName('');
              setDialogDesc('');
              setDialogTags('');
              setDialogOpen(true);
            }}
          >
            Create Model Portfolio
          </Button>
        </Paper>
      )}
      </Box>}{/* end mainView === 0 */}

      {/* ── Signals View ── */}
      {mainView === 2 && (() => {
        const pfPortfolioNames = Array.from(
          new Set(Object.values(pfSignals).flatMap(s => s.portfolios.map((p: any) => p.name)))
        ).sort();
        const matchesPfFilters = (sig: any, symbol: string, name: string, portfolios: any[]) => {
          if (pfSignalTypes.size > 0 && !pfSignalTypes.has(sig.signal_type)) return false;
          if (pfDirectionFilter !== 'all' && sig.direction !== pfDirectionFilter) return false;
          if (pfPortfolioFilter && !portfolios.some((p: any) => p.name === pfPortfolioFilter)) return false;
          if (pfSearch) {
            const q = pfSearch.toLowerCase();
            if (!symbol.toLowerCase().includes(q) && !(name || '').toLowerCase().includes(q)) return false;
          }
          return true;
        };
        const filteredHoldings = Object.entries(pfSignals).filter(([symbol, symData]) =>
          symData.signals.some((s: any) => matchesPfFilters(s, symbol, symData.name, symData.portfolios))
        );
        const filteredSignalCount = filteredHoldings.reduce(
          (n, [symbol, symData]) => n + symData.signals.filter((s: any) => matchesPfFilters(s, symbol, symData.name, symData.portfolios)).length,
          0
        );
        const toggleSignalType = (type: string) => {
          setPfSignalTypes(prev => {
            const next = new Set(prev);
            if (next.has(type)) next.delete(type); else next.add(type);
            return next;
          });
        };
        const handlePfSort = (col: string) => {
          if (pfSortCol === col) {
            setPfSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
          } else {
            setPfSortCol(col);
            setPfSortDir('desc');
          }
        };
        // Column-key → value extractor shared by both views' sort comparators.
        // 'symbol' | 'company' | 'portfolio' only apply to the By Event view
        // (flattened item), everything else reads off the signal itself.
        const pfSortValue = (item: any, sig: any, col: string): number | string => {
          switch (col) {
            case 'symbol': return item?.symbol || '';
            case 'company': return item?.name || '';
            case 'portfolio': return item?.portfolios?.[0]?.name || '';
            case 'date': return sig.signal_date || '';
            case 'signal': return SIGNAL_LABELS[sig.signal_type] || sig.signal_type || '';
            case 'direction': return sig.direction || '';
            case 'price': return sig.price ?? -Infinity;
            case 'chg': return sig.change_pct ?? -Infinity;
            case 'rsi': return sig.rsi ?? -Infinity;
            case 'ma20': return sig.sma20_dist ?? -Infinity;
            case 'ma50': return sig.sma50_dist ?? -Infinity;
            case 'ma200': return sig.sma200_dist ?? -Infinity;
            default: return '';
          }
        };
        const pfCompare = (itemA: any, sigA: any, itemB: any, sigB: any): number => {
          if (!pfSortCol) return 0;
          const va = pfSortValue(itemA, sigA, pfSortCol);
          const vb = pfSortValue(itemB, sigB, pfSortCol);
          const cmp = typeof va === 'string' || typeof vb === 'string'
            ? String(va).localeCompare(String(vb))
            : (va as number) - (vb as number);
          return pfSortDir === 'asc' ? cmp : -cmp;
        };
        // Clickable, sortable column header — mirrors the existing
        // TableSortLabel convention used elsewhere in this file, adapted to
        // these tables' plain <Box component="th"> markup.
        const SortableTh: React.FC<{ label: string; col: string; align?: 'left' | 'right' }> = ({ label, col, align = 'right' }) => (
          <Box
            component="th"
            onClick={() => handlePfSort(col)}
            sx={{
              py: 0.5, px: 1.5, fontSize: '0.65rem', fontWeight: 700,
              color: pfSortCol === col ? bloomberg.accent.orange : bloomberg.text.muted,
              textAlign: align, textTransform: 'uppercase', letterSpacing: '0.05em',
              whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none',
              '&:hover': { color: bloomberg.accent.orange },
            }}
          >
            {label}{pfSortCol === col ? (pfSortDir === 'asc' ? ' ▲' : ' ▼') : ''}
          </Box>
        );
        return (
        <Box>
          {/* Toolbar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              value={pfView} exclusive size="small"
              onChange={(_, v) => { if (v) setPfView(v); }}
              sx={{ '& .MuiToggleButton-root': { py: 0.4, px: 1.5, fontSize: 11, fontWeight: 600, textTransform: 'none', color: bloomberg.text.muted, borderColor: bloomberg.border.primary, '&.Mui-selected': { color: bloomberg.accent.orange, bgcolor: bloomberg.accent.orangeDim } } }}
            >
              <ToggleButton value="stock">By Stock</ToggleButton>
              <ToggleButton value="event">By Event</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              select size="small" label="Window"
              value={pfDays}
              onChange={e => setPfDays(Number(e.target.value))}
              sx={{ minWidth: 110, '& .MuiInputBase-root': { fontSize: 12 } }}
            >
              {[1, 7, 14, 30].map(d => <MenuItem key={d} value={d}>{d} {d === 1 ? 'day' : 'days'}</MenuItem>)}
            </TextField>
            <Box
              component="button"
              onClick={fetchPortfolioSignals}
              disabled={pfLoading}
              sx={{ px: 2, py: 0.6, border: `1px solid ${bloomberg.accent.orange}`, borderRadius: 1, bgcolor: 'transparent', color: bloomberg.accent.orange, fontSize: 12, fontWeight: 600, cursor: 'pointer', '&:hover': { bgcolor: bloomberg.accent.orangeDim }, '&:disabled': { opacity: 0.5 } }}
            >
              {pfLoading ? 'Loading…' : 'Refresh'}
            </Box>
            <Box sx={{ flex: 1 }} />
            {pfCount > 0 && (
              <Chip size="small" label={`${filteredSignalCount} of ${pfCount} signals · ${filteredHoldings.length} of ${Object.keys(pfSignals).length} holdings`}
                sx={{ fontSize: 11, fontWeight: 600, bgcolor: bloomberg.accent.orangeDim, color: bloomberg.accent.orange }} />
            )}
            <Box
              component="button"
              onClick={async () => { setPfPdfLoading(true); try { await downloadPortfolioSignalsPdf(pfDays, pfView); } finally { setPfPdfLoading(false); } }}
              disabled={pfPdfLoading || pfCount === 0}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 2, py: 0.6, border: `1px solid ${bloomberg.text.muted}`, borderRadius: 1, bgcolor: 'transparent', color: bloomberg.text.muted, fontSize: 12, fontWeight: 600, cursor: 'pointer', '&:hover': { color: bloomberg.accent.orange, borderColor: bloomberg.accent.orange }, '&:disabled': { opacity: 0.4, cursor: 'default' } }}
            >
              <PictureAsPdfIcon sx={{ fontSize: 15 }} />
              {pfPdfLoading ? 'Generating…' : 'Download PDF'}
            </Box>
          </Box>

          {/* Signal-type filter buttons */}
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
            <Chip
              label="All Types" size="small" onClick={() => setPfSignalTypes(new Set())}
              sx={{
                height: 24, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                bgcolor: pfSignalTypes.size === 0 ? bloomberg.accent.orange : 'transparent',
                color: pfSignalTypes.size === 0 ? '#000' : bloomberg.text.secondary,
                border: `1px solid ${pfSignalTypes.size === 0 ? bloomberg.accent.orange : bloomberg.border.primary}`,
                '&:hover': { bgcolor: pfSignalTypes.size === 0 ? bloomberg.accent.orange : bloomberg.bg.hover },
              }}
            />
            {Object.entries(SIGNAL_LABELS).map(([type, label]) => {
              const active = pfSignalTypes.size === 0 || pfSignalTypes.has(type);
              return (
                <Chip
                  key={type} label={label} size="small" onClick={() => toggleSignalType(type)}
                  sx={{
                    height: 24, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    bgcolor: pfSignalTypes.has(type) ? bloomberg.accent.orange : 'transparent',
                    color: pfSignalTypes.has(type) ? '#000' : (active ? bloomberg.text.secondary : bloomberg.text.muted),
                    opacity: active ? 1 : 0.5,
                    border: `1px solid ${pfSignalTypes.has(type) ? bloomberg.accent.orange : bloomberg.border.primary}`,
                    '&:hover': { bgcolor: pfSignalTypes.has(type) ? bloomberg.accent.orange : bloomberg.bg.hover },
                  }}
                />
              );
            })}
          </Box>

          {/* Column filters: direction, portfolio, symbol/company search */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              value={pfDirectionFilter} exclusive size="small"
              onChange={(_, v) => { if (v) setPfDirectionFilter(v); }}
              sx={{ '& .MuiToggleButton-root': { py: 0.3, px: 1.2, fontSize: 11, fontWeight: 600, textTransform: 'none', color: bloomberg.text.muted, borderColor: bloomberg.border.primary, '&.Mui-selected': { color: bloomberg.accent.orange, bgcolor: bloomberg.accent.orangeDim } } }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="bullish">Bullish</ToggleButton>
              <ToggleButton value="bearish">Bearish</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              select size="small" label="Portfolio"
              value={pfPortfolioFilter}
              onChange={e => setPfPortfolioFilter(e.target.value)}
              sx={{ minWidth: 160, '& .MuiInputBase-root': { fontSize: 12 } }}
            >
              <MenuItem value="">All Portfolios</MenuItem>
              {pfPortfolioNames.map(name => <MenuItem key={name} value={name}>{name}</MenuItem>)}
            </TextField>
            <TextField
              size="small" placeholder="Filter by symbol or company…"
              value={pfSearch}
              onChange={e => setPfSearch(e.target.value)}
              sx={{ minWidth: 220, '& .MuiInputBase-root': { fontSize: 12 } }}
            />
            {(pfSignalTypes.size > 0 || pfDirectionFilter !== 'all' || pfPortfolioFilter || pfSearch) && (
              <Box
                component="button"
                onClick={() => { setPfSignalTypes(new Set()); setPfDirectionFilter('all'); setPfPortfolioFilter(''); setPfSearch(''); }}
                sx={{ px: 1.2, py: 0.4, border: `1px solid ${bloomberg.border.primary}`, borderRadius: 1, bgcolor: 'transparent', color: bloomberg.text.muted, fontSize: 11, fontWeight: 600, cursor: 'pointer', '&:hover': { color: bloomberg.accent.orange, borderColor: bloomberg.accent.orange } }}
              >
                Clear filters
              </Box>
            )}
          </Box>

          {pfLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {[1,2,3].map(i => <Skeleton key={i} variant="rounded" height={80} sx={{ bgcolor: bloomberg.bg.tertiary }} />)}
            </Box>
          ) : pfCount === 0 ? (
            <Paper elevation={1} sx={{ p: 4, textAlign: 'center', bgcolor: bloomberg.bg.tertiary }}>
              <Typography variant="body2" color="text.secondary">
                No signals for portfolio holdings in the last {pfDays} days.
              </Typography>
            </Paper>
          ) : filteredHoldings.length === 0 ? (
            <Paper elevation={1} sx={{ p: 4, textAlign: 'center', bgcolor: bloomberg.bg.tertiary }}>
              <Typography variant="body2" color="text.secondary">
                No signals match the current filters.
              </Typography>
            </Paper>
          ) : pfView === 'stock' ? (
            /* ── By Stock view ── */
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {Object.entries(pfSignals)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([symbol, symData]) => {
                  const filteredSignals = symData.signals.filter((s: any) => matchesPfFilters(s, symbol, symData.name, symData.portfolios));
                  if (filteredSignals.length === 0) return null;
                  const bullish = filteredSignals.filter((s: any) => s.direction === 'bullish').length;
                  const bearish = filteredSignals.filter((s: any) => s.direction === 'bearish').length;
                  return (
                    <Paper key={symbol} elevation={1} sx={{ bgcolor: bloomberg.bg.tertiary, border: `1px solid ${bloomberg.border.primary}`, overflow: 'hidden' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, borderBottom: `1px solid ${bloomberg.border.primary}`, flexWrap: 'wrap' }}>
                        <TickerLink symbol={symbol} fontWeight={700} fontSize={12} />
                        <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: bloomberg.text.primary }}>{symData.name}</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {symData.portfolios.map((pf: any, i: number) => (
                            <Chip key={i} size="small" label={`${pf.name} ${(pf.weight * 100).toFixed(1)}%`}
                              sx={{ fontSize: '0.65rem', height: 18, bgcolor: bloomberg.bg.secondary, color: bloomberg.text.secondary }} />
                          ))}
                        </Box>
                        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                          {bullish > 0 && <Chip size="small" label={`${bullish} bullish`} sx={{ fontSize: '0.65rem', height: 18, bgcolor: '#16a34a22', color: '#4caf50' }} />}
                          {bearish > 0 && <Chip size="small" label={`${bearish} bearish`} sx={{ fontSize: '0.65rem', height: 18, bgcolor: '#dc262622', color: '#ef4444' }} />}
                        </Box>
                      </Box>
                      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                        <Box component="thead">
                          <Box component="tr" sx={{ bgcolor: bloomberg.bg.secondary }}>
                            <SortableTh label="Date" col="date" />
                            <SortableTh label="Signal" col="signal" />
                            <SortableTh label="Direction" col="direction" />
                            <SortableTh label="Price" col="price" />
                            <SortableTh label="Chg%" col="chg" />
                            <SortableTh label="RSI" col="rsi" />
                            <SortableTh label="20MA" col="ma20" />
                            <SortableTh label="50MA" col="ma50" />
                            <SortableTh label="200MA" col="ma200" />
                            <Box component="th" sx={{ py: 0.5, px: 1.5, fontSize: '0.65rem', fontWeight: 700, color: bloomberg.accent.orange, textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Detail</Box>
                          </Box>
                        </Box>
                        <Box component="tbody">
                          {[...filteredSignals]
                            .sort((a: any, b: any) => pfSortCol
                              ? pfCompare(null, a, null, b)
                              : (b.signal_date || '').localeCompare(a.signal_date || ''))
                            .map((sig: any, i: number) => {
                              const isBull = sig.direction === 'bullish';
                              const dirColor = isBull ? '#4caf50' : '#ef4444';
                              const chgColor = sig.change_pct == null ? bloomberg.text.muted : sig.change_pct > 0 ? '#4caf50' : sig.change_pct < 0 ? '#ef4444' : bloomberg.text.primary;
                              return (
                                <Box key={i} component="tr" sx={{ borderTop: `1px solid ${bloomberg.border.primary}`, '&:hover': { bgcolor: bloomberg.bg.secondary } }}>
                                  <Box component="td" sx={{ py: 0.7, px: 1.5, fontSize: '0.72rem', fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.text.muted, textAlign: 'right', whiteSpace: 'nowrap' }}>{sig.signal_date}</Box>
                                  <Box component="td" sx={{ py: 0.7, px: 1.5, fontSize: '0.72rem', fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.text.primary, textAlign: 'right', whiteSpace: 'nowrap' }}>{SIGNAL_LABELS[sig.signal_type] || sig.signal_type}</Box>
                                  <Box component="td" sx={{ py: 0.7, px: 1.5, fontSize: '0.72rem', fontWeight: 700, color: dirColor, textAlign: 'right', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.3 }}>
                                    {isBull ? <TrendingUpIcon sx={{ fontSize: 12 }} /> : <TrendingDownIcon sx={{ fontSize: 12 }} />}
                                    {sig.direction}
                                  </Box>
                                  <Box component="td" sx={{ py: 0.7, px: 1.5, fontSize: '0.72rem', fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.text.secondary, textAlign: 'right', whiteSpace: 'nowrap' }}>{sig.price != null ? sig.price.toFixed(2) : '—'}</Box>
                                  <Box component="td" sx={{ py: 0.7, px: 1.5, fontSize: '0.72rem', fontFamily: '"IBM Plex Mono", monospace', color: chgColor, textAlign: 'right', whiteSpace: 'nowrap' }}>{sig.change_pct != null ? `${sig.change_pct > 0 ? '+' : ''}${sig.change_pct.toFixed(1)}%` : '—'}</Box>
                                  <Box component="td" sx={{ py: 0.7, px: 1.5, fontSize: '0.72rem', fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.text.muted, textAlign: 'right', whiteSpace: 'nowrap' }}>{sig.rsi != null ? sig.rsi.toFixed(0) : '—'}</Box>
                                  {[sig.sma20_dist, sig.sma50_dist, sig.sma200_dist].map((dist, di) => (
                                    <Box key={di} component="td" sx={{ py: 0.7, px: 1.5, fontSize: '0.72rem', fontFamily: '"IBM Plex Mono", monospace', color: dist == null ? bloomberg.text.muted : dist > 0 ? '#4caf50' : dist < 0 ? '#ef4444' : bloomberg.text.primary, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                      {dist != null ? `${dist > 0 ? '+' : ''}${dist.toFixed(1)}%` : '—'}
                                    </Box>
                                  ))}
                                  <Box component="td" sx={{ py: 0.7, px: 1.5, fontSize: '0.72rem', color: bloomberg.text.secondary, textAlign: 'left' }}>{sig.detail}</Box>
                                </Box>
                              );
                            })}
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
            </Box>
          ) : (
            /* ── By Event view ── */
            (() => {
              const flat: { symbol: string; name: string; portfolios: { name: string; weight: number }[]; sig: PortfolioSignalSymbol['signals'][0] }[] = [];
              Object.entries(pfSignals).forEach(([symbol, symData]) => {
                symData.signals.forEach((sig: any) => {
                  if (matchesPfFilters(sig, symbol, symData.name, symData.portfolios)) {
                    flat.push({ symbol, name: symData.name, portfolios: symData.portfolios, sig });
                  }
                });
              });
              const categoryOrder = ['milestone', 'ma_cross', 'momentum'];
              const grouped: Record<string, Record<string, typeof flat>> = {};
              flat.forEach(item => {
                const cat = item.sig.category || 'other';
                const type = item.sig.signal_type;
                if (!grouped[cat]) grouped[cat] = {};
                if (!grouped[cat][type]) grouped[cat][type] = [];
                grouped[cat][type].push(item);
              });
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {categoryOrder.filter(cat => grouped[cat]).map(cat => (
                    <Box key={cat}>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: bloomberg.accent.orange, textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1 }}>
                        {CATEGORY_LABELS[cat] || cat}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {Object.entries(grouped[cat])
                          .sort(([a], [b]) => a.localeCompare(b))
                          .map(([sigType, items]) => {
                            const bullish = items.filter(i => i.sig.direction === 'bullish').length;
                            const bearish = items.filter(i => i.sig.direction === 'bearish').length;
                            return (
                              <Paper key={sigType} elevation={1} sx={{ bgcolor: bloomberg.bg.tertiary, border: `1px solid ${bloomberg.border.primary}`, overflow: 'hidden' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, borderBottom: `1px solid ${bloomberg.border.primary}` }}>
                                  <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: bloomberg.text.primary }}>{SIGNAL_LABELS[sigType] || sigType}</Typography>
                                  <Chip size="small" label={`${items.length} signal${items.length !== 1 ? 's' : ''}`}
                                    sx={{ fontSize: '0.65rem', height: 18, bgcolor: bloomberg.accent.orangeDim, color: bloomberg.accent.orange }} />
                                  <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                                    {bullish > 0 && <Chip size="small" label={`${bullish} bullish`} sx={{ fontSize: '0.65rem', height: 18, bgcolor: '#16a34a22', color: '#4caf50' }} />}
                                    {bearish > 0 && <Chip size="small" label={`${bearish} bearish`} sx={{ fontSize: '0.65rem', height: 18, bgcolor: '#dc262622', color: '#ef4444' }} />}
                                  </Box>
                                </Box>
                                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <Box component="thead">
                                    <Box component="tr" sx={{ bgcolor: bloomberg.bg.secondary }}>
                                      <SortableTh label="Symbol" col="symbol" align="left" />
                                      <SortableTh label="Company" col="company" align="left" />
                                      <SortableTh label="Portfolio" col="portfolio" align="left" />
                                      <SortableTh label="Date" col="date" />
                                      <SortableTh label="Direction" col="direction" />
                                      <SortableTh label="Price" col="price" />
                                      <SortableTh label="Chg%" col="chg" />
                                      <SortableTh label="RSI" col="rsi" />
                                      <SortableTh label="20MA" col="ma20" />
                                      <SortableTh label="50MA" col="ma50" />
                                      <SortableTh label="200MA" col="ma200" />
                                      <Box component="th" sx={{ py: 0.5, px: 1.5, fontSize: '0.65rem', fontWeight: 700, color: bloomberg.accent.orange, textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Detail</Box>
                                    </Box>
                                  </Box>
                                  <Box component="tbody">
                                    {[...items]
                                      .sort((a, b) => pfSortCol
                                        ? pfCompare(a, a.sig, b, b.sig)
                                        : (b.sig.signal_date || '').localeCompare(a.sig.signal_date || '') || a.symbol.localeCompare(b.symbol))
                                      .map((item, i) => {
                                        const isBull = item.sig.direction === 'bullish';
                                        const dirColor = isBull ? '#4caf50' : '#ef4444';
                                        const chgColor = item.sig.change_pct == null ? bloomberg.text.muted : item.sig.change_pct > 0 ? '#4caf50' : item.sig.change_pct < 0 ? '#ef4444' : bloomberg.text.primary;
                                        return (
                                          <Box key={i} component="tr" sx={{ borderTop: `1px solid ${bloomberg.border.primary}`, '&:hover': { bgcolor: bloomberg.bg.secondary } }}>
                                            <Box component="td" sx={{ py: 0.7, px: 1.5, whiteSpace: 'nowrap' }}>
                                              <TickerLink symbol={item.symbol} fontWeight={700} fontSize={12} />
                                            </Box>
                                            <Box component="td" sx={{ py: 0.7, px: 1.5, fontSize: '0.72rem', color: bloomberg.text.secondary, whiteSpace: 'nowrap' }}>{item.name}</Box>
                                            <Box component="td" sx={{ py: 0.7, px: 1.5, fontSize: '0.65rem', color: bloomberg.text.muted }}>
                                              {item.portfolios.map((pf: any, pi: number) => (
                                                <span key={pi} style={{ marginRight: 6 }}>{pf.name} {(pf.weight * 100).toFixed(1)}%</span>
                                              ))}
                                            </Box>
                                            <Box component="td" sx={{ py: 0.7, px: 1.5, fontSize: '0.72rem', fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.text.muted, textAlign: 'right', whiteSpace: 'nowrap' }}>{item.sig.signal_date}</Box>
                                            <Box component="td" sx={{ py: 0.7, px: 1.5, fontSize: '0.72rem', fontWeight: 700, color: dirColor, textAlign: 'right', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.3 }}>
                                              {isBull ? <TrendingUpIcon sx={{ fontSize: 12 }} /> : <TrendingDownIcon sx={{ fontSize: 12 }} />}
                                              {item.sig.direction}
                                            </Box>
                                            <Box component="td" sx={{ py: 0.7, px: 1.5, fontSize: '0.72rem', fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.text.secondary, textAlign: 'right', whiteSpace: 'nowrap' }}>{item.sig.price != null ? item.sig.price.toFixed(2) : '—'}</Box>
                                            <Box component="td" sx={{ py: 0.7, px: 1.5, fontSize: '0.72rem', fontFamily: '"IBM Plex Mono", monospace', color: chgColor, textAlign: 'right', whiteSpace: 'nowrap' }}>{item.sig.change_pct != null ? `${item.sig.change_pct > 0 ? '+' : ''}${item.sig.change_pct.toFixed(1)}%` : '—'}</Box>
                                            <Box component="td" sx={{ py: 0.7, px: 1.5, fontSize: '0.72rem', fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.text.muted, textAlign: 'right', whiteSpace: 'nowrap' }}>{item.sig.rsi != null ? item.sig.rsi.toFixed(0) : '—'}</Box>
                                            {[item.sig.sma20_dist, item.sig.sma50_dist, item.sig.sma200_dist].map((dist, di) => (
                                              <Box key={di} component="td" sx={{ py: 0.7, px: 1.5, fontSize: '0.72rem', fontFamily: '"IBM Plex Mono", monospace', color: dist == null ? bloomberg.text.muted : dist > 0 ? '#4caf50' : dist < 0 ? '#ef4444' : bloomberg.text.primary, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                {dist != null ? `${dist > 0 ? '+' : ''}${dist.toFixed(1)}%` : '—'}
                                              </Box>
                                            ))}
                                            <Box component="td" sx={{ py: 0.7, px: 1.5, fontSize: '0.72rem', color: bloomberg.text.secondary, textAlign: 'left' }}>{item.sig.detail}</Box>
                                          </Box>
                                        );
                                      })}
                                  </Box>
                                </Box>
                              </Paper>
                            );
                          })}
                      </Box>
                    </Box>
                  ))}
                </Box>
              );
            })()
          )}
        </Box>
        );
      })()}{/* end mainView === 2 */}

      {mainView === 3 && (
        <Box sx={{ p: 2 }}>
          <ModelPortfolioEarnings />
        </Box>
      )}

      {mainView === 4 && (
        <Box sx={{ p: 2 }}>
          {/* Period filter */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Typography variant="caption" sx={{ color: bloomberg.text.secondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mr: 0.5 }}>Period:</Typography>
            {(['1D', '7D', '30D'] as const).map(p => (
              <Chip
                key={p}
                label={p}
                size="small"
                onClick={() => setMoversPeriod(p)}
                sx={{
                  height: 24, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  bgcolor: moversPeriod === p ? bloomberg.accent.orange : 'transparent',
                  color: moversPeriod === p ? '#000' : bloomberg.text.secondary,
                  border: `1px solid ${moversPeriod === p ? bloomberg.accent.orange : bloomberg.border}`,
                  '&:hover': { bgcolor: moversPeriod === p ? bloomberg.accent.orange : `${bloomberg.accent.orange}22` },
                }}
              />
            ))}
          </Box>

          {moversLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[...Array(8)].map((_, i) => <Skeleton key={i} variant="rounded" height={36} />)}
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              {/* Gainers */}
              <Paper elevation={2} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TrendingUpIcon sx={{ color: bloomberg.status?.positive || '#4caf50', fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: bloomberg.status?.positive || '#4caf50' }}>
                    Top Gainers ({moversPeriod})
                  </Typography>
                </Box>
                {moversGainers.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No data</Typography>
                ) : (
                  <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                    <Box component="thead">
                      <Box component="tr" sx={{ borderBottom: `2px solid ${bloomberg.accent.orange}` }}>
                        {['Symbol', 'Company', 'Chg%', 'Sector', 'Portfolios'].map((h, i) => (
                          <Box key={h} component="th" sx={{ textAlign: i >= 2 ? 'right' : 'left', py: 0.7, px: 0.8, fontSize: 10, fontWeight: 700, color: bloomberg.accent.orange, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</Box>
                        ))}
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {moversGainers.map(m => (
                        <Box component="tr" key={m.symbol} sx={{ borderBottom: `1px solid ${bloomberg.border}`, '&:hover': { bgcolor: `${bloomberg.accent.orange}08` } }}>
                          <Box component="td" sx={{ py: 0.7, px: 0.8, fontSize: 12 }}><TickerLink symbol={m.symbol} fontSize={12} /></Box>
                          <Box component="td" sx={{ py: 0.7, px: 0.8, fontSize: 11, color: bloomberg.text.secondary, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.company_name}</Box>
                          <Box component="td" sx={{ py: 0.7, px: 0.8, textAlign: 'right' }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: bloomberg.status?.positive || '#4caf50' }}>+{m.change_pct.toFixed(2)}%</Typography>
                          </Box>
                          <Box component="td" sx={{ py: 0.7, px: 0.8, fontSize: 10, color: bloomberg.text.muted, textAlign: 'right', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.sector || '—'}</Box>
                          <Box component="td" sx={{ py: 0.7, px: 0.8, textAlign: 'right' }}>
                            <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              {m.portfolios.slice(0, 2).map(pf => (
                                <Chip key={pf} label={pf} size="small" sx={{ fontSize: 9, height: 16, maxWidth: 90, '& .MuiChip-label': { px: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }, bgcolor: `${bloomberg.accent.orange}20`, color: bloomberg.accent.orange }} />
                              ))}
                              {m.portfolios.length > 2 && <Chip label={`+${m.portfolios.length - 2}`} size="small" sx={{ fontSize: 9, height: 16, bgcolor: bloomberg.border, color: bloomberg.text.muted }} />}
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Paper>

              {/* Losers */}
              <Paper elevation={2} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TrendingDownIcon sx={{ color: bloomberg.status?.negative || '#f44336', fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: bloomberg.status?.negative || '#f44336' }}>
                    Top Losers ({moversPeriod})
                  </Typography>
                </Box>
                {moversLosers.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No data</Typography>
                ) : (
                  <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                    <Box component="thead">
                      <Box component="tr" sx={{ borderBottom: `2px solid ${bloomberg.status?.negative || '#f44336'}` }}>
                        {['Symbol', 'Company', 'Chg%', 'Sector', 'Portfolios'].map((h, i) => (
                          <Box key={h} component="th" sx={{ textAlign: i >= 2 ? 'right' : 'left', py: 0.7, px: 0.8, fontSize: 10, fontWeight: 700, color: bloomberg.status?.negative || '#f44336', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</Box>
                        ))}
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {moversLosers.map(m => (
                        <Box component="tr" key={m.symbol} sx={{ borderBottom: `1px solid ${bloomberg.border}`, '&:hover': { bgcolor: `${bloomberg.status?.negative || '#f44336'}08` } }}>
                          <Box component="td" sx={{ py: 0.7, px: 0.8, fontSize: 12 }}><TickerLink symbol={m.symbol} fontSize={12} /></Box>
                          <Box component="td" sx={{ py: 0.7, px: 0.8, fontSize: 11, color: bloomberg.text.secondary, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.company_name}</Box>
                          <Box component="td" sx={{ py: 0.7, px: 0.8, textAlign: 'right' }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: bloomberg.status?.negative || '#f44336' }}>{m.change_pct.toFixed(2)}%</Typography>
                          </Box>
                          <Box component="td" sx={{ py: 0.7, px: 0.8, fontSize: 10, color: bloomberg.text.muted, textAlign: 'right', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.sector || '—'}</Box>
                          <Box component="td" sx={{ py: 0.7, px: 0.8, textAlign: 'right' }}>
                            <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              {m.portfolios.slice(0, 2).map(pf => (
                                <Chip key={pf} label={pf} size="small" sx={{ fontSize: 9, height: 16, maxWidth: 90, '& .MuiChip-label': { px: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }, bgcolor: `${bloomberg.status?.negative || '#f44336'}20`, color: bloomberg.status?.negative || '#f44336' }} />
                              ))}
                              {m.portfolios.length > 2 && <Chip label={`+${m.portfolios.length - 2}`} size="small" sx={{ fontSize: 9, height: 16, bgcolor: bloomberg.border, color: bloomberg.text.muted }} />}
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Paper>
            </Box>
          )}

          {/* ── MA Momentum ── */}
          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                MA Momentum
              </Typography>
              <Typography variant="caption" sx={{ color: bloomberg.text.secondary }}>
                Stocks furthest above / below their moving average
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                {(['ma20', 'ma50', 'ma200'] as const).map(ma => (
                  <Chip
                    key={ma}
                    label={ma === 'ma20' ? '20 MA' : ma === 'ma50' ? '50 MA' : '200 MA'}
                    size="small"
                    onClick={() => { setSelectedMa(ma); setMaSort(ma); }}
                    sx={{
                      height: 24, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      bgcolor: selectedMa === ma ? bloomberg.accent.orange : 'transparent',
                      color: selectedMa === ma ? '#000' : bloomberg.text.secondary,
                      border: `1px solid ${selectedMa === ma ? bloomberg.accent.orange : bloomberg.border}`,
                      '&:hover': { bgcolor: selectedMa === ma ? bloomberg.accent.orange : `${bloomberg.accent.orange}22` },
                    }}
                  />
                ))}
              </Box>
            </Box>

            {maMomentumLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[...Array(6)].map((_, i) => <Skeleton key={i} variant="rounded" height={36} />)}
              </Box>
            ) : maMomentum ? (() => {
              const bucket = maMomentum[selectedMa];
              const maLabel = selectedMa === 'ma20' ? '20MA' : selectedMa === 'ma50' ? '50MA' : '200MA';

              const fmtDist = (v: number | null, highlight: boolean, posColor: string, negColor: string) => {
                if (v == null) return <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>—</Typography>;
                const col = v >= 0 ? posColor : negColor;
                return (
                  <Typography sx={{ fontSize: highlight ? 12 : 11, fontWeight: highlight ? 700 : 500, color: col }}>
                    {v >= 0 ? '+' : ''}{v.toFixed(1)}%
                  </Typography>
                );
              };

              const maSortVal = (m: MaMover): number => {
                switch (maSort) {
                  case 'ma20':  return m.sma20_dist  ?? -Infinity;
                  case 'ma50':  return m.sma50_dist  ?? -Infinity;
                  case 'ma200': return m.sma200_dist ?? -Infinity;
                  case 'macd':  return m.macd_hist   ?? -Infinity;
                  case 'chg':   return m.change_pct  ?? -Infinity;
                }
              };

              const MaTable: React.FC<{ rows: MaMover[]; direction: 'above' | 'below' }> = ({ rows, direction }) => {
                const posColor = bloomberg.status?.positive || '#4caf50';
                const negColor = bloomberg.status?.negative || '#f44336';
                const headerColor = direction === 'above' ? posColor : negColor;
                const sorted = [...rows].sort((a, b) =>
                  direction === 'above' ? maSortVal(b) - maSortVal(a) : maSortVal(a) - maSortVal(b)
                );
                const colHeaders = [
                  { h: '#',          key: null },
                  { h: 'Symbol',     key: null },
                  { h: 'Company',    key: null },
                  { h: 'MACD Hist',  key: 'macd'  },
                  { h: 'vs 20MA',    key: 'ma20'  },
                  { h: 'vs 50MA',    key: 'ma50'  },
                  { h: 'vs 200MA',   key: 'ma200' },
                  { h: '1D Chg%',    key: 'chg'   },
                ] as { h: string; key: 'ma20'|'ma50'|'ma200'|'macd'|'chg'|null }[];
                return (
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      {direction === 'above'
                        ? <TrendingUpIcon sx={{ color: posColor, fontSize: 18 }} />
                        : <TrendingDownIcon sx={{ color: negColor, fontSize: 18 }} />
                      }
                      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: headerColor }}>
                        {direction === 'above' ? `Above ${maLabel}` : `Below ${maLabel}`}
                      </Typography>
                    </Box>
                    {sorted.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No data</Typography>
                    ) : (
                      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                        <Box component="thead">
                          <Box component="tr" sx={{ borderBottom: `2px solid ${headerColor}` }}>
                            {colHeaders.map(({ h, key }, i) => {
                              const isSort = key === maSort;
                              const isMaHighlight = (h === 'vs 20MA' && selectedMa === 'ma20') || (h === 'vs 50MA' && selectedMa === 'ma50') || (h === 'vs 200MA' && selectedMa === 'ma200');
                              return (
                                <Box key={h} component="th"
                                  onClick={key ? () => setMaSort(key) : undefined}
                                  sx={{
                                    textAlign: i >= 3 ? 'right' : 'left', py: 0.7, px: 0.8, fontSize: 10, fontWeight: 700,
                                    color: isSort ? bloomberg.accent.orange : isMaHighlight ? bloomberg.accent.orange : headerColor,
                                    textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap',
                                    cursor: key ? 'pointer' : 'default', userSelect: 'none',
                                    '&:hover': key ? { color: bloomberg.accent.orange, opacity: 0.85 } : {},
                                  }}>{h}{isSort ? ' ▾' : ''}</Box>
                              );
                            })}
                          </Box>
                        </Box>
                        <Box component="tbody">
                          {sorted.slice(0, 10).map((m, idx) => (
                            <Box component="tr" key={m.symbol} sx={{ borderBottom: `1px solid ${bloomberg.border}`, '&:hover': { bgcolor: `${headerColor}08` } }}>
                              <Box component="td" sx={{ py: 0.6, px: 0.8, fontSize: 10, color: bloomberg.text.muted, width: 24 }}>{idx + 1}</Box>
                              <Box component="td" sx={{ py: 0.6, px: 0.8, fontSize: 12 }}><TickerLink symbol={m.symbol} fontSize={12} /></Box>
                              <Tooltip title={m.company_name} placement="top" enterDelay={800} arrow>
                                <Box component="td" sx={{ py: 0.6, px: 0.8, fontSize: 11, color: bloomberg.text.secondary, maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'default' }}>{m.company_name}</Box>
                              </Tooltip>
                              {/* MACD Hist */}
                              <Box component="td" sx={{ py: 0.6, px: 0.8, textAlign: 'right', bgcolor: maSort === 'macd' ? 'rgba(255,255,255,0.03)' : undefined }}>
                                {m.macd_hist == null
                                  ? <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>—</Typography>
                                  : <Typography sx={{ fontSize: maSort === 'macd' ? 12 : 11, fontWeight: maSort === 'macd' ? 700 : 500, color: m.macd_hist >= 0 ? posColor : negColor }}>
                                      {m.macd_hist >= 0 ? '+' : ''}{m.macd_hist.toFixed(2)}%
                                    </Typography>
                                }
                              </Box>
                              <Box component="td" sx={{ py: 0.6, px: 0.8, textAlign: 'right', bgcolor: maSort === 'ma20' ? 'rgba(255,255,255,0.03)' : undefined }}>
                                {fmtDist(m.sma20_dist, maSort === 'ma20', posColor, negColor)}
                              </Box>
                              <Box component="td" sx={{ py: 0.6, px: 0.8, textAlign: 'right', bgcolor: maSort === 'ma50' ? 'rgba(255,255,255,0.03)' : undefined }}>
                                {fmtDist(m.sma50_dist, maSort === 'ma50', posColor, negColor)}
                              </Box>
                              <Box component="td" sx={{ py: 0.6, px: 0.8, textAlign: 'right', bgcolor: maSort === 'ma200' ? 'rgba(255,255,255,0.03)' : undefined }}>
                                {fmtDist(m.sma200_dist, maSort === 'ma200', posColor, negColor)}
                              </Box>
                              <Box component="td" sx={{ py: 0.6, px: 0.8, textAlign: 'right', bgcolor: maSort === 'chg' ? 'rgba(255,255,255,0.03)' : undefined }}>
                                {m.change_pct == null
                                  ? <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>—</Typography>
                                  : <Typography sx={{ fontSize: maSort === 'chg' ? 12 : 11, fontWeight: maSort === 'chg' ? 700 : 500, color: m.change_pct >= 0 ? posColor : negColor }}>{m.change_pct >= 0 ? '+' : ''}{m.change_pct.toFixed(2)}%</Typography>
                                }
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Paper>
                );
              };

              return (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                  <MaTable rows={bucket.above} direction="above" />
                  <MaTable rows={bucket.below} direction="below" />
                </Box>
              );
            })() : null}
          </Box>
        </Box>
      )}

      {/* ── Radar Tab ── */}
      {mainView === 5 && (
        <Box sx={{ p: 2 }}>
          {/* Filter bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            <Select size="small" value={radarCountry} onChange={e => setRadarCountry(e.target.value)}
              displayEmpty sx={{ fontSize: 12, height: 28, minWidth: 120 }}>
              <MenuItem value=""><em>All Countries</em></MenuItem>
              {(radarData?.meta.available_countries || []).map(c => (
                <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>
              ))}
            </Select>
            <Select size="small" value={radarSector} onChange={e => setRadarSector(e.target.value)}
              displayEmpty sx={{ fontSize: 12, height: 28, minWidth: 140 }}>
              <MenuItem value=""><em>All Sectors</em></MenuItem>
              {(radarData?.meta.available_sectors || []).map(s => (
                <MenuItem key={s} value={s} sx={{ fontSize: 12 }}>{s}</MenuItem>
              ))}
            </Select>
            <Select size="small" value={radarCapFilter} onChange={e => setRadarCapFilter(e.target.value)}
              displayEmpty sx={{ fontSize: 12, height: 28, minWidth: 130 }}>
              <MenuItem value=""><em>All Sizes</em></MenuItem>
              <MenuItem value="large" sx={{ fontSize: 12 }}>Large (&gt;$10B)</MenuItem>
              <MenuItem value="mid"   sx={{ fontSize: 12 }}>Mid ($2.5–10B)</MenuItem>
            </Select>
            <Chip
              label="Small Caps"
              size="small"
              clickable
              onClick={() => setRadarIncludeSmallCap(v => !v)}
              sx={{
                fontSize: 11, height: 26,
                bgcolor: radarIncludeSmallCap ? bloomberg.accent.orange : 'transparent',
                color: radarIncludeSmallCap ? '#000' : bloomberg.text.muted,
                border: `1px solid ${radarIncludeSmallCap ? bloomberg.accent.orange : bloomberg.border}`,
                fontWeight: radarIncludeSmallCap ? 700 : 400,
                '&:hover': { bgcolor: radarIncludeSmallCap ? bloomberg.accent.orange : `${bloomberg.accent.orange}22` },
              }}
            />
            {radarData && (
              <Typography variant="caption" sx={{ ml: 'auto', color: bloomberg.text.secondary, fontSize: 11 }}>
                {radarData.meta.total_accelerating} accelerating · {radarData.meta.total_decelerating} decelerating
                <span style={{ color: bloomberg.text.muted }}> · not in any portfolio</span>
              </Typography>
            )}
          </Box>

          {radarLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[...Array(6)].map((_, i) => <Skeleton key={i} variant="rounded" height={36} />)}
            </Box>
          ) : radarError ? (
            <Box sx={{ p: 2, bgcolor: 'rgba(239,83,80,0.08)', border: '1px solid rgba(239,83,80,0.3)', borderRadius: 1 }}>
              <Typography sx={{ fontSize: 12, color: '#ef5350' }}>
                Failed to load radar: {radarError}
              </Typography>
            </Box>
          ) : radarData ? (() => {
            const posColor = bloomberg.status?.positive || '#4caf50';
            const negColor = bloomberg.status?.negative || '#f44336';

            const fmtDist = (v: number | null) => {
              if (v == null) return <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>—</Typography>;
              return <Typography sx={{ fontSize: 11, fontWeight: 500, color: v >= 0 ? posColor : negColor }}>{v >= 0 ? '+' : ''}{v.toFixed(1)}%</Typography>;
            };

            const sortVal = (r: RadarEntry): number => {
              switch (radarSort) {
                case 'ma20':  return r.sma20_dist  ?? -Infinity;
                case 'ma50':  return r.sma50_dist  ?? -Infinity;
                case 'ma200': return r.sma200_dist ?? -Infinity;
                case 'chg':   return r.change_pct  ?? -Infinity;
                default:      return r.macd_hist   ?? -Infinity;
              }
            };

            const colActive = (h: string) => {
              if (radarSort === 'macd'  && h === 'MACD Hist') return true;
              if (radarSort === 'ma20'  && h === 'vs 20MA')   return true;
              if (radarSort === 'ma50'  && h === 'vs 50MA')   return true;
              if (radarSort === 'ma200' && h === 'vs 200MA')  return true;
              if (radarSort === 'chg'   && h === '1D Chg%')   return true;
              return false;
            };

            const RadarTable: React.FC<{ rows: RadarEntry[]; direction: 'accelerating' | 'decelerating' }> = ({ rows, direction }) => {
              const headerColor = direction === 'accelerating' ? posColor : negColor;
              const histColor = (v: number) => v > 0 ? posColor : negColor;
              const sorted = [...rows].sort((a, b) =>
                direction === 'accelerating' ? sortVal(b) - sortVal(a) : sortVal(a) - sortVal(b)
              );
              return (
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    {direction === 'accelerating'
                      ? <TrendingUpIcon sx={{ color: posColor, fontSize: 18 }} />
                      : <TrendingDownIcon sx={{ color: negColor, fontSize: 18 }} />
                    }
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: headerColor, lineHeight: 1.2 }}>
                        {direction === 'accelerating' ? 'Accelerating Up' : 'Accelerating Down'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: bloomberg.text.muted, lineHeight: 1.2 }}>
                        {direction === 'accelerating' ? 'MACD histogram rising — buying momentum building' : 'MACD histogram falling — selling momentum building'}
                      </Typography>
                    </Box>
                  </Box>
                  {sorted.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No data</Typography>
                  ) : (
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                      <Box component="thead">
                        <Box component="tr" sx={{ borderBottom: `2px solid ${headerColor}` }}>
                          {([
                            { h: '#',         key: null },
                            { h: 'Symbol',    key: null },
                            { h: 'Company',   key: null },
                            { h: 'MACD Hist', key: 'macd'  },
                            { h: 'vs 20MA',   key: 'ma20'  },
                            { h: 'vs 50MA',   key: 'ma50'  },
                            { h: 'vs 200MA',  key: 'ma200' },
                            { h: '1D Chg%',   key: 'chg'   },
                          ] as { h: string; key: 'macd'|'ma20'|'ma50'|'ma200'|'chg'|null }[]).map(({ h, key }, i) => (
                            <Box key={h} component="th"
                              onClick={key ? () => setRadarSort(key) : undefined}
                              sx={{
                                textAlign: i >= 3 ? 'right' : 'left', py: 0.7, px: 0.8, fontSize: 10,
                                fontWeight: 700,
                                color: colActive(h) ? bloomberg.accent.orange : headerColor,
                                textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap',
                                cursor: key ? 'pointer' : 'default',
                                userSelect: 'none',
                                '&:hover': key ? { color: bloomberg.accent.orange, opacity: 0.85 } : {},
                              }}>{h}{colActive(h) ? ' ▾' : ''}</Box>
                          ))}
                        </Box>
                      </Box>
                      <Box component="tbody">
                        {sorted.map((m, idx) => (
                          <Box component="tr" key={m.symbol} sx={{ borderBottom: `1px solid ${bloomberg.border}`, '&:hover': { bgcolor: `${headerColor}08` } }}>
                            <Box component="td" sx={{ py: 0.6, px: 0.8, fontSize: 10, color: bloomberg.text.muted, width: 24 }}>{idx + 1}</Box>
                            <Box component="td" sx={{ py: 0.6, px: 0.8, fontSize: 12 }}><TickerLink symbol={m.symbol} fontSize={12} /></Box>
                            <Tooltip title={m.company_name} placement="top" enterDelay={800} arrow>
                              <Box component="td" sx={{ py: 0.6, px: 0.8, fontSize: 11, color: bloomberg.text.secondary, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'default' }}>{m.company_name}</Box>
                            </Tooltip>
                            <Box component="td" sx={{ py: 0.6, px: 0.8, textAlign: 'right', bgcolor: colActive('MACD Hist') ? 'rgba(255,255,255,0.03)' : undefined }}>
                              {m.macd_hist == null
                                ? <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>—</Typography>
                                : <Typography sx={{ fontSize: colActive('MACD Hist') ? 12 : 11, fontWeight: colActive('MACD Hist') ? 700 : 500, color: histColor(m.macd_hist) }}>
                                    {m.macd_hist > 0 ? '+' : ''}{m.macd_hist.toFixed(2)}%
                                  </Typography>
                              }
                            </Box>
                            <Box component="td" sx={{ py: 0.6, px: 0.8, textAlign: 'right', bgcolor: colActive('vs 20MA') ? 'rgba(255,255,255,0.03)' : undefined }}>
                              <Typography sx={{ fontSize: colActive('vs 20MA') ? 12 : 11, fontWeight: colActive('vs 20MA') ? 700 : 500, color: (m.sma20_dist ?? 0) >= 0 ? posColor : negColor }}>
                                {m.sma20_dist == null ? '—' : `${m.sma20_dist >= 0 ? '+' : ''}${m.sma20_dist.toFixed(1)}%`}
                              </Typography>
                            </Box>
                            <Box component="td" sx={{ py: 0.6, px: 0.8, textAlign: 'right', bgcolor: colActive('vs 50MA') ? 'rgba(255,255,255,0.03)' : undefined }}>
                              <Typography sx={{ fontSize: colActive('vs 50MA') ? 12 : 11, fontWeight: colActive('vs 50MA') ? 700 : 500, color: (m.sma50_dist ?? 0) >= 0 ? posColor : negColor }}>
                                {m.sma50_dist == null ? '—' : `${m.sma50_dist >= 0 ? '+' : ''}${m.sma50_dist.toFixed(1)}%`}
                              </Typography>
                            </Box>
                            <Box component="td" sx={{ py: 0.6, px: 0.8, textAlign: 'right', bgcolor: colActive('vs 200MA') ? 'rgba(255,255,255,0.03)' : undefined }}>
                              <Typography sx={{ fontSize: colActive('vs 200MA') ? 12 : 11, fontWeight: colActive('vs 200MA') ? 700 : 500, color: (m.sma200_dist ?? 0) >= 0 ? posColor : negColor }}>
                                {m.sma200_dist == null ? '—' : `${m.sma200_dist >= 0 ? '+' : ''}${m.sma200_dist.toFixed(1)}%`}
                              </Typography>
                            </Box>
                            <Box component="td" sx={{ py: 0.6, px: 0.8, textAlign: 'right', bgcolor: colActive('1D Chg%') ? 'rgba(255,255,255,0.03)' : undefined }}>
                              {m.change_pct == null
                                ? <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>—</Typography>
                                : <Typography sx={{ fontSize: colActive('1D Chg%') ? 12 : 11, fontWeight: colActive('1D Chg%') ? 700 : 500, color: m.change_pct >= 0 ? posColor : negColor }}>
                                    {m.change_pct >= 0 ? '+' : ''}{m.change_pct.toFixed(2)}%
                                  </Typography>
                              }
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Paper>
              );
            };

            return (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <RadarTable rows={radarData.accelerating} direction="accelerating" />
                <RadarTable rows={radarData.decelerating} direction="decelerating" />
              </Box>
            );
          })() : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary">No radar data available.</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* ── News Tab ── */}
      {mainView === 6 && (
        <Box sx={{ p: 2 }}>
          {/* Filter bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            {/* Portfolio filter */}
            <Select
              size="small"
              value={newsPortfolioFilter}
              onChange={e => { setNewsPortfolioFilter(e.target.value as number | ''); }}
              displayEmpty
              sx={{ fontSize: 12, height: 28, minWidth: 160 }}
            >
              <MenuItem value=""><em>All Portfolios</em></MenuItem>
              {(newsData?.portfolios || []).map(p => (
                <MenuItem key={p.id} value={p.id} sx={{ fontSize: 12 }}>{p.name}</MenuItem>
              ))}
            </Select>
            {/* Sector filter */}
            <Select
              size="small"
              value={newsSectorFilter}
              onChange={e => setNewsSectorFilter(e.target.value)}
              displayEmpty
              sx={{ fontSize: 12, height: 28, minWidth: 140 }}
            >
              <MenuItem value=""><em>All Sectors</em></MenuItem>
              {(newsData?.available_sectors || []).map(s => (
                <MenuItem key={s} value={s} sx={{ fontSize: 12 }}>{s}</MenuItem>
              ))}
            </Select>
            {/* Country filter */}
            <Select
              size="small"
              value={newsCountryFilter}
              onChange={e => setNewsCountryFilter(e.target.value)}
              displayEmpty
              sx={{ fontSize: 12, height: 28, minWidth: 120 }}
            >
              <MenuItem value=""><em>All Countries</em></MenuItem>
              {(newsData?.available_countries || []).map(c => (
                <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>
              ))}
            </Select>
            {/* News / Social toggle */}
            {(['all', 'news', 'social'] as const).map(ct => (
              <Chip
                key={ct}
                label={ct === 'all' ? 'All' : ct === 'news' ? 'News' : 'Social'}
                size="small"
                clickable
                onClick={() => setNewsContentType(ct)}
                sx={{
                  fontSize: 11, height: 26,
                  bgcolor: newsContentType === ct ? bloomberg.accent.orange : 'transparent',
                  color: newsContentType === ct ? '#000' : bloomberg.text.muted,
                  border: `1px solid ${newsContentType === ct ? bloomberg.accent.orange : bloomberg.border}`,
                  fontWeight: newsContentType === ct ? 700 : 400,
                  '&:hover': { bgcolor: newsContentType === ct ? bloomberg.accent.orange : `${bloomberg.accent.orange}22` },
                }}
              />
            ))}
            {/* Breaking-only toggle */}
            <Chip
              label="🔴 Breaking"
              size="small"
              clickable
              onClick={() => setNewsBreakingOnly(v => !v)}
              sx={{
                fontSize: 11, height: 26,
                bgcolor: newsBreakingOnly ? '#ef4444' : 'transparent',
                color: newsBreakingOnly ? '#fff' : bloomberg.text.muted,
                border: `1px solid ${newsBreakingOnly ? '#ef4444' : bloomberg.border}`,
                fontWeight: newsBreakingOnly ? 700 : 400,
                '&:hover': { bgcolor: newsBreakingOnly ? '#ef4444' : '#ef444422' },
              }}
            />
            {/* Sort: recent (default) vs quality */}
            <ToggleButtonGroup
              value={newsSort} exclusive size="small"
              onChange={(_, v) => { if (v) setNewsSort(v); }}
              sx={{ '& .MuiToggleButton-root': { py: 0.2, px: 1, fontSize: 11, fontWeight: 600, textTransform: 'none', color: bloomberg.text.muted, borderColor: bloomberg.border.primary, '&.Mui-selected': { color: bloomberg.accent.orange, bgcolor: bloomberg.accent.orangeDim } } }}
            >
              <ToggleButton value="recent">Recent</ToggleButton>
              <ToggleButton value="quality">Top Quality</ToggleButton>
            </ToggleButtonGroup>
            {/* Search */}
            <TextField
              size="small"
              placeholder="Search headlines…"
              value={newsSearchInput}
              onChange={e => setNewsSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') setNewsSearch(newsSearchInput); }}
              onBlur={() => setNewsSearch(newsSearchInput)}
              sx={{ fontSize: 12, '& input': { fontSize: 12, py: '4px', px: '8px' }, width: 180 }}
              inputProps={{ style: { height: 20 } }}
            />
            {newsData && (
              <Typography variant="caption" sx={{ ml: 'auto', color: bloomberg.text.secondary, fontSize: 11 }}>
                {newsData.total.toLocaleString()} articles
              </Typography>
            )}
          </Box>

          {/* Article list */}
          {newsLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[...Array(8)].map((_, i) => <Skeleton key={i} variant="rounded" height={72} />)}
            </Box>
          ) : !newsData || newsData.articles.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary">No articles found.</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {newsData.articles.map(article => {
                  const isSocial = article.source.startsWith('Reddit:') || article.source.startsWith('Twitter:');
                  const sentColor = article.sentiment === 'positive' ? '#4caf50' : article.sentiment === 'negative' ? '#ef5350' : bloomberg.text.muted;
                  const pubDate = article.published_at ? new Date(article.published_at) : null;
                  const timeAgo = pubDate ? (() => {
                    const diff = Date.now() - pubDate.getTime();
                    const h = Math.floor(diff / 3600000);
                    const d = Math.floor(h / 24);
                    return d > 0 ? `${d}d ago` : h > 0 ? `${h}h ago` : 'recently';
                  })() : '';
                  return (
                    <Paper
                      key={article.id}
                      elevation={1}
                      sx={{
                        p: 1.5,
                        borderLeft: `3px solid ${article.is_breaking ? '#ef4444' : isSocial ? '#7c4dff' : bloomberg.accent.orange}`,
                        '&:hover': { bgcolor: 'action.hover' },
                        cursor: article.url ? 'pointer' : 'default',
                      }}
                      onClick={() => article.url && window.open(article.url, '_blank', 'noopener')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {/* Title row */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                            {article.is_breaking && (
                              <Chip
                                label="BREAKING" size="small"
                                sx={{ fontSize: 9, height: 16, fontWeight: 800, bgcolor: '#ef4444', color: '#fff', letterSpacing: '0.05em' }}
                              />
                            )}
                            <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, flex: 1, minWidth: 0 }}>
                              {article.title}
                            </Typography>
                            {article.url && (
                              <OpenInNewIcon sx={{ fontSize: 13, color: bloomberg.text.muted, flexShrink: 0 }} />
                            )}
                          </Box>
                          {/* Meta row */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Chip
                              label={article.source}
                              size="small"
                              sx={{
                                fontSize: 10, height: 18,
                                bgcolor: isSocial ? '#7c4dff22' : `${bloomberg.accent.orange}22`,
                                color: isSocial ? '#7c4dff' : bloomberg.accent.orange,
                                border: `1px solid ${isSocial ? '#7c4dff44' : `${bloomberg.accent.orange}44`}`,
                              }}
                            />
                            {article.symbol && (
                              <Chip
                                label={article.symbol}
                                size="small"
                                sx={{ fontSize: 10, height: 18, bgcolor: 'transparent', border: `1px solid ${bloomberg.border}`, color: bloomberg.text.secondary }}
                              />
                            )}
                            {article.sentiment && (
                              <Typography sx={{ fontSize: 10, color: sentColor, fontWeight: 600 }}>
                                {article.sentiment}
                                {article.sentiment_score != null && ` (${article.sentiment_score.toFixed(2)})`}
                              </Typography>
                            )}
                            {article.quality_score != null && (
                              <Tooltip title="Heuristic quality score — source tier + substance signals, 0-100">
                                <Typography sx={{
                                  fontSize: 10, fontWeight: 600, cursor: 'help',
                                  color: article.quality_score >= 50 ? '#4caf50' : article.quality_score >= 35 ? bloomberg.accent.orange : bloomberg.text.muted,
                                }}>
                                  Q{Math.round(article.quality_score)}
                                </Typography>
                              </Tooltip>
                            )}
                            <Typography sx={{ fontSize: 10, color: bloomberg.text.muted, ml: 'auto' }}>
                              {timeAgo}
                            </Typography>
                          </Box>
                          {/* Summary */}
                          {article.summary && (
                            <Typography sx={{ fontSize: 11, color: bloomberg.text.secondary, mt: 0.5, lineHeight: 1.4,
                              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {article.summary}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
              {/* Pagination */}
              {newsData.total_pages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 2 }}>
                  <Button
                    size="small"
                    disabled={newsPage <= 1 || newsLoading}
                    onClick={() => fetchNews(newsPage - 1)}
                    sx={{ fontSize: 11, minWidth: 0, px: 1 }}
                  >
                    ‹ Prev
                  </Button>
                  <Typography sx={{ fontSize: 11, color: bloomberg.text.secondary }}>
                    {newsPage} / {newsData.total_pages}
                  </Typography>
                  <Button
                    size="small"
                    disabled={newsPage >= newsData.total_pages || newsLoading}
                    onClick={() => fetchNews(newsPage + 1)}
                    sx={{ fontSize: 11, minWidth: 0, px: 1 }}
                  >
                    Next ›
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {mainView === 7 && (
        <LiveUniverse />
      )}
    </Box>
  );
};

/* ── Helper: Metric Card ── */
const MetricCard: React.FC<{ label: string; value: string; tooltip?: string; color?: string }> = ({ label, value, tooltip, color }) => (
  <Tooltip title={tooltip || ''} arrow placement="top">
    <Paper elevation={2} sx={{ p: 1.5, textAlign: 'center' }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, color: color || 'text.primary', lineHeight: 1.3 }}>
        {value}
      </Typography>
    </Paper>
  </Tooltip>
);

/* ── Rebalance History ──
   Every set_holdings call now appends an immutable snapshot (backend:
   ModelPortfolioRebalance). This surfaces that log and — critically — makes
   explicit that any performance/risk figure calculated over a longer window
   than "time since the current weights were set" is a hypothetical backtest
   of today's allocation, not a realized track record. */
const RebalanceHistoryPanel: React.FC<{ history: RebalanceEvent[] }> = ({ history }) => {
  const [expanded, setExpanded] = useState(false);
  const latest = history[0];

  return (
    <Box sx={{ mb: 3 }}>
      <Alert
        severity={history.length === 0 ? 'warning' : 'info'}
        sx={{ mb: expanded ? 1 : 0 }}
        action={
          history.length > 0 ? (
            <Button size="small" onClick={() => setExpanded(!expanded)} sx={{ fontSize: '0.7rem' }}>
              {expanded ? 'Hide' : 'View'} log ({history.length})
            </Button>
          ) : undefined
        }
      >
        {history.length === 0 ? (
          <>No rebalance history recorded yet — the current weights may predate this log. The next edit starts the record.</>
        ) : (
          <>
            Current weights set <strong>{formatTimeAgo(latest.effective_at)}</strong> ({new Date(latest.effective_at).toLocaleString()}, {(REBALANCE_SOURCE_LABELS[latest.source] || latest.source).toLowerCase()}).
            {' '}Performance figures over a longer window than that assume today's weights were held constant the whole period — a hypothetical backtest, not a realized track record.
          </>
        )}
      </Alert>
      <Collapse in={expanded}>
        <TableContainer component={Paper} elevation={0} sx={{ mt: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Effective</TableCell>
                <TableCell>Source</TableCell>
                <TableCell align="right">Holdings</TableCell>
                <TableCell>Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map(ev => (
                <TableRow key={ev.id} hover>
                  <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                    {new Date(ev.effective_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={REBALANCE_SOURCE_LABELS[ev.source] || ev.source}
                      sx={{
                        fontSize: '0.65rem', height: 20,
                        bgcolor: ev.source === 'recommendation' ? 'rgba(255,140,0,0.15)' : 'action.selected',
                        color: ev.source === 'recommendation' ? bloomberg.accent.orange : 'text.secondary',
                      }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.78rem' }}>{ev.holdings_count}</TableCell>
                  <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ev.note || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Box>
  );
};

/* ── Helper: Section Header ── */
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, mt: 2, borderBottom: '1px solid', borderColor: 'divider', pb: 0.5, fontSize: '0.85rem' }}>
    {title}
  </Typography>
);

/* ── Helper: Stat Row ── */
const StatRow: React.FC<{ label: string; value: string; color?: string; tooltip?: string }> = ({ label, value, color, tooltip }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, px: 1, '&:hover': { bgcolor: 'action.hover' }, borderRadius: 0.5 }}>
    <Tooltip title={tooltip || ''} arrow placement="left">
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>{label}</Typography>
    </Tooltip>
    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: color || 'text.primary' }}>{value}</Typography>
  </Box>
);

/* ── Risk Analytics Panel (Tab 1) ── */
const RiskAnalyticsPanel: React.FC<{ risk: RiskMetricsType }> = ({ risk }) => {
  // Backend returns values already as percentages (e.g., 25.56 for 25.56%)
  const pctColor = (v: number) => v > 0 ? '#4caf50' : v < 0 ? '#f44336' : 'text.primary';
  const fPct = (v: number) => `${v > 0 ? '+' : ''}${v.toFixed(2)}%`;
  const f2 = (v: number | null) => v != null ? v.toFixed(2) : 'N/A';
  const fPct1 = (v: number | null) => v != null ? `${v.toFixed(1)}%` : 'N/A';

  return (
    <Box>
      {/* Summary strip */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip label={`${risk.data_points} trading days`} size="small" variant="outlined" />
        <Chip label={`${risk.period_days}d period`} size="small" variant="outlined" />
        <Chip label={`${risk.symbols_used.length} stocks`} size="small" variant="outlined" />
        <Chip label={`Rf: ${risk.risk_free_rate.toFixed(1)}%`} size="small" variant="outlined" />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
        {/* Column 1: Return & Risk */}
        <Paper elevation={1} sx={{ p: 1.5 }}>
          <SectionHeader title="Return Metrics" />
          <StatRow label="Total Return" value={fPct(risk.total_return)} color={pctColor(risk.total_return)} />
          <StatRow label="Annualized Return" value={fPct(risk.annualized_return)} color={pctColor(risk.annualized_return)} />
          <StatRow label="CAGR" value={fPct(risk.cagr)} color={pctColor(risk.cagr)} tooltip="Compound Annual Growth Rate" />

          <SectionHeader title="Risk Metrics" />
          <StatRow label="Volatility (ann.)" value={fPct1(risk.volatility)} />
          <StatRow label="Downside Deviation" value={fPct1(risk.downside_deviation)} tooltip="Volatility of negative returns only" />
          <StatRow label="Max Drawdown" value={fPct(risk.max_drawdown)} color="#f44336" />
          <StatRow label="Drawdown Duration" value={`${risk.max_drawdown_duration} days`} tooltip="Longest peak-to-trough duration" />
          <StatRow label="Current Drawdown" value={fPct(risk.current_drawdown)} color={risk.current_drawdown < 0 ? '#ff9800' : '#4caf50'} />
        </Paper>

        {/* Column 2: Risk-Adjusted & VaR */}
        <Paper elevation={1} sx={{ p: 1.5 }}>
          <SectionHeader title="Risk-Adjusted Ratios" />
          <StatRow label="Sharpe Ratio" value={f2(risk.sharpe_ratio)} tooltip="(Return - Rf) / Volatility" color={risk.sharpe_ratio >= 1 ? '#4caf50' : risk.sharpe_ratio >= 0 ? '#ff9800' : '#f44336'} />
          <StatRow label="Sortino Ratio" value={f2(risk.sortino_ratio)} tooltip="(Return - Rf) / Downside Deviation" color={risk.sortino_ratio >= 1.5 ? '#4caf50' : risk.sortino_ratio >= 0 ? '#ff9800' : '#f44336'} />
          <StatRow label="Calmar Ratio" value={f2(risk.calmar_ratio)} tooltip="Annualized Return / |Max Drawdown|" />

          <SectionHeader title="Value at Risk" />
          <StatRow label="VaR (95%)" value={fPct(risk.var_95)} color="#f44336" tooltip="5% probability of losing more than this in one day" />
          <StatRow label="VaR (99%)" value={fPct(risk.var_99)} color="#f44336" tooltip="1% probability of losing more than this in one day" />
          <StatRow label="CVaR (95%)" value={fPct(risk.cvar_95)} color="#f44336" tooltip="Expected loss in worst 5% of days" />
          <StatRow label="CVaR (99%)" value={fPct(risk.cvar_99)} color="#f44336" tooltip="Expected loss in worst 1% of days" />
        </Paper>

        {/* Column 3: Distribution & Win/Loss */}
        <Paper elevation={1} sx={{ p: 1.5 }}>
          <SectionHeader title="Distribution" />
          <StatRow label="Skewness" value={risk.skewness.toFixed(3)} tooltip="Negative = left-tail heavy (more extreme losses)" color={risk.skewness < -0.5 ? '#f44336' : risk.skewness > 0.5 ? '#4caf50' : 'text.primary'} />
          <StatRow label="Kurtosis" value={risk.kurtosis.toFixed(3)} tooltip="Higher = fatter tails (more extreme moves)" color={risk.kurtosis > 3 ? '#ff9800' : 'text.primary'} />

          <SectionHeader title="Win / Loss Analysis" />
          <StatRow label="Positive Days" value={`${risk.positive_days}`} />
          <StatRow label="Negative Days" value={`${risk.negative_days}`} />
          <StatRow label="Win Rate" value={`${risk.win_rate.toFixed(1)}%`} color={risk.win_rate >= 50 ? '#4caf50' : '#f44336'} />
          <StatRow label="Avg Win" value={fPct(risk.avg_win)} color="#4caf50" />
          <StatRow label="Avg Loss" value={fPct(risk.avg_loss)} color="#f44336" />
          <StatRow label="Profit Factor" value={risk.profit_factor.toFixed(2)} tooltip="Total gains / Total losses" color={risk.profit_factor >= 1 ? '#4caf50' : '#f44336'} />
          <StatRow label="Best Day" value={fPct(risk.best_day)} color="#4caf50" />
          <StatRow label="Worst Day" value={fPct(risk.worst_day)} color="#f44336" />
        </Paper>
      </Box>

      {/* Risk Charts */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 3 }}>
        {/* Drawdown Chart */}
        {risk.drawdown_series && risk.drawdown_series.length > 0 && (
          <Paper elevation={1} sx={{ p: 1.5 }}>
            <SectionHeader title="Drawdown Over Time" />
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={risk.drawdown_series} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 9, fill: '#94a3b8' }}
                  tickFormatter={(d: string) => d.slice(5)}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 9, fill: '#94a3b8' }}
                  tickFormatter={(v: number) => `${v.toFixed(0)}%`}
                  domain={['dataMin', 0]}
                />
                <RechartsTooltip
                  contentStyle={{ background: '#171a21', border: '1px solid #334155', fontSize: '0.75rem' }}
                  formatter={(value: number) => [`${value.toFixed(2)}%`, 'Drawdown']}
                  labelFormatter={(label: string) => label}
                />
                <ReferenceLine y={risk.max_drawdown} stroke="#ef4444" strokeDasharray="3 3" label={{ value: `Max: ${risk.max_drawdown.toFixed(1)}%`, fill: '#ef4444', fontSize: 10, position: 'insideBottomLeft' }} />
                <Area type="monotone" dataKey="value" stroke="#ef4444" fill="rgba(239,68,68,0.2)" strokeWidth={1.5} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        )}

        {/* Returns Distribution Histogram */}
        {risk.returns_distribution && risk.returns_distribution.length > 0 && (() => {
          const returns = risk.returns_distribution!;
          const min = Math.min(...returns);
          const max = Math.max(...returns);
          const binCount = 30;
          const binWidth = (max - min) / binCount || 0.01;
          const bins: Array<{ range: string; mid: number; count: number; color: string }> = [];
          for (let i = 0; i < binCount; i++) {
            const lo = min + i * binWidth;
            const hi = lo + binWidth;
            const count = returns.filter(r => r >= lo && (i === binCount - 1 ? r <= hi : r < hi)).length;
            const mid = (lo + hi) / 2;
            bins.push({ range: `${mid.toFixed(2)}%`, mid, count, color: mid >= 0 ? '#22c55e' : '#ef4444' });
          }
          return (
            <Paper elevation={1} sx={{ p: 1.5 }}>
              <SectionHeader title="Returns Distribution" />
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={bins} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="range" tick={{ fontSize: 8, fill: '#94a3b8' }} interval={4} />
                  <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} />
                  <RechartsTooltip
                    contentStyle={{ background: '#171a21', border: '1px solid #334155', fontSize: '0.75rem' }}
                    formatter={(value: number, _: string, props: any) => [`${value} days`, props.payload.range]}
                  />
                  <ReferenceLine x={bins.reduce((closest, b) => Math.abs(b.mid - risk.var_95) < Math.abs(closest.mid - risk.var_95) ? b : closest, bins[0]).range} stroke="var(--bb-accent)" strokeDasharray="3 3" label={{ value: 'VaR 95%', fill: 'var(--bb-accent)', fontSize: 9, angle: -90, position: 'insideTopLeft' }} />
                  <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                    {bins.map((entry, idx) => (
                      <Cell key={idx} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          );
        })()}

        {/* Cumulative Returns Chart */}
        {risk.cumulative_returns && risk.cumulative_returns.length > 0 && (
          <Paper elevation={1} sx={{ p: 1.5 }}>
            <SectionHeader title="Cumulative Returns" />
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={risk.cumulative_returns} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 9, fill: '#94a3b8' }}
                  tickFormatter={(d: string) => d.slice(5)}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 9, fill: '#94a3b8' }}
                  tickFormatter={(v: number) => `${v.toFixed(0)}%`}
                />
                <RechartsTooltip
                  contentStyle={{ background: '#171a21', border: '1px solid #334155', fontSize: '0.75rem' }}
                  formatter={(value: number) => [`${value.toFixed(2)}%`, 'Return']}
                  labelFormatter={(label: string) => label}
                />
                <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
                <Area type="monotone" dataKey="value" stroke="#22c55e" fill="rgba(34,197,94,0.15)" strokeWidth={1.5} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        )}

        {/* Contribution Waterfall */}
        {risk.holding_metrics && risk.holding_metrics.length > 0 && (
          <Paper elevation={1} sx={{ p: 1.5 }}>
            <SectionHeader title="Return Contribution by Holding" />
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={[...risk.holding_metrics].sort((a, b) => b.return_contribution - a.return_contribution)}
                layout="vertical"
                margin={{ top: 5, right: 10, left: 50, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis type="number" tick={{ fontSize: 9, fill: '#94a3b8' }} tickFormatter={(v: number) => `${v.toFixed(1)}%`} />
                <YAxis type="category" dataKey="symbol" tick={{ fontSize: 9, fill: '#94a3b8' }} width={45} />
                <RechartsTooltip
                  contentStyle={{ background: '#171a21', border: '1px solid #334155', fontSize: '0.75rem' }}
                  formatter={(value: number) => [`${value >= 0 ? '+' : ''}${value.toFixed(2)}%`, 'Contribution']}
                />
                <ReferenceLine x={0} stroke="#94a3b8" />
                <Bar dataKey="return_contribution" radius={[0, 4, 4, 0]}>
                  {[...risk.holding_metrics].sort((a, b) => b.return_contribution - a.return_contribution).map((entry, idx) => (
                    <Cell key={idx} fill={entry.return_contribution >= 0 ? '#22c55e' : '#ef4444'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        )}
      </Box>

      {/* Per-holding contribution table */}
      {risk.holding_metrics && risk.holding_metrics.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <SectionHeader title="Per-Holding Contribution Analysis" />
          <TableContainer component={Paper} elevation={1}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Symbol</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Weight</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                    <Tooltip title="Individual annualized return" arrow><span>Return (ann.)</span></Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                    <Tooltip title="Individual annualized volatility" arrow><span>Volatility</span></Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                    <Tooltip title="Individual Sharpe ratio" arrow><span>Sharpe</span></Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                    <Tooltip title="Weight * stock return = contribution to portfolio return" arrow><span>Return Contrib.</span></Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                    <Tooltip title="Proportion of total portfolio risk attributable to this holding" arrow><span>Risk Contrib.</span></Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {risk.holding_metrics.sort((a, b) => b.return_contribution - a.return_contribution).map((hm) => (
                  <TableRow key={hm.symbol} hover>
                    <TableCell>
                      <Chip label={hm.symbol} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                      {(hm.weight * 100).toFixed(1)}%
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem', color: hm.annualized_return >= 0 ? '#4caf50' : '#f44336' }}>
                      {hm.annualized_return.toFixed(1)}%
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                      {hm.volatility.toFixed(1)}%
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem', color: hm.sharpe >= 0 ? '#4caf50' : '#f44336' }}>
                      {hm.sharpe.toFixed(2)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, color: hm.return_contribution >= 0 ? '#4caf50' : '#f44336' }}>
                      {hm.return_contribution >= 0 ? '+' : ''}{hm.return_contribution.toFixed(2)}%
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                      {hm.risk_contribution.toFixed(1)}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Benchmark comparisons in detail */}
      {risk.benchmark_comparisons && risk.benchmark_comparisons.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <SectionHeader title="Benchmark Comparison (Detail)" />
          <BenchmarkComparisonTable
            comparisons={risk.benchmark_comparisons}
            portfolioReturn={risk.annualized_return}
            portfolioVol={risk.volatility}
            portfolioSharpe={risk.sharpe_ratio}
            portfolioMaxDD={risk.max_drawdown}
          />
        </Box>
      )}
    </Box>
  );
};

/* ── Benchmark Comparison Table ── */
const BenchmarkComparisonTable: React.FC<{
  comparisons: BenchmarkComparison[];
  portfolioReturn: number;
  portfolioVol: number;
  portfolioSharpe: number;
  portfolioMaxDD: number;
}> = ({ comparisons, portfolioReturn, portfolioVol, portfolioSharpe, portfolioMaxDD }) => {
  const pctColor = (v: number) => v > 0 ? '#4caf50' : v < 0 ? '#f44336' : 'text.primary';
  const cellSx = { fontSize: '0.75rem', py: 0.5 };
  const headSx = { fontWeight: 700, fontSize: '0.7rem', py: 0.5, whiteSpace: 'nowrap' as const };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Performance Comparison */}
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>Performance</TableCell>
              <TableCell align="right" sx={headSx}>
                <Tooltip title="Annualized return" arrow><span>Return</span></Tooltip>
              </TableCell>
              <TableCell align="right" sx={headSx}>
                <Tooltip title="Annualized volatility" arrow><span>Vol</span></Tooltip>
              </TableCell>
              <TableCell align="right" sx={headSx}>
                <Tooltip title="Sharpe ratio" arrow><span>Sharpe</span></Tooltip>
              </TableCell>
              <TableCell align="right" sx={headSx}>
                <Tooltip title="Maximum drawdown" arrow><span>Max DD</span></Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow sx={{ bgcolor: 'action.selected' }}>
              <TableCell sx={{ ...cellSx, fontWeight: 700 }}>Portfolio</TableCell>
              <TableCell align="right" sx={{ ...cellSx, fontWeight: 700, color: pctColor(portfolioReturn) }}>
                {portfolioReturn >= 0 ? '+' : ''}{portfolioReturn.toFixed(1)}%
              </TableCell>
              <TableCell align="right" sx={{ ...cellSx, fontWeight: 700 }}>{portfolioVol.toFixed(1)}%</TableCell>
              <TableCell align="right" sx={{ ...cellSx, fontWeight: 700 }}>{portfolioSharpe.toFixed(2)}</TableCell>
              <TableCell align="right" sx={{ ...cellSx, fontWeight: 700, color: '#f44336' }}>{portfolioMaxDD.toFixed(1)}%</TableCell>
            </TableRow>
            {comparisons.map(b => (
              <TableRow key={b.symbol} hover>
                <TableCell sx={cellSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip label={b.symbol} size="small" sx={{ fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                    <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 }}>
                      {b.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ ...cellSx, color: pctColor(b.annualized_return) }}>
                  {b.annualized_return >= 0 ? '+' : ''}{b.annualized_return.toFixed(1)}%
                </TableCell>
                <TableCell align="right" sx={cellSx}>{b.volatility.toFixed(1)}%</TableCell>
                <TableCell align="right" sx={{ ...cellSx, color: b.sharpe >= 1 ? '#4caf50' : b.sharpe >= 0 ? '#ff9800' : '#f44336' }}>
                  {b.sharpe.toFixed(2)}
                </TableCell>
                <TableCell align="right" sx={{ ...cellSx, color: '#f44336' }}>{b.max_drawdown.toFixed(1)}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Portfolio vs Benchmark Relative Metrics */}
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>Portfolio vs Benchmark</TableCell>
              <TableCell align="right" sx={headSx}>
                <Tooltip title="Portfolio sensitivity to benchmark moves. >1 = more volatile, <1 = less volatile, 1 = moves in line" arrow><span>Beta</span></Tooltip>
              </TableCell>
              <TableCell align="right" sx={headSx}>
                <Tooltip title="Jensen's Alpha — excess return above what beta predicts (annualized)" arrow><span>Alpha</span></Tooltip>
              </TableCell>
              <TableCell align="right" sx={headSx}>
                <Tooltip title="How much of portfolio variance is explained by the benchmark (0-1)" arrow><span>R²</span></Tooltip>
              </TableCell>
              <TableCell align="right" sx={headSx}>
                <Tooltip title="Standard deviation of active returns vs benchmark (annualized)" arrow><span>Tracking Error</span></Tooltip>
              </TableCell>
              <TableCell align="right" sx={headSx}>
                <Tooltip title="Active return per unit of tracking error" arrow><span>Info Ratio</span></Tooltip>
              </TableCell>
              <TableCell align="right" sx={headSx}>
                <Tooltip title="Correlation between portfolio and benchmark returns" arrow><span>Correlation</span></Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {comparisons.map(b => {
              const bn = b.name || b.symbol;
              const betaTip = b.beta > 1
                ? `For every 1% ${bn} moves, your portfolio moves ~${b.beta.toFixed(2)}% — ${((b.beta - 1) * 100).toFixed(0)}% more volatile than ${bn}`
                : b.beta < 1
                ? `For every 1% ${bn} moves, your portfolio moves ~${b.beta.toFixed(2)}% — ${((1 - b.beta) * 100).toFixed(0)}% less volatile than ${bn}`
                : `Your portfolio moves roughly in line with ${bn}`;
              const alphaTip = b.alpha >= 0
                ? `Your portfolio generated ${b.alpha.toFixed(1)}% annualized excess return above what its beta exposure to ${bn} would predict`
                : `Your portfolio underperformed by ${Math.abs(b.alpha).toFixed(1)}% annualized vs what its beta exposure to ${bn} would predict`;
              const r2Tip = `${(b.r_squared * 100).toFixed(0)}% of your portfolio's variance is explained by ${bn} — ${b.r_squared >= 0.7 ? 'strong' : b.r_squared >= 0.4 ? 'moderate' : 'weak'} explanatory power`;
              const teTip = `Your portfolio's returns deviate from ${bn} by ~${b.tracking_error.toFixed(1)}% annualized — ${b.tracking_error < 5 ? 'closely tracks' : b.tracking_error < 15 ? 'moderately deviates from' : 'significantly differs from'} the benchmark`;
              const irTip = b.information_ratio != null
                ? (b.information_ratio >= 0.5 ? `Strong active management: ${b.information_ratio.toFixed(2)} units of excess return per unit of tracking error vs ${bn}` : b.information_ratio >= 0 ? `Positive but modest active return of ${b.information_ratio.toFixed(2)} per unit of tracking error vs ${bn}` : `Negative information ratio — underperforming ${bn} on a risk-adjusted basis`)
                : 'Not available';
              const corrTip = `${b.correlation >= 0.7 ? 'High' : b.correlation >= 0.3 ? 'Moderate' : 'Low'} correlation (${b.correlation.toFixed(2)}) — your portfolio ${b.correlation >= 0.7 ? 'moves closely with' : b.correlation >= 0.3 ? 'partially follows' : 'behaves independently of'} ${bn}`;
              return (
              <TableRow key={b.symbol} hover>
                <TableCell sx={cellSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mr: 0.5 }}>vs</Typography>
                    <Chip label={b.symbol} size="small" sx={{ fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                    <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 }}>
                      {b.name}
                    </Typography>
                  </Box>
                </TableCell>
                <Tooltip title={betaTip} arrow placement="top"><TableCell align="right" sx={{ ...cellSx, fontWeight: 600, cursor: 'help' }}>{b.beta.toFixed(2)}</TableCell></Tooltip>
                <Tooltip title={alphaTip} arrow placement="top"><TableCell align="right" sx={{ ...cellSx, color: pctColor(b.alpha), cursor: 'help' }}>
                  {b.alpha >= 0 ? '+' : ''}{b.alpha.toFixed(1)}%
                </TableCell></Tooltip>
                <Tooltip title={r2Tip} arrow placement="top"><TableCell align="right" sx={{ ...cellSx, cursor: 'help' }}>{b.r_squared.toFixed(2)}</TableCell></Tooltip>
                <Tooltip title={teTip} arrow placement="top"><TableCell align="right" sx={{ ...cellSx, cursor: 'help' }}>{b.tracking_error.toFixed(1)}%</TableCell></Tooltip>
                <Tooltip title={irTip} arrow placement="top"><TableCell align="right" sx={{ ...cellSx, cursor: 'help' }}>
                  {b.information_ratio != null ? b.information_ratio.toFixed(2) : 'N/A'}
                </TableCell></Tooltip>
                <Tooltip title={corrTip} arrow placement="top"><TableCell align="right" sx={{ ...cellSx, color: b.correlation > 0.7 ? '#4caf50' : b.correlation > 0.3 ? '#ff9800' : '#f44336', cursor: 'help' }}>
                  {b.correlation.toFixed(2)}
                </TableCell></Tooltip>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

/* ── Trade Recommendations Panel (Tab 6) ── */
const REC_COLORS: Record<string, string> = {
  'Strong Buy': '#4caf50',
  'Buy': '#66bb6a',
  'Hold': '#757575',
  'Sell': '#ef5350',
  'Strong Sell': '#f44336',
};

const FACTOR_LABELS: Record<string, string> = {
  signal: 'Signals',
  momentum: 'Momentum',
  trend: 'Trend',
  fundamentals: 'Fundamentals',
  pe_relative: 'P/E Relative',
  risk_contribution: 'Risk Contrib.',
  weight_drift: 'Weight Drift',
};

const TradeRecommendationsPanel: React.FC<{
  portfolioId: number;
  timeframe: string;
  benchmarks: string[];
  roles: Record<string, 'core' | 'satellite'>;
  onApplied: () => void;
}> = ({ portfolioId, timeframe, benchmarks, roles, onApplied }) => {
  const [data, setData] = useState<TradeRecommendationsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [methodologyOpen, setMethodologyOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applySuccess, setApplySuccess] = useState(false);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getTradeRecommendations(portfolioId, timeframe, benchmarks);
      setData(result);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Failed to generate recommendations');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!data) return;
    setApplying(true);
    setApplyError(null);
    try {
      const holdings = data.holdings.map(h => ({
        symbol: h.symbol,
        target_weight: h.suggested_weight,
        role: roles[h.symbol] || 'core',
      }));
      await setModelPortfolioHoldings(
        portfolioId, holdings, 'recommendation',
        `Applied from Trade Recommendations (${timeframe} composite score)`
      );
      setConfirmOpen(false);
      setApplySuccess(true);
      onApplied();
      await handleGenerate(); // refresh against the new baseline — drift/change should now read ~0
    } catch (err: any) {
      setApplyError(err?.response?.data?.detail || err.message || 'Failed to apply recommendation');
    } finally {
      setApplying(false);
    }
  };

  const scoreColor = (score: number) => {
    if (score >= 60) return '#4caf50';
    if (score >= 20) return '#66bb6a';
    if (score >= -20) return '#757575';
    if (score >= -60) return '#ef5350';
    return '#f44336';
  };

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={loading}
          size="small"
          startIcon={loading ? <CircularProgress size={16} /> : <AnalyticsIcon />}
        >
          {loading ? 'Analyzing...' : 'Generate Recommendations'}
        </Button>
        {data && (
          <Button
            variant="outlined"
            color="warning"
            onClick={() => setConfirmOpen(true)}
            size="small"
            startIcon={<CheckCircleIcon />}
          >
            Apply Suggested Weights
          </Button>
        )}
        <Tooltip title="How scores are calculated">
          <IconButton size="small" onClick={() => setMethodologyOpen(true)} sx={{ color: 'text.secondary' }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Apply confirmation dialog */}
      <Dialog open={confirmOpen} onClose={() => !applying && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Apply Suggested Weights?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This replaces the portfolio's target weights with the suggested weights below and logs a
            rebalance event (source: Recommendation). This is the same as editing weights manually — it can be
            undone by re-editing the holdings afterward, but the previous weights won't be restored automatically.
          </Typography>
          {applyError && <Alert severity="error" sx={{ mb: 2 }}>{applyError}</Alert>}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Symbol</TableCell>
                  <TableCell align="right">Current</TableCell>
                  <TableCell align="right">Suggested</TableCell>
                  <TableCell align="right">Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.holdings.map(h => (
                  <TableRow key={h.symbol}>
                    <TableCell sx={{ fontWeight: 600 }}>{h.symbol}</TableCell>
                    <TableCell align="right">{(h.target_weight * 100).toFixed(1)}%</TableCell>
                    <TableCell align="right">{(h.suggested_weight * 100).toFixed(1)}%</TableCell>
                    <TableCell align="right" sx={{ color: h.weight_change > 0 ? '#4caf50' : h.weight_change < 0 ? '#ef5350' : 'text.secondary', fontWeight: 600 }}>
                      {h.weight_change > 0 ? '+' : ''}{h.weight_change !== 0 ? `${h.weight_change.toFixed(1)}pp` : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={applying}>Cancel</Button>
          <Button
            onClick={handleApply}
            variant="contained"
            color="warning"
            disabled={applying}
            startIcon={applying ? <CircularProgress size={16} /> : undefined}
          >
            {applying ? 'Applying...' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={applySuccess}
        autoHideDuration={4000}
        onClose={() => setApplySuccess(false)}
        message="Weights updated — rebalance logged"
      />

      {/* Methodology Drawer */}
      <Drawer
        anchor="right"
        open={methodologyOpen}
        onClose={() => setMethodologyOpen(false)}
        PaperProps={{ sx: { width: 480, p: 3, bgcolor: '#0d1117' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Recommendation Methodology</Typography>
          <IconButton size="small" onClick={() => setMethodologyOpen(false)}><CloseIcon /></IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each holding is scored on a <strong>-100 to +100</strong> composite scale across 7 factors.
          Scores map to recommendations: <strong style={{ color: '#4caf50' }}>Strong Buy</strong> ≥ 60,{' '}
          <strong style={{ color: '#66bb6a' }}>Buy</strong> ≥ 20,{' '}
          <strong style={{ color: '#757575' }}>Hold</strong> ≥ -20,{' '}
          <strong style={{ color: '#ef5350' }}>Reduce</strong> ≥ -60,{' '}
          <strong style={{ color: '#f44336' }}>Sell</strong> below -60.
        </Typography>

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Scoring Factors</Typography>

        {[
          {
            label: 'Signals',
            key: 'signal',
            weight: '±30 pts',
            desc: 'Technical signal strength derived from RSI, MACD cross, and Bollinger Band position. Bullish signals (e.g. RSI < 35 + MACD bullish cross + price near lower band) score up to +30; bearish signals score down to -30.',
          },
          {
            label: 'Momentum',
            key: 'momentum',
            weight: '±20 pts',
            desc: 'Risk-adjusted 3-month price momentum relative to portfolio peers. Calculated as 3M return ÷ volatility. Top quartile scores +20, bottom quartile scores -20.',
          },
          {
            label: 'Trend',
            key: 'trend',
            weight: '±15 pts',
            desc: 'Short- vs long-term trend alignment using a configurable lookback window. Price above its moving average = positive trend (+15 max); below = negative trend (-15 max). Magnitude scales with the degree of separation.',
          },
          {
            label: 'Fundamentals',
            key: 'fundamentals',
            weight: '±15 pts',
            desc: 'Earnings momentum and revenue growth quality. Strong EPS and revenue growth score +15; declining fundamentals score -15. Scores are normalized across portfolio holdings.',
          },
          {
            label: 'P/E Relative',
            key: 'pe_relative',
            weight: '±10 pts',
            desc: "Stock's P/E ratio vs. portfolio median. Significantly cheaper than peers (P/E < 0.7× median) scores +10 (value opportunity); significantly more expensive (P/E > 1.5× median) scores -10 (stretched valuation). Neutral if P/E data is unavailable.",
          },
          {
            label: 'Risk Contribution',
            key: 'risk_contribution',
            weight: '±5 pts',
            desc: "Marginal contribution to portfolio risk vs. its weight. If a holding contributes proportionally more risk than its portfolio weight warrants, it scores -5. If it contributes less risk per unit of weight (diversifying), it scores +5.",
          },
          {
            label: 'Weight Drift',
            key: 'weight_drift',
            weight: '±5 pts',
            desc: 'Deviation of current weight from an equal-weight target. Holdings significantly over-weight their equal-weight target score -5 (rebalance pressure); under-weight score +5. Encourages mean-reversion toward balance.',
          },
        ].map((f) => (
          <Box key={f.key} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{f.label}</Typography>
              <Typography variant="caption" sx={{ color: bloomberg.accent.orange, fontFamily: 'monospace' }}>{f.weight}</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
              {f.desc}
            </Typography>
          </Box>
        ))}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Suggested Weight</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5, mb: 2 }}>
          The suggested weight adjusts each holding's current weight by up to ±10 percentage points in the direction of its score.
          The full ±10pp shift applies at score magnitudes ≥ 60; smaller scores produce proportionally smaller adjustments.
          Weights are then re-normalized to 100%.
        </Typography>

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Portfolio Insights</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
          Aggregate portfolio signals: flags if {">"} 60% of holdings are technically bearish, detects "death cross" conditions
          (50-day MA crossing below 200-day MA), and notes when the portfolio is broadly momentum-driven.
        </Typography>
      </Drawer>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <>
          {/* Portfolio Insights */}
          {data.portfolio_insights.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Portfolio Insights</Typography>
              {data.portfolio_insights.map((insight, i) => (
                <Alert
                  key={i}
                  severity={insight.toLowerCase().includes('reducing') || insight.toLowerCase().includes('death') ? 'warning' : 'info'}
                  sx={{ mb: 0.5, py: 0 }}
                >
                  {insight}
                </Alert>
              ))}
            </Box>
          )}

          {/* Holdings Recommendations Table */}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Symbol</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell align="right">Weight</TableCell>
                  <TableCell align="right">Suggested</TableCell>
                  <TableCell align="right">Change</TableCell>
                  <TableCell align="center">Score</TableCell>
                  <TableCell align="center">Recommendation</TableCell>
                  <TableCell>Key Factors</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.holdings.map((h) => (
                  <React.Fragment key={h.symbol}>
                    <TableRow
                      hover
                      onClick={() => setExpandedRow(expandedRow === h.symbol ? null : h.symbol)}
                      sx={{ cursor: 'pointer', '& td': { borderBottom: expandedRow === h.symbol ? 'none' : undefined } }}
                    >
                      <TableCell><TickerLink symbol={h.symbol} fontSize="0.8rem" fontWeight={700} /></TableCell>
                      <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {h.name}
                      </TableCell>
                      <TableCell align="right">{(h.current_weight * 100).toFixed(1)}%</TableCell>
                      <TableCell align="right">{(h.suggested_weight * 100).toFixed(1)}%</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: h.weight_change > 0.05 ? '#4caf50' : h.weight_change < -0.05 ? '#ef5350' : '#757575',
                          }}
                        >
                          {h.weight_change > 0.05 ? '+' : ''}{h.weight_change !== 0 ? `${h.weight_change.toFixed(1)}pp` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: scoreColor(h.score) }}
                        >
                          {h.score > 0 ? '+' : ''}{h.score}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={h.recommendation}
                          size="small"
                          sx={{
                            bgcolor: REC_COLORS[h.recommendation] || '#757575',
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {h.key_factors.join(' · ') || '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {/* Expanded Factor Breakdown */}
                    {expandedRow === h.symbol && (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 1, px: 3, bgcolor: 'rgba(255,255,255,0.02)' }}>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {Object.entries(h.factors).map(([key, factor]) => (
                              <Paper key={key} sx={{ p: 1, minWidth: 140, flex: '1 1 140px', borderLeft: `3px solid ${scoreColor(factor.score)}` }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                                  {FACTOR_LABELS[key] || key}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: scoreColor(factor.score) }}>
                                  {factor.score > 0 ? '+' : ''}{factor.score}
                                </Typography>
                                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.65rem' }}>
                                  {factor.detail}
                                </Typography>
                              </Paper>
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {!data && !loading && !error && (
        <Alert severity="info">
          Click "Generate Recommendations" to analyze your portfolio holdings and receive actionable trade suggestions.
        </Alert>
      )}
    </Box>
  );
};

/* ── Portfolio Comparison Panel (Tab 7) ── */
const PortfolioComparePanel: React.FC<{
  nameA: string;
  nameB: string;
  analysisA: ModelPortfolioAnalysis;
  analysisB: ModelPortfolioAnalysis;
  perfColor: (v: number | null | undefined) => string;
  fmtPerf: (v: number | null | undefined) => string;
}> = ({ nameA, nameB, analysisA, analysisB, perfColor, fmtPerf }) => {
  const rA = analysisA.risk_metrics;
  const rB = analysisB.risk_metrics;
  const scoreColor = (s: number) => s >= 70 ? '#4caf50' : s >= 40 ? '#ff9800' : '#f44336';
  const pctColor = (v: number | null | undefined) => {
    if (v == null) return 'text.primary';
    return v > 0 ? '#4caf50' : v < 0 ? '#f44336' : 'text.primary';
  };
  const fPct = (v: number | null | undefined, d = 1) =>
    v != null ? `${v >= 0 ? '+' : ''}${v.toFixed(d)}%` : 'N/A';
  const f2 = (v: number | null | undefined) => v != null ? v.toFixed(2) : 'N/A';
  const fPct0 = (v: number | null | undefined) => v != null ? `${v.toFixed(1)}%` : 'N/A';

  type MetricDef = { label: string; a: number | null | undefined; b: number | null | undefined; fmt: (v: any) => string; higherIsBetter?: boolean; colorize?: boolean };
  const metrics: MetricDef[] = [
    { label: 'Annualized Return', a: rA?.annualized_return, b: rB?.annualized_return, fmt: fPct, higherIsBetter: true, colorize: true },
    { label: 'Total Return', a: rA?.total_return, b: rB?.total_return, fmt: fPct, higherIsBetter: true, colorize: true },
    { label: 'CAGR', a: rA?.cagr, b: rB?.cagr, fmt: fPct, higherIsBetter: true, colorize: true },
    { label: 'Volatility (ann.)', a: rA?.volatility, b: rB?.volatility, fmt: fPct0, higherIsBetter: false },
    { label: 'Sharpe Ratio', a: rA?.sharpe_ratio, b: rB?.sharpe_ratio, fmt: f2, higherIsBetter: true, colorize: true },
    { label: 'Sortino Ratio', a: rA?.sortino_ratio, b: rB?.sortino_ratio, fmt: f2, higherIsBetter: true, colorize: true },
    { label: 'Calmar Ratio', a: rA?.calmar_ratio, b: rB?.calmar_ratio, fmt: f2, higherIsBetter: true, colorize: true },
    { label: 'Max Drawdown', a: rA?.max_drawdown, b: rB?.max_drawdown, fmt: fPct, higherIsBetter: false, colorize: true },
    { label: 'VaR (95%)', a: rA?.var_95, b: rB?.var_95, fmt: fPct, higherIsBetter: false, colorize: true },
    { label: 'Win Rate', a: rA?.win_rate, b: rB?.win_rate, fmt: fPct0, higherIsBetter: true, colorize: true },
    { label: 'Best Day', a: rA?.best_day, b: rB?.best_day, fmt: fPct, higherIsBetter: true, colorize: true },
    { label: 'Worst Day', a: rA?.worst_day, b: rB?.worst_day, fmt: fPct, higherIsBetter: false, colorize: true },
    { label: 'Avg Correlation', a: analysisA.score?.avg_pairwise_correlation, b: analysisB.score?.avg_pairwise_correlation, fmt: f2, higherIsBetter: false },
    { label: 'Diversification', a: analysisA.score?.diversification_score, b: analysisB.score?.diversification_score, fmt: (v: any) => v != null ? v.toFixed(1) : 'N/A', higherIsBetter: true },
  ];

  const winner = (a: number | null | undefined, b: number | null | undefined, higherIsBetter = true): 'A' | 'B' | null => {
    if (a == null || b == null || Math.abs(a - b) < 0.001) return null;
    return (higherIsBetter ? a > b : a < b) ? 'A' : 'B';
  };

  const headSx = { fontWeight: 700, fontSize: '0.75rem', py: 0.75 };
  const cellSx = { fontSize: '0.8rem', py: 0.5 };

  return (
    <Box>
      {/* Score cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
        {([{ name: nameA, data: analysisA }, { name: nameB, data: analysisB }] as const).map(({ name, data }) => (
          <Paper key={name} elevation={1} sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, textAlign: 'center' }}>{name}</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
              <Tooltip title="Based on average pairwise correlation. Lower correlation = better diversification. Score 0–100." arrow>
              <Box sx={{ textAlign: 'center', cursor: 'help' }}>
                <Typography variant="caption" color="text.secondary">Diversification</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: scoreColor(data.score.diversification_score) }}>
                  {data.score.diversification_score}
                </Typography>
              </Box>
              </Tooltip>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Holdings</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {data.score.num_holdings}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Avg Corr</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {data.score.avg_pairwise_correlation.toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Risk metrics comparison table */}
      {rA && rB && !rA.error && !rB.error && (
        <TableContainer component={Paper} elevation={1} sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headSx}>Metric</TableCell>
                <TableCell align="right" sx={{ ...headSx, color: '#4caf50' }}>{nameA}</TableCell>
                <TableCell align="right" sx={{ ...headSx, color: '#42a5f5' }}>{nameB}</TableCell>
                <TableCell align="right" sx={headSx}>Delta (A–B)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {metrics.map(({ label, a, b, fmt, higherIsBetter, colorize }) => {
                const numA = typeof a === 'number' ? a : null;
                const numB = typeof b === 'number' ? b : null;
                const delta = numA != null && numB != null ? numA - numB : null;
                const w = winner(numA, numB, higherIsBetter);
                return (
                  <TableRow key={label} hover>
                    <TableCell sx={cellSx}>{label}</TableCell>
                    <TableCell align="right" sx={{ ...cellSx, fontWeight: w === 'A' ? 700 : 400, color: colorize && numA != null ? pctColor(numA) : 'text.primary' }}>
                      {fmt(a)}{w === 'A' && <span style={{ marginLeft: 4, color: '#4caf50', fontSize: '0.7rem' }}>▲</span>}
                    </TableCell>
                    <TableCell align="right" sx={{ ...cellSx, fontWeight: w === 'B' ? 700 : 400, color: colorize && numB != null ? pctColor(numB) : 'text.primary' }}>
                      {fmt(b)}{w === 'B' && <span style={{ marginLeft: 4, color: '#4caf50', fontSize: '0.7rem' }}>▲</span>}
                    </TableCell>
                    <TableCell align="right" sx={{ ...cellSx, color: delta != null ? pctColor(delta) : 'text.secondary' }}>
                      {delta != null ? `${delta >= 0 ? '+' : ''}${delta.toFixed(2)}` : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Weighted performance comparison */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Weighted Portfolio Returns</Typography>
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>Portfolio</TableCell>
              {PERF_LABELS.map(({ label }) => (
                <TableCell key={label} align="right" sx={headSx}>{label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {([{ name: nameA, perf: analysisA.performance.weighted }, { name: nameB, perf: analysisB.performance.weighted }] as const).map(({ name, perf }) => (
              <TableRow key={name} hover>
                <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{name}</TableCell>
                {PERF_LABELS.map(({ key }) => {
                  const val = (perf as any)[key] as number | null;
                  return (
                    <TableCell key={key} align="right" sx={{ ...cellSx, color: perfColor(val) }}>
                      {fmtPerf(val)}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ModelPortfolios;
